import { emitHintSignal } from '@flobi/asset-library/hints';
import { encodeStateAsUpdate } from 'yjs';
import { runRasterExport, type RasterOptions, type RasterSnapshot } from './rasterExport';
import { generationPlacement, generationViewport } from './ai/generationPlacement';
import { rasterDimensions } from './rasterExportLimits';
import type { RawVideoSnapshot, VideoOptions } from './videoSnapshot';
import { canCombineSelection, geometrySelection, planSelectionMask } from './selectionGeometry';
import { GroupEditing } from './groupEditing';
import { setCanvasCursor } from './canvasCursor';
import { ActiveArtboard } from './activeArtboard';
import { placeOnBoard } from './pasteOntoBoard';
import { fetchLibraryItem } from '../services/lumens';
import { deltaToParent } from './space';
import { tidyUp, type TidyHow } from './tidyUp';
import {
  CLOSE,
  flattened,
  holdsRings,
  nodeCommands,
  realRings,
  relinkCopies,
  looksLikeSvg,
  svgToArtwork,
  ringArea,
  subpathsOfCommands,
  transformCommands,
  type Subpath,
  type LengthUnit,
  IDENTITY,
  rulerIn,
  applyOps,
  applyToPoint,
  invert as invertMatrix,
  multiply as multiplyMatrix,
  type Matrix,
  type Color,
  type ShaderDef,
  assetsMap,
  toCanvasKit,
  shadersMap,
  newId,
  fontsMap,
  type Asset,
  type DocumentFont,
  type SvgExport,
  CUBIC,
  intersects,
  LINE,
  MOVE,
  ROOT_ID,
  toSvgWithNotes,
  union,
  type ImageNode,
  type Pose,
  type SketchControl,
  type ImagePaint,
  type NodeRecord,
  type Rect,
  addGuide,
  decimalsFor,
  fromUnit,
  toUnit,
  listGuides,
  moveGuide,
  pageGuides,
  readMeta,
  removeGuide,
  clearGuides,
  poseArtboard,
  soloPlay,
  planMagic,
  magicChoices,
  MAGIC_STYLES,
  measurePath,
  simplifyPath,
  smoothPath,
  fitPath,
  MOST_PATH_POINTS,
  LayerAnimationSchema,
  playableLength,
  AnimationSchema,
  TimingSchema,
  type Animation,
  type Timing,
  type LayerAnimation,
  type Guide,
  type SnapGuide,
  type SnapSettings,
  snapAlong,
  snapDragPoint,
  formatLength,
  type SnapLine,
  defaultEffect,
  type Effect,
  TextSchema,
  NODE_KEYS,
  effectorsLeftBehind,
  emptiedCloners,
  partsOf,
  boardGrid,
  COORD_LIMIT,
  gradientHandles,
  dragGradientHandle,
  commandsBounds,
  type HandleId,
  type Paint,
  invert,
  pathConversion,
  placeInParent,
  placePicture,
  type PictureCrop,
  drawsAt,
  stageSchedule,
  inheritedAnimation,
  RecipeSchema,
  type Poses,
  type MagicChoices,
  type Recipe,
  type RecipeSettings,
} from '@flobi/artwork-core';
import { keyBelongsToControl, keyBelongsToField, menuIsOpen } from '../ui/keys';
import { arrangement, partsOfRegion, ringsOf, subpathsOf, touchingGroups, unite } from '../canvas/pieces';
import { exportTools, Painter, ProofPass, ResourceCache, SketchPictures, SketchShapes, SketchStills, textOf, TileRenderer, warmUpRenderer, type Camera, type ImageSource, type LiveWindows } from '@flobi/artwork-core/render';
import { SKETCH_DEFAULT_SECONDS, SKETCH_FPS, SKETCH_REST_FRAME } from '@flobi/artwork-core/sketch';
import { effectLook } from './shell/effectLooks';
import type { Cmyk, ColorProofer, TextNode } from '@flobi/artwork-core';
import { openPress, pressLut, PRESS_LUT_SIZE } from './press';
import { buildSnapshot } from '@flobi/artwork-core/plugin';
import type { AddonBridge } from './addons/bridge';
import type { StripInput } from './shell/timingStripModel';
import { addonsApi } from './addons/api';
import type { Image, Shader } from 'canvaskit-wasm';
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { fitCamera, panBy, zoomAt } from '../canvas/camera';
import { GlSurface, loadCanvasKit } from '../canvas/canvaskit';
import { peerColor } from '@flobi/collab-client/react';
import { labelled } from '../live/label';
import { viewKeyOf } from './viewing';
import { heldLook, holdLook, propertiesFor, propertiesOf } from './copyProperties';
import { cameraAt, createPublisher, remoteSelections, selectionKey, type RemoteSelection } from '../live/presence';
import { DEFAULT_FAMILY, loadFonts, loadText } from '../canvas/fonts';
import { glyphFaces } from '../canvas/glyphFaces';
import { outlineText, type OutlinePiece } from './textOutlines';
import { DocumentFonts } from '../canvas/documentFonts';
import { documentOps, type LayerDocument } from './imageTools/document';
import { ADJUST_SHADER, ADJUST_SHADER_ID, adjustEffect, adjustIndex, isNeutral, type AdjustValues } from './imageTools/adjust';
import type { Result } from './ai/contracts';
import { DocumentImages, corsReadUrl } from '../canvas/images';
import { picturePlaces, type PicturePlaces } from '../canvas/pictureSizes';
import { isClipFile, uploadPicture, uploadPictures } from '../services/media';
import { checkImageSize, downsizingNotice } from '../services/imageUpload';
import { dropCarried, isCarried } from './carry';
import { addVariation, distinctNames, expandable, makeLayers, removeVariation, stopRepeating, tooManyCopies, wrapInCloner } from './tools/repeat';
import { RECIPES } from './tools/effectors';
import { fileColours as countFileColours } from './chrome/colourChoice';
import { usedAssetIds } from './usedAssets';
import { fitScale, isSvgFile, PASTED_SVG, pictureFile, svgLayerName, svgNotice } from './svgImport';
import { rgba, type Palette } from '../theme';
import { hexColor, inkedColor, paintOf, styleFill } from './colors';
import { Furniture, GUIDE_REACH, type LitGuide } from './furniture';
import { backInRulers, bandAt, rulerLayout } from './rulerLayout';
import { watchGestures } from './gestures';
import { PathEditing, whenPointsLeft } from './pathEditing';
import { markUnder, mayChangeMoving, movingBoards } from './movingBoards';
import { labelledBoards, labelUnder, type LabelBox } from './boardLabels';
import { lumensGlRunner, prepareLumensGl, prepareLibrarySketch, shaderSource, type LibrarySketch } from './shaders';
import { drawTool } from './tools/draw';
import { liquifyTool } from './tools/liquify';
import { widthTool } from './tools/width';
import { cutTool } from './tools/cut';
import { transformTool } from './tools/transform';
import { sliceTool } from './tools/slice';
import { blendTool } from './tools/blend';
import { measureTool } from './tools/measure';
import { clonerTool } from './tools/cloner';
import { artboardTool, behindLooseArt, besideBoards } from './tools/artboard';
import { boardSlotMoves } from './boardOrder';
import { builderTool } from './tools/builder';
import { arcTool } from './tools/arc';
import { penTool } from './tools/pen';
import { selectTool } from './tools/select';
import { shapeTool } from './tools/shapes';
import { contextArtboardAt, contextSelection } from './contextMenu';
import { textTool } from './tools/text';
import type { Overlay, Rail, Tool, ToolContext } from './tools/types';
import { TextEditing } from './textEditing';
// Renamed here: the canvas has its own `holding`, the guide in hand.
import { holding as panelHeld, onHold } from '../ui/hold';
import { MOST_EFFECTS } from './shell/effectList';
import { LOCAL_ORIGIN, MEASURE_ORIGIN, type OpenDocument } from './useEditorDocument';
import { snapSettingsOf, VIEW_DEFAULTS, type ViewSettings } from './view';

/** What an export covers: the artboard in hand, the chosen artboards, or every one. */
/** What an export covers: the artboard in hand, the artboards chosen, every artboard — or the layers chosen, alone. */
export type ExportScope = 'board' | 'selected' | 'all' | 'layers';

export type ToolId = 'select' | 'rect' | 'ellipse' | 'polygon' | 'star' | 'line' | 'arc' | 'text' | 'pen' | 'draw' | 'liquify' | 'width' | 'cut' | 'warp' | 'slice' | 'builder' | 'blend' | 'measure' | 'cloner' | 'artboard' | 'addon';

/**
 * How near a guide has to come to something before it lands on it, in screen pixels.
 *
 * The same reach a dragged shape gets, because it is the same gesture: a few pixels of
 * pull, so it feels identical at any zoom.
 */
const GUIDE_SNAP = 6;
/** How far around a point being placed to look for things to line up with, in screen pixels (as a move looks). */
const PLACE_LOOK = 600;
/** How far the canvas travels for each pixel of trackpad or wheel scroll. A drag stays one to one, under the hand. */
const WHEEL_PAN_SPEED = 1.75;

/** Keys held while something else happens, which therefore never start an undo step. */
const HELD_KEYS = new Set(['Shift', 'Alt', 'Meta', 'Control', ' ', 'CapsLock']);
/** A button that has focus because it was clicked, not because the keyboard went to it. */
const clickedButton = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el || typeof el.matches !== 'function') return false;
  if (el.tagName !== 'BUTTON' && el.getAttribute('role') !== 'button') return false;
  try {
    return !el.matches(':focus-visible');
  } catch {
    return false;
  }
};
/** The most dots a stippled region is drawn with, whatever size it is on the screen. */
const MOST_DOTS = 4000;
/** The most letters text pasted from another app brings in: a page of words, not a book. */
const PASTED_TEXT_MOST = 5000;

/** How big new text starts, in document units. */
const TEXT_SIZE = 32;

/** A millimetre at 72 units to the inch, which is what print sizes are given in. */
const MM = 72 / 25.4;

import { ARTBOARD_PRESETS, sizeOf, sizedLike, type ArtboardPreset } from './artboardPresets';
import { noteTouch, reach as touchReach, withHeldKeys } from './touchInput';
export { ARTBOARD_PRESETS, type ArtboardPreset } from './artboardPresets';

/** A box on screen, in CSS pixels in the window. */
export interface ScreenBox {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export interface EditorStyle {
  fill: string;
  /** False draws the shape hollow: the colour is kept, so turning it back on remembers it. */
  fillOn: boolean;
  /** The colour of the outline, which used to be a hardcoded near-black. */
  stroke: string;
  strokeWeight: number;
  opacity: number;
  /**
   * The ink the fill and the stroke stand for, in a CMYK file: exactly what was typed, or
   * what the press makes of what was picked. Absent in RGB, and whenever the colour
   * changes without one.
   */
  fillInk?: Cmyk;
  strokeInk?: Cmyk;
}

/**
 * What the Animate panel is pointed at: always a page, and a layer when one is chosen.
 *
 * Both together rather than one or the other, because the panel shows both — Canva's
 * arrangement, where picking a layer animates the layer and picking nothing animates the
 * page, and the two are one press apart rather than in two different places.
 */
const NAMES_OF_SHAPES: Record<LibraryShape['type'], string> = { rect: 'Rectangle', ellipse: 'Ellipse', polygon: 'Polygon', star: 'Star', line: 'Line' };

export interface AnimationTarget {
  readonly page: {
    readonly id: string;
    readonly name: string;
    readonly animation: Animation | null;
    readonly duration: number;
    /**
     * How long it plays, which is `duration` unless what is on it needs longer to arrive
     * and leave. Windows and the timing strip are measured against this.
     */
    readonly length: number;
    /** Whether anything on it moves, comes or goes at all: whether there is anything to play. */
    readonly moves: boolean;
  };
  readonly layer: {
    /** The one whose settings are shown. */
    readonly id: string;
    /** Everything chosen, which an edit is applied to. */
    readonly ids: readonly string[];
    readonly name: string;
    /** What it is, so the menu can leave out a motion that means nothing on it. */
    readonly type: string;
    readonly animation: LayerAnimation | null;
    /**
     * What it plays from its page while it has none of its own, said as a layer's
     * animation: what the bar shows as chosen, and what a first edit starts from.
     */
    readonly inherited: LayerAnimation | null;
    /** When it is on the page, or null when it is there the whole time. */
    readonly timing: Timing | null;
  } | null;
  /** Layers are chosen, but none of them sits on this page, so none can be animated. */
  readonly offPage?: boolean;
}

/** A shape the library adds: the node it becomes, less where it goes and how big. */
export type LibraryShape =
  | { type: 'rect'; round?: number }
  | { type: 'ellipse' }
  | { type: 'polygon'; sides: number }
  | { type: 'star'; points: number; innerRatio: number }
  | { type: 'line' };
/**
 * A line of type the library adds: its words, its size as a share of the page's width, its
 * weight — and, for a brand's style, the face and the setting the brand gives it.
 */
export interface LibraryText {
  readonly text: string;
  readonly size: number;
  readonly weight: number;
  /** The face it is set in, with its files: added to the file if it is not there. Absent is the editor's own face. */
  readonly font?: DocumentFont;
  /** Line height as a share of the size. */
  readonly lineHeight?: number | null;
  /** Letter spacing as a share of the size. */
  readonly spacing?: number;
  readonly textCase?: 'none' | 'upper' | 'lower';
}
/** A paint from a brand: one colour, or two and the way they run. */
export type LibraryPaint = string | { readonly from: string; readonly to: string; readonly angle: number };
/** A picture the library can place: where its pixels are, and how big they are. */
/**
 * Layers as the catalogue keeps them: what Copy writes — each layer as it was, what is
 * inside it, and the pictures, shaders and fonts they draw with.
 */
export interface CatalogLayers {
  readonly artwork: readonly {
    readonly id: string;
    readonly parent?: string;
    readonly world?: readonly number[];
    readonly node: Record<string, unknown>;
    readonly children: readonly { id: string; parent: string; node: Record<string, unknown> }[];
  }[];
  readonly resources?: { assets?: Record<string, unknown>; shaders?: Record<string, unknown>; fonts?: Record<string, unknown> };
}

export interface LibraryPicture {
  /** What the layer points at: a media asset's id, or an id of the library's own making. */
  readonly assetId: string;
  /** The media asset it is, when it is one. */
  readonly mediaAssetId?: string;
  readonly name: string;
  readonly src: string;
  readonly width: number;
  readonly height: number;
  readonly mime?: string;
  /** A video's length in seconds: present, the picture is a clip and plays with the page. */
  readonly duration?: number;
}

/**
 * Where something dragged out of Assets was let go, in the document's own units. Given, it
 * lands centred there, on the artboard under it; without one it lands in the middle of the
 * page being worked on.
 */
export type DropPoint = { readonly x: number; readonly y: number };

/** One Effects tile: the look it shows (and adds), and its picture as PNG bytes, or null. */
export interface EffectPreview { type: Effect['type']; look: Effect; png: Uint8Array | null }

export interface CanvasHandle {
  /** Insert a default-size shape at a context-menu press, in client coordinates. */
  insertShape(kind: 'rect' | 'ellipse' | 'polygon' | 'star', at: { x: number; y: number }): void;
  readonly canvasRef: RefObject<HTMLCanvasElement | null>;
  zoomBy(factor: number): void;
  fit(): void;
  actualSize(): void;
  /** Moves the view by a distance in screen pixels, as a two-finger scroll does. */
  panBy(dx: number, dy: number): void;
  select(ids: string[]): void;
  deleteSelection(): void;
  /** Applies style to the selection; new shapes use the same values. */
  restyle(patch: Partial<EditorStyle>): void;
  /** Puts the caret in a text node, as the layers list does when it opens one. */
  editText(id: string): void;
  /** Fills the selection with the Lumens shader, importing it into the file once. */
  applyShader(id: string): Promise<void>;
  getShader(id: string): ShaderDef | null;
  /** What a sketch layer runs, for its settings; null for any other layer. */
  sketchOf(id: string): { name: string; controls: readonly SketchControl[]; error: string | null; sources?: { id: string; name: string }[]; pictures?: { id: string; name: string }[]; chosen?: Record<string, string | null>; presets?: { name: string; seed?: number; values: Record<string, unknown> }[] } | null;
  /** Changes how a sketch layer is set: its `sketch` or its `clip`. */
  setSketch(id: string, props: Record<string, unknown>): void;
  /**
   * Puts an effect on everything chosen, and answers with the ids it reached.
   *
   * An effect belongs to a NODE, and a group is a node — so "apply to a group" needs no
   * special case here, and putting a blur on a group blurs what the group makes rather
   * than each thing in it, which is the whole reason somebody groups first.
   */
  addEffect(type: Effect['type'], look?: Effect): string[];
  /**
   * A picture's Adjust (imageTools/adjust): the values written into its one Adjust effect — made,
   * with the file's Adjust shader, the first time; taken off when every value is back to zero.
   * A slider's drag is one undo step (the slider holds the capture).
   */
  setAdjust(id: string, values: AdjustValues): void;
  /** A picture's Blur: one layer blur of this radius, or none at 0. */
  setBlur(id: string, radius: number): void;
  /** Changes one field of one effect on a layer (or on several, as one step), as a slider does. */
  setEffect(id: string | readonly string[], index: number, props: Record<string, unknown>): void;
  removeEffect(id: string | readonly string[], index: number): void;
  /** The order is the order they are applied, so moving one is a real edit. */
  moveEffect(id: string | readonly string[], index: number, to: number): void;
  /** Combines the selection into one shape: union, subtract, intersect or exclude. */
  combine(op: 'union' | 'subtract' | 'intersect' | 'exclude'): void;
  /** Groups the selection and uses the backmost layer to mask the rest. */
  mask(): void;
  /** Puts the selection in a group, as ⌘G does. */
  group(): void;
  /** Takes the chosen groups (and blends, cloners, combined shapes) apart. */
  ungroup(): void;
  /**
   * Repeat (`tools/repeat.ts`): what is chosen becomes a repeat — a cloner — and is chosen;
   * its id, or null when nothing chosen can be repeated. The Edit dock's Repeat button.
   */
  repeat(): string | null;
  /** A variation on a repeat, by its recipe; the repeat stays chosen. */
  addVariation(clonerId: string, recipe: string): void;
  removeVariation(effectorId: string): void;
  /** Make Layers: the copies become ordinary layers. */
  makeLayers(clonerId: string): void;
  /** Stop Repeating: back to what was being copied. */
  stopRepeating(clonerId: string): void;
  /** How big what a repeat copies is: what its Gap is measured from. */
  repeatSource(clonerId: string): { width: number; height: number } | null;
  /** Shapes on its page a repeat could run along, nearest first, no two named alike. */
  repeatPaths(clonerId: string): { id: string; name: string }[];
  /** The chosen blends as the shapes they draw (Expand), or back to their ends (Release). */
  expandBlend(): void;
  createBlend(): void;
  releaseBlend(): void;
  /**
   * The chosen layers drawn into one picture that takes their place — one undo step. The
   * picture is uploaded like any other, so it is a picture everywhere, not only here.
   */
  rasterize(): Promise<void>;
  /**
   * The chosen text layers' letters as editable paths that take their place — one undo
   * step. Fill, outline, effects, opacity, blend, mask and motion stay as they were.
   */
  outlineText(): Promise<void>;
  /** Moves the chosen layers through the stack: all the way, or past one neighbour. */
  arrange(how: 'front' | 'forward' | 'backward' | 'back'): void;
  /** Mirrors the chosen layers about their own middles, where they stand. */
  flip(axis: 'x' | 'y'): void;
  /** Changes fields on one node, as the inspector's own edits do. */
  update(id: string, props: Record<string, unknown>): void;
  /** The same fields on several nodes, as ONE undo step (QA 2026-09-24, artboards-F5). */
  updateAll(ids: readonly string[], props: Record<string, unknown>): void;
  /** Different fields on different nodes, as ONE undo step (Selection colours). */
  updateEach(writes: readonly { id: string; props: Record<string, unknown> }[]): void;
  /** The middle of what a layer draws, in its own space: what it turns about. */
  middleOf(id: string): { x: number; y: number } | null;
  /** The box of what a layer draws, in its parent's space: where it is and how big. */
  boxInParent(id: string): { x: number; y: number; width: number; height: number } | null;
  /** Lines the selection up on one edge, or spreads it evenly. */
  align(how: 'left' | 'centre' | 'right' | 'top' | 'middle' | 'bottom' | 'spread-x' | 'spread-y'): void;
  /** Lays several layers out again as a grid, masonry, column or row, evenly spaced: one undo step. */
  tidyUp(how: TidyHow): void;
  /** An artboard as PNG; ownContent excludes overlapping objects outside its subtree. */
  exportPng(scale: number, artboardId?: string, ownContent?: boolean): Uint8Array | null;
  /** Raster drawing and encoding in an isolated, cancellable worker. */
  exportRaster(options: RasterOptions): Promise<Uint8Array>;
  /**
   * What a video of these artboards needs to be drawn in this browser (`videoExport.ts`), or why
   * it has to be drawn by the render service instead: a clip or a sketch walks forward through
   * state the export page does not have.
   */
  exportVideo(options: VideoOptions): { snapshot: RawVideoSnapshot } | { cloud: string };
  /**
   * An artboard (the one in hand when none is named) as SVG — what the canvas shows, with
   * the canvas's own renderer answering what the document alone cannot — and the notes on
   * anything SVG could not say directly. `pictureScale` is pixels per document unit for
   * whatever had to become a picture.
   */
  exportSvg(artboardId?: string, pictureScale?: number): SvgExport | null;
  /**
   * The artboards an export covers: the one in hand, the ones chosen, or all of them —
   * in the order they sit on the page. Empty when there is nothing to export.
   */
  /** The artboards an export covers, with their sizes in document units. */
  exportBoards(scope: ExportScope): { id: string; name: string; width: number; height: number }[];
  /** Each slice on the page as its own SVG, named after the slice. Empty when there are none. */
  exportSlices(pictureScale?: number): { name: string; svg: string; notes: string[] }[];
  /**
   * The layers a selection export would cover — what is chosen, without the artboards —
   * as how many there are and what to call the file. Null when no layer is chosen.
   */
  exportLayers(): { count: number; name: string; size: { width: number; height: number } } | null;
  /** Those layers alone as a PNG, on nothing: whole, even where their artboard clips them. */
  exportLayersPng(scale: number): Uint8Array | null;
  /**
   * The full originals of the pictures an artboard (or, with none named, the chosen layers)
   * draws, before `exportPng`/`exportLayersPng`: the canvas shows a picture at the size it is
   * drawn on screen, and a PNG to keep wants every pixel.
   */
  loadOriginals(artboardId?: string): Promise<void>;
  /**
   * One layer as it looks — its effects, adjustments and every fill drawn — in its frame, on
   * nothing, drawn from its pictures' full originals at their own resolution (at most `maxEdge`
   * px on its long side). Waits for the originals. Null when it cannot, or one will not load.
   */
  renderLayerPng(id: string, maxEdge: number): Promise<Uint8Array | null>;
  /**
   * A layer as the canvas shows it now, effects and Lumens included, drawn on the canvas's GPU
   * from the pictures already on screen, for a reference going to the AI. Null when a picture is
   * not on screen yet or no GPU surface can be made: the caller uses the export renderer then.
   */
  renderReference(id: string, longEdge: number, format: 'png' | 'jpg'): Promise<Blob | null>;
  /**
   * The Effects tiles: this layer as it is plus one more effect, each kind in its tile look
   * (shell/effectLooks), `longEdge` px square, drawn by the canvas's own painter. Nothing is
   * written: the painter reads the layer through a view that adds the effect. A kind it
   * cannot draw comes back with no picture.
   */
  previewEffects(id: string, types: readonly Effect['type'][], longEdge: number, onTile?: (tile: EffectPreview) => void, signal?: AbortSignal): Promise<EffectPreview[]>;
  /** Those layers alone as SVG, in a box that fits them. */
  exportLayersSvg(pictureScale?: number): SvgExport | null;
  /**
   * How many slices there are.
   *
   * Its own call because the panel shows the number whether or not anybody asks for the
   * files, and writing every slice's SVG to find out how many there are — on every render
   * of the frame — is what that costs if the two questions share an answer.
   */
  sliceCount(): number;
  /** What an export would be called, from the artboard's name. */
  exportName(): string;
  /** The artboard an export means: the one holding the selection, else the last active artboard. */
  exportArtboard(): string | null;
  /**
   * Uploads pictures and places them — at the point given or in the middle of the view,
   * several a fixed step apart in the order given — as one undo step.
   */
  placePicture(files: File | readonly File[], at?: { x: number; y: number }): Promise<void>;
  /**
   * Adds a shape from the library to the page being worked on: in the middle of it, a
   * third of its shorter side across, wearing the colours the tools draw with. One undo
   * step; the shape is chosen afterwards, so the next thing done is done to it.
   */
  addShape(shape: LibraryShape, at?: DropPoint): void;
  /** Adds a line of type from the library — a heading, a subheading, body — and opens it for typing. */
  addText(preset: LibraryText, at?: DropPoint): void;
  /**
   * Paints what is chosen with one colour, each layer where its colour lives: a shape's
   * fill, a line's stroke, a text's own fill, a page's background, and everything inside
   * a group. Pictures have no one colour and are left. One undo step. False when nothing
   * chosen takes a colour. `to: 'stroke'` paints the outlines instead (text and pages have none).
   */
  paintSelection(paint: LibraryPaint, to?: 'stroke'): boolean;
  /** The pictures this file already holds, newest first: what Uploads shows under its button. */
  filePictures(): LibraryPicture[];
  /** Registers a still image for a fill, without creating an image layer. */
  uploadPaintPicture(file: File): Promise<LibraryPicture | null>;
  /** Places a picture the file already holds, or one from elsewhere in Flobi given by its address. */
  addPicture(picture: LibraryPicture, at?: DropPoint): void;
  /** An empty, selected frame for a new generation; existing artwork is left intact. */
  /** `avoid`: boxes a new frame must not land on (the ones still generating). */
  createGenerationTarget(size: { width: number; height: number }, name: string, avoid?: readonly string[]): string | null;
  /** The visible canvas area used by generation placeholders. */
  generationViewport(): ScreenBox | null;
  /** Reveal only when a generation target is outside the visible canvas area. */
  revealGenerationTarget(id: string): void;
  /**
   * Places a drawing from the catalogue — a graphic or an icon, kept as SVG — in the
   * middle of the page being worked on, `share` of its shorter side across, as one group
   * of editable layers. One undo step; chosen afterwards.
   */
  placeDrawing(svg: string, options: { name: string; share: number; at?: DropPoint }): void;
  /**
   * A traced picture (Vectorize): the SVG as editable layers where the picture is, at its size,
   * and the picture hidden — one undo step. Answers the new group's id, or null.
   */
  placeVectorOver(sourceId: string, svg: string, name: string): string | null;
  /**
   * A layer document (Layers with Live Text) on a new artboard beside the others, at the
   * document's size — its pictures, flat colours and live text — with the document's fonts put in
   * the file first; the picture it came from is left as it was. One undo step. Answers the board.
   */
  placeLayerDocument(sourceId: string, doc: LayerDocument, pictures: readonly Result[], fonts: readonly DocumentFont[], size?: { width: number; height: number } | null, name?: string): string | null;
  /** The same for a graphic kept as layers (one saved from the editor), which arrives at the size it was made. */
  placeLayers(layers: CatalogLayers, size: { width: number; height: number }, at?: DropPoint): void;
  /**
   * Starts from a template. A page with nothing on it becomes the template — its size,
   * its colour, its layers; a page somebody has worked on is left alone and the template
   * arrives as a new page beside the others. Its layers sit straight on the page, not in
   * a group, so every line of type is one press away. One undo step.
   */
  placeTemplate(template: { name: string; width: number; height: number; background: string | null; svg?: string | null; layers?: CatalogLayers | null }, at?: DropPoint): void;
  /** One explicit artboard and its contents, including the resources they use. */
  clipArtboard(id: string): { name: string; layers: CatalogLayers; width: number; height: number } | null;
  /** What is chosen as the catalogue keeps a graphic: the layers, moved to the corner of the box they fill. Null with nothing chosen, or a page. */
  clipSelection(): { name: string; layers: CatalogLayers; width: number; height: number } | null;
  /**
   * Swaps the picture in image layers for another, in place: the layer keeps its frame,
   * its place in the stack, its animation, effects and mask, and the new picture fills
   * the frame. What a template is for — the same post, somebody else's photographs.
   */
  replacePicture(ids: readonly string[], file: File): Promise<void>;
  /**
   * How a picture sits in its frame: `fill` covers it (cropping what does not fit), `fit`
   * shows all of it inside, `frame` reshapes the frame to the picture's own proportions.
   */
  fitPicture(ids: readonly string[], how: 'fill' | 'fit' | 'frame'): void;
  /**
   * Lays the artboards out in a grid, in the order given — or, with no order, in the list's
   * order (Tidy). Only ever on a command: never automatically after some other edit. The
   * order becomes the boards' order in the file, which the list shows and a PDF follows.
   */
  arrangeArtboards(options: { order?: string[]; columns: number; gap: number }): void;
  /**
   * Puts a gradient's handles on the canvas — for the fill or the stroke of one shape — or
   * takes them off (null). The picker calls this while it is open on a gradient.
   */
  editGradient(target: { id: string; which: 'fills' | 'strokes'; index?: number } | null): void;
  /** Gives several artboards one preset size, each kept the way up it was. */
  sizeArtboards(ids: readonly string[], preset: ArtboardPreset): void;
  /** Adds an artboard of a known size, beside the ones already there. */
  addArtboard(preset: ArtboardPreset, turned?: boolean, custom?: { name: string; width: number; height: number; fills: Paint[]; preset?: string }): void;
  /**
   * The artboards in the document, in the order they are stacked, with the one being
   * worked on marked — the board holding the selection, else the first.
   */
  artboards(): { id: string; name: string; current: boolean }[];
  /** Brings one artboard into view, filling it, as double-clicking its name does. */
  focusArtboard(id: string): void;
  /** Fits one artboard in the room the chrome leaves, choosing nothing: what is picked stays picked. */
  showArtboard(id: string): void;
  /** Where the view is, said as a string: the same string later means nobody has moved it. */
  viewKey(): string;
  /** Puts this view where another person's is — Follow. A view that is nonsense is ignored. */
  goTo(view: { x: number; y: number; zoom: number }): void;
  /** A layer's box on screen, in CSS pixels in the window; null when it is not on the page. */
  boxOf(id: string): ScreenBox | null;
  /** Where a point of the page is on screen, in CSS pixels in the window. */
  screenAt(at: { x: number; y: number }): { x: number; y: number };
  /** The point of the page under a point on screen (CSS pixels in the window): screenAt's inverse. */
  worldAt(at: { x: number; y: number }): { x: number; y: number };
  /** A layer's box on the page, in document units; null when it is not on the page. */
  worldBoxOf(id: string): { x: number; y: number; width: number; height: number } | null;
  /** Everything on the page, as ⌘A takes it. */
  selectAll(): void;
  /**
   * Where the chosen layers are on screen, and the artboard they share if they share one,
   * in CSS pixels in the window. Null with nothing chosen. The quick bar reads it to sit
   * against the page — or, zoomed right in, against the thing itself.
   */
  selectionPlace(): { box: ScreenBox; board: ScreenBox | null } | null;
  /**
   * Every layer drawn under a point on screen, topmost first, and the groups they sit in
   * after them, innermost first: what "Select layer" offers.
   */
  layersAt(at: { x: number; y: number }): string[];
  /**
   * The locked layers under a point on screen, or the locked groups and pages they sit in.
   * A locked layer cannot be picked, so this is how the context menu still reaches it.
   */
  lockedAt?(at: { x: number; y: number }): string[];
  /** Calls back when the view moves, the choice changes, or a press starts or ends. */
  onViewChange(listener: () => void): () => void;
  /** A canvas press has moved beyond click slop: floating chrome steps aside for a drag. */
  dragging(): boolean;
  /** Copies the selection a little down and right, as ⌘D does. */
  duplicate(): void;
  /**
   * A copy of one layer, with `props` over it, placed beside the original — clear of whatever
   * already stands there, so copies made one after another line up instead of stacking — and
   * chosen. One undo step. Answers the copy's id.
   */
  copyBeside(id: string, props: Record<string, unknown>): string | null;
  /**
   * Pastes shapes from text off the system clipboard, answering whether it was ours.
   *
   * Text the editor did not write is nobody's business here, so it says no and the
   * browser gets on with whatever it would have done.
   */
  pasteText(text: string, at?: { x: number; y: number }): boolean;
  /** Copies the selection, as ⌘C does. False when nothing is chosen. */
  copySelection(): boolean;
  /** Copies the selection and takes it off the page, as ⌘X does. */
  cutSelection(): void;
  /** Whether something was copied in this tab, which a menu's Paste can use without asking to read the clipboard. */
  hasCopy(): boolean;
  /** Copies the look of the chosen layer (the first, when several are), as ⌥⌘C does. False when it has none. */
  copyProperties(): boolean;
  /** Puts the copied look on every chosen layer that can take it, as ⌥⌘V does: one undo step. False when nothing changed. */
  pasteProperties(): boolean;
  /** Whether a look has been copied, for the menus' Paste properties. */
  hasProperties(): boolean;
  /**
   * The eyedropper: the next press on the canvas takes the colour under it. Given `then`,
   * that colour goes to it (the colour picker's own eyedropper); else to `onPickedColour`.
   */
  pickColour(then?: (color: Color) => void): void;
  /**
   * Picking a layer off the canvas for a sketch's picture slot: each of `candidates` is outlined,
   * and the next press on one hands its id to `then` (null when Esc, or a press on nothing, ends it).
   */
  pickLayer(candidates: readonly string[], then: (id: string | null) => void): void;
  /** A small picture of one layer or artboard as it is now, for a slot's thumbnail (PNG data url). */
  thumbnailOf(id: string): Promise<string | null>;
  /** Every colour the open page paints with, the most used first (the picker's In This File). */
  fileColours(): string[];
  /** Puts a layer next to another one in the stack, which is what the layers list drags. */
  move(id: string, target: { parentId: string; before?: string; after?: string }): void;
  /**
   * Sets a text layer's family, keeping the font's faces with the document when it is
   * one the file has not used before.
   */
  setFont(id: string, family: string, font: DocumentFont | null): void;
  /**
   * Keeps a font's faces with the document without setting any layer in it — a font sent
   * from Flobi Fonts (/receive). A family the file has already gains the faces it lacks.
   */
  addFont(font: DocumentFont): void;
  /** The families this document carries, for the picker to offer first. */
  fontsInDocument(): DocumentFont[];
  /** The one anchor chosen on an open path, where it is on the page, and a way to move it. */
  anchor(): { index: number; at: { x: number; y: number } } | null;
  placeAnchor(at: { x: number; y: number }): void;
  /**
   * What the tool in hand wants the rail to show. Read during render rather than pushed,
   * so it can never be stale: the style, the selection and the tool's own settings are
   * all live behind it.
   */
  rail(tool?: ToolId): Rail | null;
  /** Adopt Select before opening points, so the previous tool cannot close them afterward. */
  selectMode(id: string): void;
  /** How many guides the page carries, so the panel can offer to clear them. */
  guideCount(): number;
  clearGuides(): void;
  /** The artboard an Animate panel acts on: the selected one, else the last active artboard. */
  animationTarget(): AnimationTarget | null;
  setAnimation(id: string, animation: Animation | null): void;
  /** Puts one animation on every layer given, or takes it off them. */
  /**
   * Sets the chosen layers' animation — one value for all, or a change worked out from
   * each layer's own (answering with that same object leaves the layer alone). A layer
   * with none of its own is also given `inherited`: what it plays from its page, said as
   * a layer's animation, so a first edit can keep what it was already doing.
   */
  setLayerAnimation(
    ids: readonly string[],
    animation: LayerAnimation | null | ((own: LayerAnimation | null, inherited: LayerAnimation | null) => LayerAnimation | null),
    timing?: Timing | null,
  ): void;
  /** Trims when layers are on the page, or puts them back for the whole of it. */
  setLayerTiming(ids: readonly string[], timing: Timing | null): void;
  setDuration(id: string, seconds: number): void;
  /**
   * Animates a whole page in one named style, in ONE undo step.
   *
   * One step because it is one decision: pressing an animation style and then pressing undo
   * four times because it touched four layers is not undo, it is a chore.
   */
  magicAnimate(id: string, styleId: string, settings?: RecipeSettings): boolean;
  /**
   * Arms the next drag on the canvas to draw this layer's motion path.
   *
   * Armed rather than a tool, because it lasts exactly one gesture: you say draw, you
   * drag, it is drawn. A mode you have to remember to leave is a mode that will eat
   * somebody's next click.
   */
  drawPath(id: string, onDone: (drawn: boolean) => void): void;
  cancelPath(): void;
  isDrawingPath(): boolean;
  /**
   * Plays a page once, from `from` seconds in. `onStop` fires when it finishes (`finished`)
   * or is interrupted, so the chrome can put its button back without having to keep its own
   * clock in step with this one. Returns false when the page has nothing to play.
   */
  play(id: string, onStop: (finished: boolean) => void, from?: number): boolean;
  /**
   * Holds a page on one instant: a playing paused, or a playhead dragged along the
   * timeline. It stays there — redrawn when the page changes under it — until it is played
   * on, moved again or stopped. False when the page has nothing to play.
   */
  seek(id: string, seconds: number, onStop: (finished: boolean) => void): boolean;
  /**
   * Ends a playing or a holding and shows the page as it is stored. The playhead is not
   * lost: it rests where it was (`playhead().resting`), and playing starts from there.
   */
  stop(): void;
  /** Puts the playhead back to the start and forgets it: Animate closing. */
  forgetPlayhead(): void;
  isPlaying(): boolean;
  /**
   * Where the playhead is, in seconds into a page. `held`: the page is shown on that
   * instant. `resting`: nothing plays and the page is shown as stored, but this is where
   * the playhead was left and where playing starts from. Null before anything was played.
   * Read, never pushed.
   */
  playhead(): { id: string; seconds: number; held: boolean; resting: boolean } | null;
  /**
   * Tries an animation on one layer without writing it: that layer plays it, over and
   * over, while everything else stands still. Null ends it. Trying is not an edit — ten
   * moves pointed at in a row are not ten undo steps sent to everyone in the file.
   */
  preview(layerId: string, animation: LayerAnimation | null, hold?: number): void;
  /**
   * The recipe (animation style) this page wears EXACTLY — its own animation and every
   * layer's the one the style, adjusted as the page records, would write — or null.
   * Checked, not trusted: a page edited after a recipe honestly stops being that recipe.
   */
  recipeOf(id: string): Recipe | null;
  /** Which of a recipe's adjustments do something that can be seen on this page: what its gear shows. */
  recipeChoices(id: string, recipe: Recipe): MagicChoices | null;
  /** A page's direct children, bottom to top, with what the timing strip draws of each. */
  timelineOf(id: string): { duration: number; length: number; children: StripInput[] } | null;
  /** Takes the page's animation off, and with `layers` every layer's own and its window too, in one undo step. */
  clearAnimation(id: string, layers: boolean): void;
  /**
   * What an add-on is lent: the open file as a snapshot, the host's three answers, and
   * the one way to write. Null until a file is open.
   */
  addonBridge(): AddonBridge | null;
  /** Puts an add-on's tool behind the 'addon' slot, or empties it. */
  setAddonTool(tool: Tool | null): void;
  /** Lets a layer's picture be placed in its frame on the canvas; null ends it. */
  cropPicture(id: string | null): void;
  /**
   * The press a CMYK file is made for, once it has loaded — what the canvas previews with
   * and what an SVG is written through. Null in an RGB file.
   */
  proofer(): ColorProofer | null;
}

/**
 * The canvas: drawing, the camera, and the pointer and keyboard work.
 *
 * Procreate's shape: the canvas fills the window and every control floats over it. This
 * owns the surface, the camera and the frame, and hands the chrome a small handle; what
 * happens between a press and a release belongs to a tool in ./tools.
 */
export function useEditorCanvas(options: {
  open: OpenDocument | null;
  palette: Palette;
  toolRef: RefObject<ToolId>;
  /** Hand mode changes navigation only; it never changes the drawing tool settings. */
  panRef?: RefObject<boolean>;
  /** The unit the document is thought in, for everything the editor says a number in. */
  unitsRef?: RefObject<LengthUnit>;
  /** The project the file is in, when this person can reach it: its Drive folder is where an upload is kept. */
  projectRef?: RefObject<string | null>;
  /** Whether text layers may be made here (the `artwork.text` flag): pasted words become one only then. */
  textEnabledRef?: RefObject<boolean>;
  styleRef: RefObject<EditorStyle>;
  /**
   * The rulers, the grid, the guides and what a drag is pulled by.
   *
   * By ref like the tool and the unit, and for the same reason: every one of these is
   * read mid-frame, and a frame is not a render. Handing them down as props would mean
   * a toggle rebuilt the whole canvas — the surface, the tile cache and the camera with
   * it — which is a repaint of the document to answer "show me the grid".
   */
  viewRef?: RefObject<ViewSettings>;
  /**
   * Where the ruler corner sits, past the floating chrome. Zero in full screen, where
   * there is no chrome for it to be past.
   */
  insetRef?: RefObject<{ top: number; left: number; right?: number; bottom?: number; reserve?: number }>;
  onSelection: (ids: string[]) => void;
  /**
   * A viewer is looking: the server refuses what they write, so the canvas writes nothing.
   * Pressing pans, the keys that edit do nothing, a drop or a paste is not taken. By ref like
   * the tool: a role arriving must not rebuild the surface.
   */
  readOnlyRef?: RefObject<boolean>;
  /** A selection-tool press on empty canvas, after handles and guides had first refusal. */
  onEmptyCanvasPress?: () => void;
  onZoom: (zoom: number) => void;
  /**
   * The page's guides changed. Nothing else re-renders the chrome for it, so Clear Guides
   * could stay enabled with none left, or greyed with some (QA 2026-09-24, view-snapping-F21).
   */
  onGuidesChanged?: () => void;
  onToolDone: () => void;
  /** A right-click, a long press or three fingers down: where to open the context menu. */
  onQuickMenu?: (at: { x: number; y: number; artboardId?: string | null }) => void;
  /** Four fingers: the interface goes away and the drawing stays. */
  onFullScreen?: () => void;
  /** A colour taken off the canvas with the eyedropper. */
  onPickedColour?: (color: Color) => void;
  /** A rail control moved: what the tool in hand will draw with, and nothing already drawn. */
  onDrawWith?: (patch: Partial<EditorStyle>) => void;
  /** A tool changed its own settings, so the page must read the rail again. */
  onRailChanged?: () => void;
  /** The layer whose picture is being placed in its frame, or null as that ends. */
  onCroppingChanged?: (id: string | null) => void;
  /** A short message for the person: something could not be done, and why. */
  onNotice?: (message: string) => void;
  /** How many pictures are on their way up, so the page can say so (files-doc-F11, F23). */
  onUploading?: (count: number, of: number) => void;
  /**
   * The canvas could not start (the engine or the fonts did not load). `retry` starts
   * it again; null once it has started. Without this a failed start was a blank canvas
   * with no word and no way back but a reload (QA 2026-09-24, files-doc-F8).
   */
  onStartFailed?: (retry: (() => void) | null) => void;
  /** A tool asked for another tool to be put in hand. */
  onChooseTool?: (id: ToolId) => void;
  /** A tool asked for the fields panel. */
  onShowFields?: () => void;
  /** The play mark outside an artboard's corner was pressed: play that page. */
  onPlayPage?: (id: string) => void;
}): CanvasHandle {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /**
   * Who is told the view moved. Kept by the hook, not by one start of the canvas: chrome that
   * subscribed before CanvasKit was up (a run's frame drawn as the file opens) or before a new
   * file replaced the handle would otherwise be holding a stand-in's subscription and never move.
   */
  const viewWatchers = useRef(new Set<() => void>());
  const handleRef = useRef<CanvasHandle>({
    canvasRef,
    zoomBy: () => {},
    fit: () => {},
    actualSize: () => {},
    panBy: () => {},
    select: () => {},
    deleteSelection: () => {},
    restyle: () => {},
    editText: () => {},
    applyShader: async () => {},
    getShader: () => null,
    sketchOf: () => null,
    pickLayer: (_candidates, then) => then(null),
    thumbnailOf: async () => null,
    setSketch: () => {},
    addEffect: () => [],
    setAdjust: () => {},
    setBlur: () => {},
    setEffect: () => {},
    removeEffect: () => {},
    moveEffect: () => {},
    combine: () => {},
    mask: () => {},
    group: () => {},
    ungroup: () => {},
    repeat: () => null,
    addVariation: () => {},
    removeVariation: () => {},
    makeLayers: () => {},
    stopRepeating: () => {},
    repeatSource: () => null,
    repeatPaths: () => [],
    expandBlend: () => {},
    createBlend: () => {},
    releaseBlend: () => {},
    rasterize: async () => {},
    outlineText: async () => {},
    arrange: () => {},
    flip: () => {},
    update: () => {},
    updateAll: () => {},
    updateEach: () => {},
    middleOf: () => null,
    boxInParent: () => null,
    align: () => {},
    tidyUp: () => {},
    exportPng: () => null,
    exportRaster: async () => { throw new Error('The artwork is still loading.'); },
    exportVideo: () => ({ cloud: 'The artwork is still loading.' }),
    exportSvg: () => null,
    exportBoards: () => [],
    arrangeArtboards: () => {},
    editGradient: () => {},
    sizeArtboards: () => {},
    exportSlices: () => [],
    exportLayers: () => null,
    exportLayersPng: () => null,
    loadOriginals: async () => {},
    renderLayerPng: async () => null,
    renderReference: async () => null,
    previewEffects: async () => [],
    exportLayersSvg: () => null,
    sliceCount: () => 0,
    exportName: () => 'artwork',
    exportArtboard: () => null,
    placePicture: async () => {},
    addShape: () => {},
    addText: () => {},
    paintSelection: () => false,
    filePictures: () => [],
    uploadPaintPicture: async () => null,
    addPicture: () => {},
    createGenerationTarget: () => null,
    generationViewport: () => null,
    revealGenerationTarget: () => {},
    placeDrawing: () => {},
    placeVectorOver: () => null,
    placeLayerDocument: () => null,
    placeLayers: () => {},
    placeTemplate: () => {},
    clipArtboard: () => null,
    clipSelection: () => null,
    replacePicture: async () => {},
    fitPicture: () => {},
    addArtboard: () => {},
    artboards: () => [],
    focusArtboard: () => {},
    showArtboard: () => {},
    viewKey: () => '',
    goTo: () => {},
    boxOf: () => null,
    screenAt: (at) => at,
    worldAt: (at) => at,
    worldBoxOf: () => null,
    selectAll: () => {},
    selectionPlace: () => null,
    layersAt: () => [],
    lockedAt: () => [],
    onViewChange: () => () => {},
    dragging: () => false,
    duplicate: () => {},
    copyBeside: () => null,
    insertShape: () => {},
    pasteText: () => false,
    copySelection: () => false,
    cutSelection: () => {},
    hasCopy: () => false,
    copyProperties: () => false,
    pasteProperties: () => false,
    hasProperties: () => false,
    pickColour: () => {},
    fileColours: () => [],
    move: () => {},
    setFont: () => {},
    addFont: () => {},
    fontsInDocument: () => [],
    anchor: () => null,
    placeAnchor: () => {},
    rail: () => null,
    selectMode: () => {},
    guideCount: () => 0,
    clearGuides: () => {},
    animationTarget: () => null,
    setAnimation: () => {},
    setLayerAnimation: () => {},
    setLayerTiming: () => {},
    setDuration: () => {},
    magicAnimate: () => false,
    drawPath: () => {},
    cancelPath: () => {},
    isDrawingPath: () => false,
    play: () => false,
    seek: () => false,
    preview: () => {},
    stop: () => {},
    forgetPlayhead: () => {},
    isPlaying: () => false,
    playhead: () => null,
    recipeOf: () => null,
    recipeChoices: () => null,
    timelineOf: () => null,
    clearAnimation: () => {},
    addonBridge: () => null,
    setAddonTool: () => {},
    cropPicture: () => {},
    proofer: () => null,
  });
  /** Bumped by Try Again after a start that failed, which runs the set-up again. */
  const [attempt, setAttempt] = useState(0);
  const paletteRef = useRef(options.palette);
  paletteRef.current = options.palette;
  const emptyCanvasPressRef = useRef(options.onEmptyCanvasPress);
  emptyCanvasPressRef.current = options.onEmptyCanvasPress;
  const playPageRef = useRef(options.onPlayPage);
  playPageRef.current = options.onPlayPage;
  const rendererRef = useRef<TileRenderer | null>(null);
  const putToolDownRef = useRef<(() => void) | null>(null);

  /**
   * The tool in hand is put down BEFORE the file's documents are torn down. Those go in
   * passive cleanups that run before this hook's, so a Pen path left half-drawn was
   * cancelled on a disconnected document: its delete never went out, and the unfinished
   * path (sometimes a single invisible dot) stayed in the file (QA 2026-09-24,
   * files-doc-F24). Layout cleanups run first, while the connection is still open.
   */
  useLayoutEffect(() => () => putToolDownRef.current?.(), [options.open]);

  // The page colour follows the theme without rebuilding the tile cache.
  useEffect(() => {
    rendererRef.current?.setBackground(rgba(options.palette.canvas));
  }, [options.palette]);

  useEffect(() => {
    const open = options.open;
    const canvas = canvasRef.current;
    if (!open || !canvas) return;
    let disposed = false;
    let raf = 0;
    const cleanups: (() => void)[] = [];
    /** Pictures on their way up for this file; none are, once the file has closed. */
    let uploads = 0;
    /**
     * How many have set off since the last time none were going up: with `uploads`, how far
     * a big drop has got. "Uploading 114 files…" said the same thing until the very last
     * one was in (QA 2026-10-05, ART-IMG-17).
     */
    let uploadsBegun = 0;
    const began = (count: number) => {
      if (uploads === 0) uploadsBegun = 0;
      uploads += count;
      uploadsBegun += count;
      options.onUploading?.(uploads, uploadsBegun);
    };
    const ended = (count: number) => {
      uploads -= count;
      if (!disposed) options.onUploading?.(uploads, uploadsBegun);
    };
    cleanups.push(() => {
      if (uploads > 0) options.onUploading?.(0, 0);
    });
    const { scene, page, undo, pageRoom, me } = open;
    const readOnly = () => options.readOnlyRef?.current === true;
    /** What this person tells the others: cursor, selection, view and the text they are typing in. */
    const publish = createPublisher(pageRoom, open.pageId);

    void (async () => {
      const ck = await loadCanvasKit();
      if (disposed) return;
      const gl = new GlSurface(ck, canvas);
      cleanups.push(() => gl.dispose());
      /**
       * How many device pixels a CSS pixel is — read again every frame.
       *
       * Read once, a browser zoom or a drag to a screen of another density left the
       * canvas drawing at the old sharpness until a reload: blurry, or at wasted cost
       * (QA 2026-09-24, view-snapping-F4). The surface is remade when its pixel size
       * changes, and the tiles and the stipple are keyed by it, so a new value is all
       * a change needs.
       */
      let dpr = window.devicePixelRatio || 1;
      await warmUpRenderer(ck, gl.surfaceFor(canvas.clientWidth, canvas.clientHeight, dpr), { dpr });
      if (disposed) return;

      // Fonts are loaded before the first frame so text is never drawn unshaped.
      const text = await loadText(ck);
      const provider = await loadFonts(ck);
      if (disposed) return;

      let needsFrame = true;
      let sketchesSwept = 0;
      const images = new DocumentImages(ck, open.root, (assetId) => {
        // A picture that has just arrived has to reach the tiles drawn without it — and only those.
        if (assetId) renderer.invalidateImage(assetId);
        else renderer.invalidateAll();
        needsFrame = true;
      }, () => { needsFrame = true; });
      cleanups.push(() => images.dispose());


      /**
       * The rulers, the grid, the checkerboard and the guides.
       *
       * Held here rather than made per frame because two of the four are a cached GPU
       * tile: a checkerboard is a pattern, and the way to draw a pattern is to hand the
       * card one square and let it repeat.
       */
      const furniture = new Furniture(ck, () => paletteRef.current);
      cleanups.push(() => furniture.dispose());
      /** What the person looking at this file has switched on, read afresh every frame. */
      const view = () => options.viewRef?.current ?? VIEW_DEFAULTS;
      const inset = () => options.insetRef?.current ?? { top: 0, left: 0 };
      const rulers = () => rulerLayout(viewSize().width, viewSize().height, inset());
      /**
       * How far apart the document says its grid is, which is the document's business.
       *
       * Kept rather than read, because this is asked on every frame the grid is drawn
       * and on every movement of the hand while something is being dragged — and the
       * answer changes about once a month, when somebody types a new number into the
       * View panel. Re-read when the root document changes, which is the only thing that
       * can change it.
       */
      let grid = { step: readMeta(open.root).gridStep, divisions: readMeta(open.root).gridDivisions };
      const readGrid = () => {
        const meta = readMeta(open.root);
        if (meta.gridStep === grid.step && meta.gridDivisions === grid.divisions) return;
        grid = { step: meta.gridStep, divisions: meta.gridDivisions };
        needsFrame = true;
      };
      open.root.on('update', readGrid);
      cleanups.push(() => open.root.off('update', readGrid));
      const gridOf = () => grid;

      const renderer = new TileRenderer(ck, scene, {
        background: rgba(paletteRef.current.canvas),
        ...(text ? { text } : {}),
        images,
        shaders: shaderSource(open.root),
        ...(lumensGlRunner() ? { lumensGl: lumensGlRunner()! } : {}),
        // Between the page colour and the artwork, which is the only moment a grid can
        // be BEHIND the drawing rather than scratched across the front of it.
        backdrop: (surface, at, viewport) => {
          const settings = view();
          if (!settings.grid && !settings.transparency) {
            furniture.drawDots(surface, { camera: at, width: viewport.width, height: viewport.height, dpr: viewport.dpr, inset: inset() });
            return;
          }
          furniture.drawBackdrop(
            surface,
            { camera: at, width: viewport.width, height: viewport.height, dpr: viewport.dpr, inset: inset() },
            { grid: settings.grid, transparency: settings.transparency, settings: gridOf() },
          );
        },
        onInvalidate: () => {
          needsFrame = true;
        },
      });
      // A sketch whose control names a layer (Letter Boxes' "Text") is handed that layer's outline.
      const sketchShapes = new SketchShapes(ck, renderer);
      images.sketches.shapeOf = (layerId, value, width, height) => sketchShapes.shapeFor(layerId, value, width, height);
      const sketchPictures = new SketchPictures(ck, renderer, (width, height) => gl.offscreen(width, height));
      images.sketches.pictureOf = (layerId, value) => sketchPictures.pictureFor(layerId, value);
      const sketchStills = new SketchStills(
        (bytes) => {
          const image = ck.MakeImageFromEncoded(bytes);
          if (!image) return null;
          const width = image.width(), height = image.height();
          const pixels = image.readPixels(0, 0, { width, height, colorType: ck.ColorType.RGBA_8888, alphaType: ck.AlphaType.Unpremul, colorSpace: ck.ColorSpace.SRGB }) as Uint8Array | null;
          image.delete();
          return pixels ? { width, height, pixels } : null;
        },
        async (src) => new Uint8Array(await (await fetch(src)).arrayBuffer()),
        () => { renderer.invalidateAll(); needsFrame = true; },
      );
      images.sketches.stillOf = (src) => sketchStills.stillFor(src);
      cleanups.push(() => { images.sketches.shapeOf = null; });

      // The families this file asked for, fetched and registered as they arrive.
      if (text && provider) {
        const fonts = new DocumentFonts(open.root, provider, text, () => {
          renderer.invalidateAll();
          needsFrame = true;
          // A text given a font that had not arrived was measured in the fallback; now
          // that it is here, measure it again.
          for (const id of awaitingFonts) if (scene.get(id)) editing?.refit(id);
          awaitingFonts.clear();
        });
        cleanups.push(() => fonts.dispose());
      }
      rendererRef.current = renderer;
      // How big each picture is drawn, so it is loaded at that size (`canvas/images.ts`):
      // worked out when asked, again only after the page changed.
      let drawnPlaces: PicturePlaces | null = null;
      const places = () => (drawnPlaces ??= picturePlaces(scene, open.root, (id) => renderer.bounds.worldGeometryBounds(id)));
      images.setSizes(() => places().sizes);
      images.setBoxes(() => places().boxes);
      cleanups.push(scene.subscribe(() => {
        drawnPlaces = null;
        images.sizesChanged();
      }));
      cleanups.push(() => {
        renderer.dispose();
        rendererRef.current = null;
      });
      // The GPU came back after the browser took it away: the tiles went with it.
      gl.onRestored = () => {
        renderer.dropTiles();
        needsFrame = true;
      };

      /**
       * The file's colour mode. In CMYK the canvas shows every colour as the press will
       * print it: the press is opened (once a session) and handed to the renderer as a pass
       * over each finished frame; back in RGB the pass goes. Read from the root document
       * on every change, so a switch made by anybody else reaches this canvas too.
       */
      let proofer: ColorProofer | null = null;
      let colorMode: 'rgb' | 'cmyk' | null = null;
      const readColorMode = () => {
        const mode = readMeta(open.root).colorMode;
        if (mode === colorMode) return;
        colorMode = mode;
        if (mode === 'rgb') {
          proofer = null;
          renderer.setProof(null);
          needsFrame = true;
          return;
        }
        openPress()
          .then((press) => {
            // Switched back (or closed) while the press was loading: nothing to show.
            if (disposed || colorMode !== 'cmyk') return;
            proofer = press;
            renderer.setProof(new ProofPass(ck, pressLut(press), PRESS_LUT_SIZE));
            needsFrame = true;
          })
          .catch((error: unknown) => {
            console.error('The press preview could not start', error);
            options.onNotice?.('The print preview could not load.');
            // Tried again on the next change of mode.
            if (colorMode === 'cmyk') colorMode = null;
          });
      };
      open.root.on('update', readColorMode);
      cleanups.push(() => open.root.off('update', readColorMode));
      readColorMode();

      /** Told whenever the selection changes; filled in once the editors below exist. */
      const selectionWatchers: ((ids: readonly string[]) => void)[] = [];
      /** Texts whose font changed here and may not have loaded when they were measured. */
      const awaitingFonts = new Set<string>();
      /** Re-measure a text after a change that moves its letters: size, weight, spacing, font. */
      const refitText = (id: string, props?: Record<string, unknown>) => {
        const node = scene.get(id);
        if (node?.type !== 'text' || !editing) return;
        if (props && !('style' in props) && !('runs' in props) && !('sizing' in props)) return;
        editing.refit(id);
      };
      const editing = text
        ? new TextEditing({
            page,
            scene,
            text,
            origin: LOCAL_ORIGIN,
            onChange: () => {
              caretOn = true;
              needsFrame = true;
              // The text being typed in carries the person's name for everyone else (the soft lock).
              publish.editing(editing?.editingId ?? null);
            },
            onNotice: (message) => options.onNotice?.(message),
            onExit: (left) => {
              // Typing is one step per visit to a text; leaving it ends that step.
              undo.stopCapturing();
              // Only Esc hands the canvas back to Select. Ending because another tool
              // took over — the Pen's press, a tool picked in the dock — keeps that tool
              // (QA 2026-09-24, text-F19).
              if (left) options.onToolDone();
            },
            // ⌘Z while typing reaches the document's history (text-F12).
            onUndo: (redo) => {
              undo.stopCapturing();
              if (redo) undo.redo();
              else undo.undo();
            },
          })
        : null;
      cleanups.push(() => editing?.dispose());

      /**
       * Text nobody here measured: the engine cannot size a text (the shaper lives in the
       * editor), so a text an agent, an add-on or the backend wrote arrives with a box of
       * 0 × 0 — drawn, but impossible to click, and framed by nothing when chosen from the
       * layers list. Every such text is measured when the page opens and whenever one comes
       * in from outside; a text already measured elsewhere measures the same and writes
       * nothing. Written under MEASURE_ORIGIN, so it is never an undo step.
       */
      const outsideText = new Set<string>();
      let measureQueued = false;
      const measureOutside = () => {
        measureQueued = false;
        // A viewer writes nothing, not even a measurement: the server would refuse it.
        if (!editing || readOnly()) {
          outsideText.clear();
          return;
        }
        const ids = [...outsideText];
        outsideText.clear();
        for (const id of ids) {
          if (!scene.get(id)) continue;
          editing.refit(id, MEASURE_ORIGIN);
          // Measured in whatever font is here now; measured again once the file's own arrive.
          awaitingFonts.add(id);
        }
      };
      const measureSoon = (id: string) => {
        const node = scene.get(id);
        if (node?.type !== 'text' || node.sizing === 'fixed') return;
        outsideText.add(id);
        if (measureQueued) return;
        measureQueued = true;
        // Not inside the transaction that brought it: a write there would be refused.
        queueMicrotask(measureOutside);
      };
      for (const id of scene.ids()) {
        const node = scene.get(id);
        if (node?.type === 'text' && !(node.width > 0 && node.height > 0)) measureSoon(id);
      }
      cleanups.push(
        scene.subscribe((change) => {
          if (change.origin === LOCAL_ORIGIN || change.origin === MEASURE_ORIGIN) return;
          for (const id of change.added) measureSoon(id);
          for (const [id, update] of change.updated) {
            const keys = update.keys;
            // What its letters are, not the box it was given: answering a box written by
            // another editor would have two editors whose fonts measure a pixel apart
            // correct each other for ever.
            if (keys.has('*') || keys.has('runs') || keys.has('style') || keys.has('sizing')) measureSoon(id);
          }
        }),
      );

      /** Tells the tool in hand that no path has its points open any more; set once the tools exist. */
      let pointsLeft: (() => void) | null = null;
      const pointsChanged = whenPointsLeft(() => pathEditing.active, () => pointsLeft?.());
      const pathEditing = new PathEditing({
        page,
        scene,
        origin: LOCAL_ORIGIN,
        toScreen: (point) => ({ x: (point.x - camera.x) * camera.zoom, y: (point.y - camera.y) * camera.zoom }),
        // The same few screen pixels a shape snaps within, turned into units at this zoom
        // so the pull feels identical whether a point or a rectangle is being dragged.
        snapReach: () => 6 / camera.zoom,
        /**
         * What a dragged anchor can agree with, out of everything on screen.
         *
         * Every shape in view offers its corners, the middles of its sides and its centre
         * — the same marks a shape snaps to another shape by — and every path in view
         * offers its anchors as well, so a point can land level with a corner of one
         * thing and the anchor of another. Bounded by the viewport rather than the page:
         * agreeing with something nobody can see reads as stopping for no reason.
         */
        snapTargets: () => {
          // What the toggles allow is decided HERE, once, rather than by the thing doing
          // the arithmetic: a shape's edges are smart guides and another path's anchors
          // are snap-to-point, and with both off there is nothing to gather.
          const allowed = view();
          if (!allowed.smart && !allowed.snapPoint) return [];
          const { width, height } = viewSize();
          const seen: Rect = { x: camera.x, y: camera.y, width: width / camera.zoom, height: height / camera.zoom };
          const open = pathEditing.editingId;
          const out: { x: number; y: number }[] = [];
          for (const id of renderer.unitsIn(seen)) {
            if (id === open) continue;
            const node = scene.get(id);
            // Nothing that paints nothing: an effector's field, an export slice (cloner-F16).
            if (!node || node.locked || node.type === 'effector' || node.type === 'slice') continue;
            const box = allowed.smart ? renderer.bounds.worldGeometryBounds(id) : null;
            if (box) {
              const midX = box.x + box.width / 2;
              const midY = box.y + box.height / 2;
              const right = box.x + box.width;
              const bottom = box.y + box.height;
              out.push(
                { x: box.x, y: box.y },
                { x: right, y: box.y },
                { x: box.x, y: bottom },
                { x: right, y: bottom },
                { x: midX, y: midY },
                { x: midX, y: box.y },
                { x: midX, y: bottom },
                { x: box.x, y: midY },
                { x: right, y: midY },
              );
            }
            if (allowed.snapPoint && node.type === 'path') for (const anchor of pathEditing.anchorsOf(id) ?? []) out.push(anchor.at);
            // A viewport crowded past this is a viewport where one more candidate changes
            // nothing, and the walk is done on every move of the hand.
            if (out.length > 2000) break;
          }
          return out;
        },
        snapSettings: () => {
          const settings = snapping();
          return { ...settings, smart: settings.smart || settings.toGuides };
        },
        snapGuides: () => liveGuides(),
        onChange: () => {
          needsFrame = true;
          pointsChanged();
        },
      });
      // Point editing and typing end when the selection moves off their layer.
      selectionWatchers.push((ids) => {
        if (pathEditing.active && pathEditing.editingId && !ids.includes(pathEditing.editingId)) pathEditing.close();
        if (editing?.current && !ids.includes(editing.current.id)) editing.stop();
      });
      cleanups.push(() => {
        // The file is closing: nobody is left to tell.
        pointsLeft = null;
        pathEditing.close();
      });
      // The caret blinks on its own clock, so a still canvas still shows it moving.
      let caretOn = true;
      const blink = setInterval(() => {
        if (!editing?.current) return;
        caretOn = !caretOn;
        needsFrame = true;
      }, 530);
      cleanups.push(() => clearInterval(blink));

      const viewSize = () => ({ width: canvas.clientWidth, height: canvas.clientHeight });
      const contentBounds = (): Rect => {
        // What is SHOWN: Fit and the opening view used to make room for hidden layers and
        // leave empty space (QA 2026-09-24, view-snapping-F9). All of it when nothing is.
        let bounds: Rect | null = null;
        let hidden: Rect | null = null;
        for (const id of scene.childrenOf(ROOT_ID)) {
          const box = renderer.bounds.worldVisualBounds(id);
          if (scene.get(id)?.visible === false) hidden = union(hidden, box);
          else bounds = union(bounds, box);
        }
        return bounds ?? hidden ?? { x: 0, y: 0, width: 1080, height: 1080 };
      };
      /** Fits something into the part of the window the chrome is not sitting on. */
      const fitBox = (box: Rect) => fitCamera(box, viewSize().width, viewSize().height, 48, inset());
      /**
       * The middle of the part of the window the chrome leaves free, in CSS pixels — what
       * the zoom buttons and keys zoom about. The window's own middle sits under the side
       * panel's edge when it is open, and the drawing slid left with every step
       * (QA 2026-09-24, view-snapping-F11).
       */
      const viewCentre = (): { x: number; y: number } => {
        const { width, height } = viewSize();
        const room = inset() as { top: number; left: number; right?: number; bottom?: number };
        const left = Math.min(room.left, width);
        const top = Math.min(room.top, height);
        const right = Math.max(left, width - (room.right ?? 0));
        const bottom = Math.max(top, height - (room.bottom ?? 0));
        return { x: (left + right) / 2, y: (top + bottom) / 2 };
      };
      /** 100%, keeping what is at the middle of the free space there. */
      const actualSizeCamera = (): Camera => {
        const at = viewCentre();
        return { x: camera.x + at.x / camera.zoom - at.x, y: camera.y + at.y / camera.zoom - at.y, zoom: 1 };
      };
      let camera: Camera = fitBox(contentBounds());
      /** What the others have chosen (not their own other tabs), and the key that says when it changed. */
      let remoteChosen: readonly RemoteSelection[] = remoteSelections(pageRoom.peers, me.id, peerColor);
      let remoteKey = selectionKey(remoteChosen);
      /**
       * The camera above was fitted to NOTHING if the page had not arrived yet — and it
       * usually has not: the page document is handed over empty and its contents stream
       * in over the live connection afterwards. So a file opened on a stand-in box at the
       * origin, and anything bigger or elsewhere showed a corner or an empty screen until
       * somebody pressed 0. The first contents FROM THE SERVER fit the view again — unless
       * the person has already moved it, and never for their own first shape in an empty
       * file, which is local and must not make the view jump.
       */
      let fittedToNothing = scene.childrenOf(ROOT_ID).length === 0;
      const openingCamera = camera;
      publish.view(camera, viewCentre());
      let selection: string[] = [];
      const activeArtboard = new ActiveArtboard(id => scene.get(id));
      /** Where a pan began, or null: the one gesture the canvas keeps for itself. */
      let panning: { x: number; y: number } | null = null;
      let spaceHeld = false;
      /** True while the next press should take a colour rather than choose a shape: where that colour goes. */
      let picking: false | ((color: Color) => void) = false;
      /** While a sketch's picture slot is being filled from the canvas: what may be picked, the one under the pointer, and where it goes. */
      let layerPick: { candidates: Set<string>; hovered: string | null; then: (id: string | null) => void } | null = null;
      /** The candidate a point on the page is in: the deepest one under it, or the artboard it sits on. */
      const pickAt = (world: { x: number; y: number }): string | null => {
        if (!layerPick) return null;
        for (let id: string | undefined | null = hitTest(world); id && id !== ROOT_ID; id = scene.parentOf(id)) if (layerPick.candidates.has(id)) return id;
        for (const id of layerPick.candidates) {
          const box = renderer.bounds.worldGeometryBounds(id);
          if (scene.get(id)?.type === 'artboard' && box && world.x >= box.x && world.y >= box.y && world.x <= box.x + box.width && world.y <= box.y + box.height) return id;
        }
        return null;
      };
      /** True while more than one finger is on the canvas: the hand, not a tool. */
      let handOnCanvas = false;

      // ── guides ───────────────────────────────────────────────────────────
      /**
       * The page's guides, kept rather than read per frame.
       *
       * They live in their own map on the page document, beside the nodes and not among
       * them — a guide is not artwork and has no business in the layers list, the paint
       * order or an export. Which means the scene's own subscription says nothing about
       * them, and this needs its own.
       */
      let guides: Guide[] = listGuides(page);
      const guideStore = pageGuides(page);
      const readGuides = () => {
        const had = guides.length;
        guides = listGuides(page);
        needsFrame = true;
        // Only a change in how many there are can change what the chrome shows.
        if ((had === 0) !== (guides.length === 0)) options.onGuidesChanged?.();
      };
      guideStore.observe(readGuides);
      cleanups.push(() => guideStore.unobserve(readGuides));
      /**
       * A guide under the hand: one being dragged out of a ruler, or one being moved.
       *
       * `id` is null until a drag out of a ruler has actually made one — which happens on
       * the first move, not on the press, so that a click on a ruler leaves nothing
       * behind.
       */
      let holding: { id: string | null; axis: 'x' | 'y'; started: boolean; at: number } | null = null;
      /** Where the pointer is, in document coordinates, for the marks on the rulers. */
      let pointerAt: { x: number; y: number } | null = null;
      /** Which guides the tool's last snap landed on, so they can be drawn as catching. */
      let litGuides: readonly LitGuide[] = [];
      /** And the lines explaining a point that landed on something, from `place`. */
      let litLines: readonly SnapLine[] = [];
      /** True while the cursor on screen is ours rather than the tool's. */
      let furnitureCursor = false;
      /**
       * The furniture as the last frame drawn it, so a change to it can ask for a frame.
       *
       * The rulers, the grid, the guides and where they start are read from refs the
       * chrome writes — which is what keeps a toggle from rebuilding the surface and the
       * tile cache. But writing a ref is neither a render nor an event, and a frame is
       * only drawn when something asks for one, so nothing here ever did: switch the
       * grid off with the pointer over the panel and the grid stayed on the screen until
       * something unrelated happened to cause a repaint. The toggles looked broken, and
       * from where anybody was sitting they were.
       */
      let shown = { view: view(), top: inset().top, left: inset().left, right: inset().right, bottom: inset().bottom, unit: options.unitsRef?.current ?? 'px' };

      /**
       * Whoever sits chrome against the artwork, told when it might have to move. A pan
       * is not a render, so without this the quick bar would stay where the thing was.
       */
      const viewListeners = viewWatchers.current;
      const viewChanged = () => viewListeners.forEach((listener) => listener());
      /** A press is down on the canvas. */
      let pressed = false;
      let pressOrigin = { x: 0, y: 0 };
      let dragging = false;
      /**
       * Whether the press being released went to the tool. The release is heard on the
       * window, and used to reach the tool after a pan or a press that began on the chrome
       * — which ended a click-to-place line mid-way (QA 2026-09-24, shapes-F4).
       */
      let toolPressed = false;

      const setCamera = (next: Camera) => {
        camera = next;
        needsFrame = true;
        options.onZoom(next.zoom);
        // Where this person is looking, for whoever follows them.
        publish.view(next, viewCentre());
        viewChanged();
      };
      const groupEditing = new GroupEditing(scene, id => renderer.bounds.worldGeometryBounds(id));
      const setSelection = (ids: string[]) => {
        groupEditing.acceptSelection(ids);
        selection = ids;
        activeArtboard.resolve(ids);
        needsFrame = true;
        options.onSelection(ids);
        publish.selection(ids);
        viewChanged();
        for (const watch of selectionWatchers) watch(ids);
      };
      /** A box in the document, as it lands on screen. */
      const onScreen = (world: Rect): ScreenBox => {
        const frame = canvas.getBoundingClientRect();
        return {
          left: frame.left + (world.x - camera.x) * camera.zoom,
          top: frame.top + (world.y - camera.y) * camera.zoom,
          right: frame.left + (world.x + world.width - camera.x) * camera.zoom,
          bottom: frame.top + (world.y + world.height - camera.y) * camera.zoom,
        };
      };
      /** The artboard a layer is on, however deep inside groups it sits, or null. */
      const artboardOf = (id: string): string | null => {
        let at = scene.get(id);
        while (at) {
          if (at.type === 'artboard') return at.id;
          if (!at.parentId || at.parentId === ROOT_ID) return null;
          at = scene.get(at.parentId);
        }
        return null;
      };
      const toWorld = (clientX: number, clientY: number) => {
        const box = canvas.getBoundingClientRect();
        return { x: camera.x + (clientX - box.left) / camera.zoom, y: camera.y + (clientY - box.top) / camera.zoom };
      };
      const edit = (ops: unknown[]) => applyOps(page, ops, { scene, origin: LOCAL_ORIGIN });

      /**
       * Closes the step after a panel edit — unless a slider or scrub is being held, which
       * is one step closed when the hand comes up (see `onHold` below). The Animate panel's
       * setters closed a step per notch, so a slider drag took dozens of undos to take
       * back (QA 2026-09-24, anim-F4).
       */
      const seal = () => {
        if (!panelHeld()) undo.stopCapturing();
      };
      // A panel control held by the hand (a slider, a scrub, the colour square) is one undo
      // step: closed as it is taken hold of, so nothing earlier joins it, and again when
      // the hand comes up (QA 2026-09-24, inspector-numbers-F10).
      cleanups.push(onHold(() => undo.stopCapturing()));

      /** What a drag is allowed to be pulled by, as the four toggles and the grid say. */
      const snapping = (): SnapSettings => snapSettingsOf(view(), gridOf(), 1 / camera.zoom);
      /**
       * The guides a drag may land on: the ones that are SHOWN.
       *
       * A hidden guide that still caught things would be the worst of both — a drag
       * stopping at a place with nothing on screen to explain it. Hiding them is how a
       * person says "not these, not now".
       */
      const liveGuides = (): SnapGuide[] => (view().guides ? guides.map((guide) => ({ axis: guide.axis, at: guide.at })) : []);
      /**
       * Where a guide being dragged should land.
       *
       * A guide is one number, so it snaps the way everything else does but on one axis:
       * onto an edge or middle of anything on screen, onto another guide, onto the grid,
       * onto a whole unit. Without this a guide was the one thing on the page that could
       * not be put anywhere exactly — which for the tool whose entire job is precision is
       * a poor showing, and is most of what "guides have no snapping" meant.
       */
      const guidePlace = (axis: 'x' | 'y', self: string | null, at: number): number => {
        const settings = snapping();
        if (!settings.smart && !settings.toGuides && !settings.toGrid && !settings.toPixel) return at;
        const places: number[] = [];
        if (settings.smart) {
          const { width, height } = viewSize();
          const seen: Rect = { x: camera.x, y: camera.y, width: width / camera.zoom, height: height / camera.zoom };
          for (const id of renderer.unitsIn(seen)) {
            const node = scene.get(id);
            // Locked shapes count, as they do for a move and for drawing (view-snapping-F10).
            if (!node || !snapsTo(id)) continue;
            const rect = renderer.bounds.worldGeometryBounds(id);
            if (!rect) continue;
            const near = axis === 'x' ? rect.x : rect.y;
            const size = axis === 'x' ? rect.width : rect.height;
            places.push(near, near + size / 2, near + size);
            if (places.length > 2000) break;
          }
        }
        // Other guides on the same axis, so a run of columns can be set out evenly by
        // eye and still come out exact. Never itself: a guide that snapped to where it
        // already was could not be moved at all.
        if (settings.toGuides) {
          for (const guide of guides) if (guide.axis === axis && guide.id !== self) places.push(guide.at);
        }
        return at + snapAlong(at, places, settings, GUIDE_SNAP * (1 / camera.zoom)).shift;
      };

      /**
       * Everything near a box that a drag of `moving` can line itself up with.
       *
       * `unitsIn` answers with the top-level layer, so a shape moved inside a group met
       * its own group — whose box is where the shape was a moment ago — and snapped to its
       * own past (QA 2026-09-24, view-snapping-F3). A group holding a moving shape is
       * opened into its other layers instead; an artboard holding one stays, because its
       * edges do not follow what is inside it and its layers are units of their own.
       */
      /** Whether a layer is something to line up with: one that paints (cloner-F16). */
      const snapsTo = (id: string): boolean => {
        const kind = scene.get(id)?.type;
        return !!kind && kind !== 'effector' && kind !== 'slice';
      };

      const snapNeighbours = (box: Rect, moving: readonly string[]): string[] => {
        const skip = new Set(moving);
        const out: string[] = [];
        const meets = (id: string) => {
          const b = renderer.bounds.worldGeometryBounds(id);
          return !!b && b.x <= box.x + box.width && b.y <= box.y + box.height && b.x + b.width >= box.x && b.y + b.height >= box.y;
        };
        const visit = (id: string) => {
          if (skip.has(id) || moving.some((one) => scene.isAncestor(one, id))) return;
          // An effector's box is its field and a slice's is an export region: neither
          // paints anything, so a shape pulled to one lines up with nothing anyone can see
          // (QA 2026-09-24, cloner-F16).
          if (!snapsTo(id)) return;
          if (scene.get(id)?.type !== 'artboard' && moving.some((one) => scene.isAncestor(id, one))) {
            for (const child of scene.childrenOf(id)) if (meets(child)) visit(child);
            return;
          }
          out.push(id);
        };
        for (const id of renderer.unitsIn(box)) visit(id);
        return out;
      };

      /**
       * Where a point being placed should land, and the marks that say why.
       *
       * Every tool that puts something down — a corner dragged out, an anchor going in,
       * a handle being sized — asks this rather than gathering its own candidates. One
       * answer, so drawing a rectangle and moving one afterwards agree about what counts
       * as lined up; eight tools deciding for themselves is eight slightly different
       * answers, and the second act undoing the care taken in the first.
       */
      const place = (world: { x: number; y: number }, ignore?: readonly string[], axes = { x: true, y: true }): { x: number; y: number } => {
        const settings = snapping();
        if (!settings.smart && !settings.toGuides && !settings.toGrid && !settings.toPixel) return world;
        const reach = GUIDE_SNAP * (1 / camera.zoom);
        const targets: { x: number; y: number }[] = [];
        if (settings.smart) {
          // Lining up is per axis: an edge meets a shape's edge however far along it the
          // shape is. So look as far around as a move does (select's SNAP_LOOK) — a box of
          // twice the reach about the point found only shapes touching the pointer, and a
          // corner being sized never lined up with the picture beside it (Tarek, 14 Oct:
          // "why guideline snapping is not working while I'm scaling").
          const look = PLACE_LOOK / camera.zoom;
          for (const id of snapNeighbours({ x: world.x - look, y: world.y - look, width: look * 2, height: look * 2 }, ignore ?? [])) {
            const node = scene.get(id);
            // A slice is a question asked of the page rather than a thing on it, so it is
            // not something to line up with. An artboard is: its edges are the margins
            // most of what gets drawn is drawn against. A locked shape is too — a lock stops
            // it moving, not being lined up with — as a move already counted it; drawing
            // and moving disagreed (QA 2026-09-24, view-snapping-F10).
            if (!node || !snapsTo(id)) continue;
            const box = renderer.bounds.worldGeometryBounds(id);
            if (!box) continue;
            const midX = box.x + box.width / 2;
            const midY = box.y + box.height / 2;
            const right = box.x + box.width;
            const bottom = box.y + box.height;
            targets.push(
              { x: box.x, y: box.y },
              { x: right, y: box.y },
              { x: box.x, y: bottom },
              { x: right, y: bottom },
              { x: midX, y: midY },
              { x: midX, y: box.y },
              { x: midX, y: bottom },
              { x: box.x, y: midY },
              { x: right, y: midY },
            );
            if (targets.length > 900) break;
          }
        }
        const snap = snapDragPoint(world, { targets, guides: liveGuides() }, settings, reach);
        const kept = (axis: 'x' | 'y') => axes[axis];
        litGuides = (snap.onGuides ?? []).filter((guide) => kept(guide.axis));
        litLines = snap.lines.filter((line) => kept(line.axis));
        needsFrame = true;
        return { x: world.x + (axes.x ? snap.dx : 0), y: world.y + (axes.y ? snap.dy : 0) };
      };

      /** The guide under a point on the glass, if the hand is near enough to take it. */
      const guideUnder = (clientX: number, clientY: number): Guide | null => {
        const settings = view();
        if (!settings.guides || settings.lockGuides) return null;
        const box = canvas.getBoundingClientRect();
        const x = clientX - box.left;
        const y = clientY - box.top;
        if (settings.rulers && bandAt(x, y, rulers()) !== null) return null;
        let best: { guide: Guide; away: number } | null = null;
        for (const guide of guides) {
          const at = guide.axis === 'x' ? (guide.at - camera.x) * camera.zoom : (guide.at - camera.y) * camera.zoom;
          const away = Math.abs(at - (guide.axis === 'x' ? x : y));
          if (away <= GUIDE_REACH && (!best || away < best.away)) best = { guide, away };
        }
        return best?.guide ?? null;
      };

      // ── the frame ────────────────────────────────────────────────────────
      const selectionPaint = new ck.Paint();
      selectionPaint.setStyle(ck.PaintStyle.Stroke);
      selectionPaint.setAntiAlias(true);
      const handlePaint = new ck.Paint();
      handlePaint.setAntiAlias(true);
      const stipplePaint = new ck.Paint();
      stipplePaint.setAntiAlias(true);
      stipplePaint.setStyle(ck.PaintStyle.Fill);
      /**
       * The stipple: one dot, tiled forever, as a shader.
       *
       * A screen of dots is a PATTERN, and the way to draw a pattern is to hand the
       * graphics card one tile and let it repeat — not to place each dot. Drawing them
       * one at a time cost forty milliseconds a frame for a single region, and clipping
       * the region's shape out of a flat wash is no better: an antialiased clip on a
       * curved path makes a coverage mask the size of the region on every frame.
       *
       * A tiled shader is neither. The dots are the paint, the region is the shape, and
       * the whole hint is one call whose cost does not depend on how large the region is.
       */
      let stipple: { image: Image; shader: Shader; dpr: number; colour: string } | null = null;
      const stippleShader = () => {
        const colour = paletteRef.current.selection;
        if (stipple && stipple.dpr === dpr && stipple.colour === colour) return stipple.shader;
        stipple?.shader.delete();
        stipple?.image.delete();
        stipple = null;
        // A whole number of pixels, or the repeat lands between them and the screen moires.
        const size = Math.max(4, Math.round(5 * dpr));
        const tile = ck.MakeSurface(size, size);
        if (!tile) return null;
        const accent = hexColor(colour);
        const dot = new ck.Paint();
        dot.setAntiAlias(true);
        dot.setColor(ck.Color4f(accent.r, accent.g, accent.b, 1));
        tile.getCanvas().drawCircle(size / 2, size / 2, Math.max(0.75, size / 5), dot);
        dot.delete();
        const image = tile.makeImageSnapshot();
        tile.delete();
        const shader = image.makeShaderOptions(ck.TileMode.Repeat, ck.TileMode.Repeat, ck.FilterMode.Linear, ck.MipmapMode.None);
        stipple = { image, shader, dpr, colour };
        return shader;
      };
      // Unchosen anchors are white with an accent edge, whatever the theme: they sit on
      // the artwork rather than on the chrome, and white reads on anything.
      const whitePaint = new ck.Paint();
      whitePaint.setAntiAlias(true);
      whitePaint.setColor(ck.Color4f(1, 1, 1, 1));
      // The hairline round a chosen handle, so an accent square reads on accent-coloured
      // artwork the same way a white one reads on white.
      const edgePaint = new ck.Paint();
      edgePaint.setAntiAlias(true);
      edgePaint.setStyle(ck.PaintStyle.Stroke);
      edgePaint.setColor(ck.Color4f(1, 1, 1, 1));
      cleanups.push(() => {
        selectionPaint.delete();
        handlePaint.delete();
        stipplePaint.delete();
        stipple?.shader.delete();
        stipple?.image.delete();
        stipple = null;
        whitePaint.delete();
        edgePaint.delete();
      });

      /**
       * What the tools draw with. Everything they ask for is in document coordinates
       * and every width is in screen pixels, so no tool has to know about the camera,
       * the device's pixel ratio or the theme.
       */
      /**
       * A gradient being edited on the canvas: whose, which paint list, and the handle in
       * the hand. The handles are drawn by the canvas above whatever tool is in hand and a
       * press on one is the canvas's, because the picker that asked for them works with any
       * tool — and a tool that had to know about gradients would be every tool.
       */
      let gradientEdit: { id: string; which: 'fills' | 'strokes'; index?: number } | null = null;
      let gradientHeld: HandleId | null = null;
      /** Within this many screen pixels of a handle, a press takes it. */
      const GRADIENT_REACH = 9;

      /** The box a paint is stored against, in the shape's own coordinates, as the renderer has it. */
      const paintBoxOf = (node: NodeRecord): Rect | null => {
        if (node.type === 'boolean') return null;
        if (node.type === 'path') {
          const cmds = nodeCommands(node);
          return cmds ? commandsBounds(cmds) : null;
        }
        if (node.type === 'line') return { x: 0, y: 0, width: (node as { length?: number }).length ?? 0, height: 0 };
        const sized = node as { width?: number; height?: number };
        return { x: 0, y: 0, width: sized.width ?? 0, height: sized.height ?? 0 };
      };

      const gradientInHand = () => {
        if (!gradientEdit) return null;
        const node = scene.get(gradientEdit.id);
        const list = node?.type === 'text' && gradientEdit.which === 'fills' ? node.style.fills : (node as Record<string, unknown> | undefined)?.[gradientEdit.which] as Paint[] | undefined;
        const paint = list?.[gradientEdit.index ?? 0];
        if (!node || !paint || (paint.type !== 'linear' && paint.type !== 'radial' && paint.type !== 'conic')) return null;
        const box = paintBoxOf(node);
        if (!box) return null;
        const world = scene.worldTransform(node.id);
        const handles = gradientHandles(paint, box).map((handle) => ({ ...handle, world: applyToPoint(world, handle.at.x, handle.at.y) }));
        return { node, list, paint, box, world, handles };
      };

      /** The handle a point in the document is on, if any. */
      const gradientHandleAt = (point: { x: number; y: number }) => {
        const held = gradientInHand();
        if (!held) return null;
        const reach = GRADIENT_REACH / camera.zoom;
        let best: HandleId | null = null;
        let near = reach;
        for (const handle of held.handles) {
          const away = Math.hypot(handle.world.x - point.x, handle.world.y - point.y);
          // On a tie the first handle — the middle — keeps the press; the last used to take
          // it, so a radius handle on top of the middle made it immovable
          // (QA 2026-09-24, paint-F28).
          if (away < near || (best === null && away <= near)) {
            near = away;
            best = handle.id;
          }
        }
        return best;
      };

      const makeOverlay = (c: ReturnType<ReturnType<GlSurface['surfaceFor']>['getCanvas']>): Overlay => {
        // Not the brand accent: what is drawn ON the artwork has to read against whatever
        // is under it, and be recognisably the editor rather than part of the drawing.
        const accent = hexColor(paletteRef.current.selection);
        const at = (point: { x: number; y: number }) => ({
          x: (point.x - camera.x) * camera.zoom * dpr,
          y: (point.y - camera.y) * camera.zoom * dpr,
        });
        const stroke = (width: number, alpha = 1) => {
          selectionPaint.setStyle(ck.PaintStyle.Stroke);
          selectionPaint.setStrokeWidth(width * dpr);
          selectionPaint.setColor(ck.Color4f(accent.r, accent.g, accent.b, alpha));
          return selectionPaint;
        };

        return {
          picture: (assetId, transform, placed, frame, alpha) => {
            const image = images.get(assetId);
            if (!image) return;
            const k = camera.zoom * dpr;
            const m = multiplyMatrix([k, 0, 0, k, -camera.x * k, -camera.y * k], transform);
            c.save();
            c.concat([m[0], m[2], m[4], m[1], m[3], m[5], 0, 0, 1]);
            c.clipRect(ck.XYWHRect(frame.x, frame.y, frame.width, frame.height), ck.ClipOp.Difference, true);
            const ghost = new ck.Paint();
            ghost.setAlphaf(alpha);
            c.drawImageRectOptions(image, ck.XYWHRect(0, 0, image.width(), image.height()), ck.XYWHRect(placed.x, placed.y, placed.width, placed.height), ck.FilterMode.Linear, ck.MipmapMode.Nearest, ghost);
            ghost.delete();
            c.restore();
          },
          line: (from, to, options) => {
            const a = at(from);
            const b = at(to);
            c.drawLine(a.x, a.y, b.x, b.y, stroke(options?.width ?? 1, options?.alpha));
          },
          box: (rect, options) => {
            const a = at(rect);
            const b = at({ x: rect.x + rect.width, y: rect.y + rect.height });
            const shape = ck.LTRBRect(a.x, a.y, b.x, b.y);
            if (options?.fill) {
              selectionPaint.setStyle(ck.PaintStyle.Fill);
              selectionPaint.setColor(ck.Color4f(accent.r, accent.g, accent.b, options.alpha ?? 0.12));
              c.drawRect(shape, selectionPaint);
            }
            const paint = stroke(options?.width ?? 1);
            // Dashed where a box is not a selection. A slice looks exactly like a chosen
            // shape otherwise, which is most of why nobody could tell what one was: the
            // canvas was saying "this is selected" about a thing that is never selected,
            // it is a window.
            const dashes = options?.dashed ? ck.PathEffect.MakeDash([6 * dpr, 4 * dpr]) : null;
            if (dashes) paint.setPathEffect(dashes);
            c.drawRect(shape, paint);
            if (dashes) {
              paint.setPathEffect(null);
              dashes.delete();
            }
          },
          handle: (point, options) => {
            const p = at(point);
            // A 7 px square with a hairline round it, which is the weight a selection
            // frame is drawn at everywhere it is drawn well. It grew to 10 and carried a
            // 2 px white halo, and a dozen of those down a path read as beads rather than
            // as the quiet furniture they are.
            const half = (options?.size ?? (options?.hovered ? 4 : 3.5)) * dpr;
            const box = ck.LTRBRect(p.x - half, p.y - half, p.x + half, p.y + half);
            if (options?.filled) {
              handlePaint.setColor(ck.Color4f(accent.r, accent.g, accent.b, 1));
              c.drawRect(box, handlePaint);
              edgePaint.setStrokeWidth(dpr);
              c.drawRect(box, edgePaint);
            } else {
              c.drawRect(box, whitePaint);
              c.drawRect(box, stroke(1));
            }
          },
          knob: (point, options) => {
            const p = at(point);
            // White with an accent hairline, like the squares: a solid accent dot on a
            // stalk was the loudest thing on the canvas and it is the least used.
            const radius = (options?.hovered ? 4 : 3.5) * dpr;
            c.drawCircle(p.x, p.y, radius, whitePaint);
            selectionPaint.setStyle(ck.PaintStyle.Stroke);
            selectionPaint.setStrokeWidth(dpr);
            selectionPaint.setColor(ck.Color4f(accent.r, accent.g, accent.b, 1));
            c.drawCircle(p.x, p.y, radius, selectionPaint);
          },
          grip: (point, options) => {
            const p = at(point);
            // Solid, unlike the quiet squares: it is pulled to make more of something, and
            // has to be found to be used — an arrow says which way.
            const radius = (options.hovered ? 10 : 9) * dpr;
            selectionPaint.setStyle(ck.PaintStyle.Fill);
            selectionPaint.setColor(ck.Color4f(accent.r, accent.g, accent.b, 1));
            c.drawCircle(p.x, p.y, radius, selectionPaint);
            edgePaint.setStrokeWidth(dpr);
            c.drawCircle(p.x, p.y, radius, edgePaint);
            const length = Math.hypot(options.toward.x, options.toward.y) || 1;
            const ux = options.toward.x / length;
            const uy = options.toward.y / length;
            const half = 4.5 * dpr;
            const head = 3 * dpr;
            const tip = { x: p.x + ux * half, y: p.y + uy * half };
            const tail = { x: p.x - ux * half, y: p.y - uy * half };
            const cmds: number[] = [MOVE, tail.x, tail.y, LINE, tip.x, tip.y];
            // A chevron at an end, opening back along the shaft.
            const chevron = (end: { x: number; y: number }, sx: number, sy: number) => {
              cmds.push(MOVE, end.x - sx * head - sy * head, end.y - sy * head + sx * head);
              cmds.push(LINE, end.x, end.y);
              cmds.push(LINE, end.x - sx * head + sy * head, end.y - sy * head - sx * head);
            };
            chevron(tip, ux, uy);
            if (options.both) chevron(tail, -ux, -uy);
            const arrow = ck.Path.MakeFromCmds(cmds);
            if (!arrow) return;
            edgePaint.setStrokeWidth(1.5 * dpr);
            edgePaint.setStrokeCap(ck.StrokeCap.Round);
            edgePaint.setStrokeJoin(ck.StrokeJoin.Round);
            c.drawPath(arrow, edgePaint);
            edgePaint.setStrokeCap(ck.StrokeCap.Butt);
            edgePaint.setStrokeJoin(ck.StrokeJoin.Miter);
            arrow.delete();
          },
          /**
           * A closed region washed in, in its OWN shape.
           *
           * Everything else the overlay fills is a rectangle, and for most of what it
           * says that is enough. For a shape builder it is not: the whole question is
           * which REGION the hand is over, and answering it with the box round that
           * region says something that is nearly always false.
           *
           * `dotted` stipples it rather than flooding it, which is how Illustrator's
           * shape builder says the same thing — and it has to be a screen of dots rather
           * than a flat wash, because the thing underneath is artwork and a person needs
           * to keep seeing it while they decide.
           */
          region: (rings, options) => {
            const cmds: number[] = [];
            for (const ring of rings) {
              if (ring.length < 3) continue;
              const first = at(ring[0]!);
              cmds.push(MOVE, first.x, first.y);
              for (const point of ring.slice(1)) {
                const on = at(point);
                cmds.push(LINE, on.x, on.y);
              }
              cmds.push(CLOSE);
            }
            if (cmds.length === 0) return;
            const path = ck.Path.MakeFromCmds(cmds);
            if (!path) return;
            if (options?.dotted) {
              // The dots are anchored to the SURFACE, not to the document, so the screen
              // keeps still while the artwork moves under it: a mark on the glass in front
              // of the drawing rather than a texture painted onto it.
              const screen = stippleShader();
              if (screen) {
                stipplePaint.setShader(screen);
                stipplePaint.setAlphaf(options.alpha ?? 0.85);
                c.drawPath(path, stipplePaint);
              }
            } else {
              selectionPaint.setStyle(ck.PaintStyle.Fill);
              selectionPaint.setColor(ck.Color4f(accent.r, accent.g, accent.b, options?.alpha ?? 0.18));
              c.drawPath(path, selectionPaint);
            }
            if (options?.width) c.drawPath(path, stroke(options.width, options.edge ?? 1));
            path.delete();
          },
          label: (point, string, options) => {
            if (!text || string === '') return;
            const size = (options?.size ?? 11) * dpr;
            const shape = text.labelSize(string, { size, weight: 600 });
            if (shape.width <= 0) return;
            const quiet = options?.tone === 'quiet';
            const padX = quiet ? 0 : 5 * dpr;
            const padY = quiet ? 0 : 3 * dpr;
            const wide = shape.width + padX * 2;
            const tall = shape.height + padY * 2;
            const spot = at(point);
            const cx = spot.x + (options?.offset?.x ?? 0) * dpr;
            const cy = spot.y + (options?.offset?.y ?? 0) * dpr;
            const x = options?.align === 'center' ? cx - wide / 2 : options?.align === 'right' ? cx - wide : cx;
            const y = options?.baseline === 'middle' ? cy - tall / 2 : options?.baseline === 'bottom' ? cy - tall : cy;
            if (!quiet) {
              selectionPaint.setStyle(ck.PaintStyle.Fill);
              selectionPaint.setColor(ck.Color4f(accent.r, accent.g, accent.b, 1));
              c.drawRRect(ck.RRectXY(ck.LTRBRect(x, y, x + wide, y + tall), 3 * dpr, 3 * dpr), selectionPaint);
            }
            text.label(
              c,
              string,
              { x: x + padX, y: y + padY },
              { size, weight: 600, color: quiet ? [accent.r, accent.g, accent.b, 1] : [1, 1, 1, 1] },
            );
          },
          guide: (from, to) => {
            const a = at(from);
            const b = at(to);
            selectionPaint.setStyle(ck.PaintStyle.Stroke);
            selectionPaint.setStrokeWidth(1 * dpr);
            selectionPaint.setColor(ck.Color4f(1, 0.23, 0.42, 0.95));
            c.drawLine(a.x, a.y, b.x, b.y, selectionPaint);
          },
          curve: (points, options) => {
            if (points.length < 2) return;
            const cmds: number[] = [];
            points.forEach((point, index) => {
              const p = at(point.at);
              if (index === 0) {
                cmds.push(MOVE, p.x, p.y);
                return;
              }
              const previous = points[index - 1]!;
              if (previous.out || point.in) {
                const c1 = at(previous.out ?? previous.at);
                const c2 = at(point.in ?? point.at);
                cmds.push(CUBIC, c1.x, c1.y, c2.x, c2.y, p.x, p.y);
              } else {
                cmds.push(LINE, p.x, p.y);
              }
            });
            const path = ck.Path.MakeFromCmds(cmds);
            if (!path) return;
            c.drawPath(path, stroke(options?.width ?? 1));
            path.delete();
          },
        };
      };

      /** The artboards that play, asked of the engine once and again only when the answer could differ. */
      let moving: string[] | null = null;
      /** The board whose play mark is under the pointer, drawn in the full ink. */
      let markLit: string | null = null;
      /** The page under a drag of pictures or of something from Assets: where it would land. */
      let aimedBoard: string | null = null;
      const movingNow = () => (moving ??= movingBoards(scene));
      /** The board whose play mark is under a point in the window, if any. */
      const markAt = (clientX: number, clientY: number): string | null => {
        const boards = movingNow().flatMap((id) => {
          const box = renderer.bounds.worldGeometryBounds(id);
          return box ? [{ id, box }] : [];
        });
        if (boards.length === 0) return null;
        const frame = canvas.getBoundingClientRect();
        return markUnder(boards, camera, { x: clientX - frame.left, y: clientY - frame.top });
      };

      /** Each artboard's name as it was last drawn, in CSS pixels, and the one under the pointer. */
      let labels: LabelBox[] = [];
      let labelLit: string | null = null;
      /**
       * The board whose name is under a point on the canvas, in CSS pixels from its corner.
       * A locked board's name is drawn but takes no press: the board cannot be moved anyway,
       * and a press that chose it would only take the selection away from what was chosen.
       */
      const labelAtScreen = (at: { x: number; y: number }): string | null => {
        const id = labelUnder(labels, at);
        return id && scene.get(id)?.type === 'artboard' && !scene.lockedHere(id) ? id : null;
      };
      const labelAt = (clientX: number, clientY: number): string | null => {
        if (labels.length === 0) return null;
        const frame = canvas.getBoundingClientRect();
        return labelAtScreen({ x: clientX - frame.left, y: clientY - frame.top });
      };
      const labelAtWorld = (world: { x: number; y: number }): string | null =>
        labels.length === 0 ? null : labelAtScreen({ x: (world.x - camera.x) * camera.zoom, y: (world.y - camera.y) * camera.zoom });

      /**
       * The page the outline goes round. While something is dragged over the canvas, the one
       * it will land on. Otherwise the page being worked on — where Assets puts things and
       * what Export means — when there is more than one to tell apart, and not while that
       * page is itself chosen: its selection box already says so.
       */
      const outlinedBoard = (): string | null => {
        if (aimedBoard) return aimedBoard;
        let boards = 0;
        for (const id of scene.childrenOf(ROOT_ID)) if (scene.get(id)?.type === 'artboard' && ++boards > 1) break;
        if (boards < 2) return null;
        const working = exportBoard();
        return working && !selection.includes(working) ? working : null;
      };
      const drawOverlay = (surface: ReturnType<GlSurface['surfaceFor']>) => {
        const c = surface.getCanvas();
        const settings = view();
        const sheet = { camera, width: viewSize().width, height: viewSize().height, dpr, inset: inset() };
        const outlined = outlinedBoard();
        const outlinedBox = outlined ? renderer.bounds.worldGeometryBounds(outlined) : null;
        if (outlinedBox) furniture.drawWorkingBoard(c, sheet, outlinedBox);
        // Which boards are animated, under everything else the editor says: a chosen
        // board's handles sit over its mark, not the other way about.
        furniture.drawMovingMarks(c, sheet, movingNow().flatMap((id) => {
          const box = renderer.bounds.worldGeometryBounds(id);
          return box ? [{ box, lit: id === markLit }] : [];
        }));
        // Each board's name over its corner: what a press takes hold of the board by.
        const marked = new Set(movingNow());
        labels = text
          ? furniture.drawBoardLabels(c, sheet, text, labelledBoards(scene).flatMap((id) => {
              const box = renderer.bounds.worldGeometryBounds(id);
              const tone = selection.includes(id) ? 'chosen' as const : id === labelLit ? 'lit' as const : 'quiet' as const;
              return box ? [{ id, box, name: scene.get(id)?.name, tone, marked: marked.has(id) }] : [];
            }))
          : [];
        // The text caret and its selection wash are drawn on the artwork too, so they
        // take the same colour as everything else the editor puts there.
        const accent = hexColor(paletteRef.current.selection);
        const toScreen = (x: number, y: number) => ({ x: (x - camera.x) * camera.zoom * dpr, y: (y - camera.y) * camera.zoom * dpr });

        // Whatever the tool has to show: the selection, a band, a path being drawn.
        const groupId = groupEditing.current;
        const groupBox = groupId ? renderer.bounds.worldGeometryBounds(groupId) : null;
        if (groupId && groupBox) {
          const overlay = makeOverlay(c);
          overlay.box(groupBox, { dashed: true, alpha: 0.5 });
          overlay.label({ x: groupBox.x, y: groupBox.y }, `${scene.get(groupId)?.name || 'Group'} · Esc to exit`, {
            baseline: 'bottom', offset: { x: 0, y: -12 }, tone: 'quiet',
          });
        }
        current.draw?.(makeOverlay(c), context);

        // A gradient's handles, over the tool's marks: the line it runs along with its stops
        // on it (linear), the radii (radial), the turn (conic).
        const gradient = gradientInHand();
        if (gradient) {
          const overlay = makeOverlay(c);
          const at = (id: HandleId) => gradient.handles.find((handle) => handle.id === id)?.world;
          const centre = at('center');
          if (gradient.paint.type === 'linear') {
            const a = at('start')!;
            const b = at('end')!;
            overlay.line(a, b, { width: 1.5 });
            for (const stop of gradient.paint.stops) overlay.knob({ x: a.x + (b.x - a.x) * stop.offset, y: a.y + (b.y - a.y) * stop.offset });
          } else if (centre) {
            for (const id of ['rx', 'ry', 'turn'] as const) {
              const end = at(id);
              if (end) overlay.line(centre, end, { width: 1.5 });
            }
          }
          for (const handle of gradient.handles) overlay.handle(handle.world, { filled: true, size: 10, hovered: gradientHeld === handle.id });
        }

        /**
         * The motion path, while it is being drawn.
         *
         * Drawn here rather than by a tool because arming it is not picking a tool — the
         * select tool is still in hand, and it is the canvas that has taken the gesture.
         * Shown as it is recorded: a path you cannot see while you draw it is a path you
         * have to draw twice.
         */
        // A picture slot being filled: everything it can take, outlined; the one under the pointer, bolder.
        if (layerPick) {
          const overlay = makeOverlay(c);
          for (const id of layerPick.candidates) {
            const box = renderer.bounds.worldGeometryBounds(id);
            if (box) overlay.box(box, { width: layerPick.hovered === id ? 3 : 1.5, alpha: layerPick.hovered === id ? 1 : 0.8 });
          }
          if (layerPick.hovered) {
            const box = renderer.bounds.worldGeometryBounds(layerPick.hovered);
            if (box) overlay.box(box, { fill: true, alpha: 0.12 });
          }
        }
        if (drawing && drawing.points.length > 1) {
          const overlay = makeOverlay(c);
          const from = drawing.from;
          const at = (point: { x: number; y: number }) => ({ x: from.x + point.x, y: from.y + point.y });
          for (let i = 1; i < drawing.points.length; i++) {
            overlay.line(at(drawing.points[i - 1]!), at(drawing.points[i]!), { width: 2 });
          }
        }

        // Typing belongs to the editor rather than to a tool, because a text layer can
        // be opened from the select tool as well as made with the text one.
        const cursor = editing?.current;
        if (cursor) {
          const node = scene.get(cursor.id);
          const world = scene.worldTransform(cursor.id);
          if (node) {
            const place = (x: number, y: number) => {
              const point = applyToPoint(world, x, y);
              return toScreen(point.x, point.y);
            };
            /**
             * The wash is an opaque band and the characters on it are redrawn in white.
             *
             * Not a blend that inverts what is under it: Difference reads the destination,
             * and on this surface Skia quietly falls back to drawing the band flat — a
             * solid rectangle with the text nowhere to be seen, which is what it did.
             * Drawing the selected characters again, in white, clipped to the band, owes
             * nothing to what the GPU is willing to read back.
             */
            const rects = editing!.selectionRects();
            if (rects.length > 0) {
              // Node coordinates to surface pixels, so the band can be clipped and the
              // text drawn in the same frame the rectangles were measured in.
              const z = camera.zoom * dpr;
              const local = [world[0]! * z, world[2]! * z, (world[4]! - camera.x) * z, world[1]! * z, world[3]! * z, (world[5]! - camera.y) * z, 0, 0, 1];
              selectionPaint.setColor(ck.Color4f(0.7, 0.7, 0.7, 1));
              selectionPaint.setStyle(ck.PaintStyle.Fill);
              // Through the text's whole transform, like the white redraw below. The band
              // was an upright box between two turned corners, so on a rotated text it sat
              // in the wrong place and the white letters landed on bare page
              // (QA 2026-09-24, text-F26).
              c.save();
              c.concat(local);
              for (const rect of rects) c.drawRect(ck.LTRBRect(rect.x, rect.y, rect.x + rect.width, rect.y + rect.height), selectionPaint);
              c.restore();
              if (text && node.type === 'text') {
                // The same box, painted white. The paragraph cache keys on the style, so
                // this is one extra laid-out paragraph per box being edited, not per frame.
                const white = { type: 'solid' as const, color: { r: 1, g: 1, b: 1, a: 1 }, visible: true, opacity: 1, blendMode: 'normal' as const };
                const lit = { ...node, style: { ...node.style, fills: [white] } };
                for (const rect of rects) {
                  c.save();
                  c.concat(local);
                  c.clipRect(ck.LTRBRect(rect.x, rect.y, rect.x + rect.width, rect.y + rect.height), ck.ClipOp.Intersect, false);
                  text.draw(c, lit);
                  c.restore();
                }
              }
            }
            selectionPaint.setStyle(ck.PaintStyle.Stroke);
            const caret = editing!.caret();
            if (caret && caretOn) {
              const a = place(caret.from.x, caret.from.y);
              const b = place(caret.to.x, caret.to.y);
              selectionPaint.setColor(ck.Color4f(accent.r, accent.g, accent.b, 1));
              selectionPaint.setStrokeWidth(Math.max(1, 1.5 * dpr));
              c.drawLine(a.x, a.y, b.x, b.y, selectionPaint);
            }
          }
        }

        // What other people have chosen, outlined in their own colour. Their pointers are not
        // drawn here: they are the collab kit's, written to the DOM above the canvas.
        for (const one of remoteChosen) {
          const colour = hexColor(one.color);
          selectionPaint.setStyle(ck.PaintStyle.Stroke);
          selectionPaint.setStrokeWidth(2 * dpr);
          selectionPaint.setColor(ck.Color4f(colour.r, colour.g, colour.b, 1));
          for (const id of one.ids) {
            const box = scene.has(id) ? renderer.bounds.worldGeometryBounds(id) : null;
            if (!box) continue;
            const a = toScreen(box.x, box.y);
            const b = toScreen(box.x + box.width, box.y + box.height);
            // Just outside the layer, so nothing of it is covered.
            const out = 1.5 * dpr;
            c.drawRect(ck.LTRBRect(a.x - out, a.y - out, b.x + out, b.y + out), selectionPaint);
          }
        }

        /**
         * The guides, and then the rulers over them.
         *
         * Last of everything, and in that order. A guide is in front of the artwork and
         * of the handles, because it is a line you aim at and a handle drawn over it
         * would break it exactly where the aiming happens; and a ruler is in front of the
         * guide, because a ruler is the edge of the window and nothing on the page is in
         * front of the window frame.
         */
        // The lines explaining a point that landed on something. Drawn here rather than
        // by each tool for the same reason the deciding is done in one place: a tool that
        // drew its own would be a tool that could forget to.
        if (litLines.length > 0) {
          const marks = makeOverlay(c);
          for (const line of litLines) {
            if (line.axis === 'x') marks.guide({ x: line.at, y: line.from }, { x: line.at, y: line.to });
            else marks.guide({ x: line.from, y: line.at }, { x: line.to, y: line.at });
          }
        }

        if (settings.guides) {
          furniture.drawGuides(c, sheet, guides, { locked: settings.lockGuides, held: holding?.id ?? null, lit: litGuides });
          /**
           * Where the guide being dragged actually is, said as a number.
           *
           * A guide is nothing but a position, so while one is in hand the position IS
           * the gesture — and without this the only way to find out where you had put it
           * was to read it off the ruler by eye, which is the thing rulers are bad at.
           * Said beside the hand rather than at the end of the line, because that is
           * where a person is looking.
           */
          if (holding?.id && pointerAt) {
            const unit = options.unitsRef?.current ?? 'px';
            const across = holding.axis === 'x';
            const overlay = makeOverlay(c);
            const beside = across ? { x: holding.at, y: pointerAt.y } : { x: pointerAt.x, y: holding.at };
            // Above the line the gap is drawn on, with the gap below it: the two are
            // about the same guide and would otherwise land on top of each other
            // exactly when the gap is small, which is when both are worth reading.
            overlay.label(beside, formatLength(holding.at, unit, { compact: true }), {
              align: across ? 'left' : 'center',
              baseline: 'bottom',
              offset: across ? { x: 8, y: -12 } : { x: 0, y: -10 },
            });
            /**
             * And how far it is from the guide next to it.
             *
             * Where a guide IS matters less than what it divides. Somebody setting out
             * columns is not thinking "put this at 570", they are thinking "make this
             * gap the same as that one" — so the gap is drawn and said, the same way the
             * gaps between shapes are when the numbers are asked for.
             */
            const neighbour = guides
              .filter((guide) => guide.axis === holding!.axis && guide.id !== holding!.id)
              .reduce<Guide | null>((best, guide) => {
                const away = Math.abs(guide.at - holding!.at);
                return !best || away < Math.abs(best.at - holding!.at) ? guide : best;
              }, null);
            if (neighbour) {
              const other = across ? { x: neighbour.at, y: pointerAt.y } : { x: pointerAt.x, y: neighbour.at };
              overlay.line(beside, other, { width: 1 });
              overlay.label(
                { x: (beside.x + other.x) / 2, y: (beside.y + other.y) / 2 },
                formatLength(Math.abs(neighbour.at - holding.at), unit, { compact: true }),
                { align: 'center', baseline: across ? 'top' : 'middle', offset: across ? { x: 0, y: 8 } : { x: 12, y: 0 } },
              );
            }
          }
        }
        if (settings.rulers) {
          const selectedBounds = selection.reduce<Rect | null>((bounds, id) => {
            const box = renderer.bounds.worldGeometryBounds(id);
            return box ? union(bounds, box) : bounds;
          }, null);
          furniture.drawRulers(c, sheet, { unit: options.unitsRef?.current ?? 'px', pointer: pointerAt, text, selection: selectedBounds });
        }
        surface.flush();
      };

      /** The size, in CSS pixels, the canvas was last drawn at. */
      let drawnSize = { width: 0, height: 0 };
      const drawFrame = () => {
        // The GPU has gone. `gl.onRestored` asks for a frame when it is back.
        if (gl.lost) return;
        // The tool in hand, as soon as it is in hand. The canvas used to wait for the
        // next PRESS to adopt one, and everything that reads `current` then showed the
        // tool before this one — the overlay worst of all, because it has no way to ask
        // for the chosen tool and so went on drawing the previous tool's nothing. Pick up
        // the shape builder and it looked switched off until you had already pressed once
        // with it, which is exactly the moment a person decides a tool is broken.
        //
        // Except mid-press: a tool key pressed during a drag swapped tools under the hand,
        // and the drag froze where it was, never settled into an artboard, and joined the
        // next action's undo step (QA 2026-09-24, undo-persist-collab-F20). The new tool
        // is picked up on the first frame after the release instead.
        if (!toolPressed) useTool(options.toolRef.current);
        // And the same for everything the chrome says by ref rather than by event.
        const settings = view();
        const chrome = inset();
        const unit = options.unitsRef?.current ?? 'px';
        if (settings !== shown.view || chrome.top !== shown.top || chrome.left !== shown.left || chrome.right !== shown.right || chrome.bottom !== shown.bottom || unit !== shown.unit) {
          shown = { view: settings, top: chrome.top, left: chrome.left, right: chrome.right, bottom: chrome.bottom, unit };
          needsFrame = true;
        }
        const density = window.devicePixelRatio || 1;
        if (density !== dpr) {
          dpr = density;
          needsFrame = true;
        }
        const { width, height } = viewSize();
        // The window was resized: the canvas is stretched to its new size by the browser
        // until it is drawn again at that size, so it is drawn now, every frame of the drag —
        // it waited for the next edit or move, and the page stretched and squeezed until the
        // hand let go (QA 2026-10-05, ART-CAN-23).
        if (width !== drawnSize.width || height !== drawnSize.height) {
          drawnSize = { width, height };
          needsFrame = true;
          if (playing) playing.drawn = false;
        }
        // A move being tried is shown over a page that is held still, and the holding is
        // there again when the trying ends; it never interrupts a page that is playing.
        if (trying && (!playing || playing.held !== undefined)) {
          const seconds = ((performance.now() - trying.from) / 1000) % (trying.length + TRY_REST);
          const surface = gl.surfaceFor(width, height, dpr);
          // Lumens shaders stand where the page is held while a move is tried on it.
          renderer.setClock(playing?.held ?? 0, playing?.id);
          renderer.drawLive(surface, camera, { width, height, dpr }, trying.at(Math.min(trying.length, seconds)));
          if (playing) playing.drawn = false;
          return;
        }
        if (playing) {
          const held = playing.held;
          const seconds = held ?? (performance.now() - playing.from) / 1000;
          // A page playing runs its clips' videos with it; held on an instant, they seek there.
          images.setFlowing(held === undefined);
          // A board deleted while it played took its Stop button with it, and the preview
          // ran on over nothing with every handle hidden (anim-F7).
          if (scene.get(playing.id)?.type !== 'artboard') stopPlaying();
          else if (held === undefined && seconds >= playing.length) {
            // Stop on the last frame rather than one past it, so what you are left
            // looking at is the page as it is stored — not a frame 3 ms into nothing.
            stopPlaying(true);
          } else if (held !== undefined && playing.drawn && !needsFrame) {
            // Held on one instant and already drawn: nothing to do until it or the page moves.
            return;
          } else {
            playing.drawn = true;
            needsFrame = false;
            const surface = gl.surfaceFor(width, height, dpr);
            // Lumens shaders run on the page's clock: playing, paused or dragged along.
            renderer.setClock(seconds, playing.id);
            // No overlay while it plays: selection handles and snap lines are the editor
            // talking, and this is the page talking. Nobody wants their own cursor in
            // the preview.
            renderer.drawLive(surface, camera, { width, height, dpr }, poseArtboard(scene, playing.id, seconds));
            return;
          }
        }
        images.setFlowing(false);
        // Edited, not played: Lumens shaders stand still at the start of the page.
        renderer.setClock(0);
        const live = sketchesOnStage();
        if (needsFrame) {
          // Zoomed in past what a picture was loaded for: its sharper copy is fetched.
          images.setScale(camera.zoom * dpr);
          images.setView({ x: camera.x, y: camera.y, width: width / camera.zoom, height: height / camera.zoom });
          const surface = gl.surfaceFor(width, height, dpr);
          const stats = renderer.draw(surface, camera, { width, height, dpr }, live);
          drawOverlay(surface);
          needsFrame = stats.pendingTiles > 0;
          // Development only, opt-in: a bench sets `__artworkFrames = []` to read what each frame drew.
          if (import.meta.env.DEV) (window as { __artworkFrames?: unknown[] }).__artworkFrames?.push(stats);
        }
      };
      /**
       * Sketches while the page is edited: they stand at their picture at rest, as every Lumens
       * shader does, and play only when the page is played (Tarek, 2 Nov: "still autoplaying …
       * without me pressing the play button"). Playing draws them through the page's poses, at
       * the playhead. Here the runner is only told which ones are gone.
       */
      const sketchesOnStage = (): LiveWindows | undefined => {
        const now = performance.now();
        if (now - sketchesSwept > 5_000) {
          sketchesSwept = now;
          images.sketches.sweep((id) => !!scene.get(id));
        }
        return undefined;
      };
      /**
       * One frame, and always the next one. A throw anywhere in a frame — the GL surface
       * failing to come back after the graphics card reset, say — used to skip the line
       * that asks for the next frame, and the canvas froze for good while every click
       * went on editing a file nobody could see (QA 2026-09-24, undo-persist-collab-F6).
       * Now the error is reported (once a second at most) and the next frame tries again.
       */
      let lastFrameError = 0;
      const frame = () => {
        try {
          drawFrame();
        } catch (err) {
          needsFrame = true;
          const now = performance.now();
          if (now - lastFrameError > 1000) {
            lastFrameError = now;
            console.error('Drawing a frame failed; trying again next frame.', err);
          }
        } finally {
          raf = requestAnimationFrame(frame);
        }
      };
      raf = requestAnimationFrame(frame);
      cleanups.push(() => cancelAnimationFrame(raf));
      cleanups.push(() => { playing = null; trying = null; resting = null; });
      const unsubscribe = scene.subscribe((change) => {
        needsFrame = true;
        if (mayChangeMoving(change)) moving = null;
        // A layer that is gone is not chosen any more. Undo (or somebody else) removing a
        // chosen layer left its id in the selection, so Delete then deleted nothing and
        // ⌘G failed on the missing id (QA 2026-09-24, sel-F11).
        if (selection.some((id) => !scene.has(id))) setSelection(selection.filter((id) => scene.has(id)));
        if (fittedToNothing && scene.childrenOf(ROOT_ID).length > 0) {
          // Whoever filled it, it is not empty any more: this is a one-time fit.
          fittedToNothing = false;
          if (!change.local && camera === openingCamera) setCamera(fitBox(contentBounds()));
        }
      });
      cleanups.push(unsubscribe);
      // Our own native cursor is not painted into the canvas. Local pointer
      // broadcasts must not repaint the scene and invalidate every glass backdrop.
      // Other people's selections are drawn here; a pointer moving is the DOM's, and repaints nothing.
      cleanups.push(
        pageRoom.on('presence', ({ peers }) => {
          const next = remoteSelections(peers, me.id, peerColor);
          const key = selectionKey(next);
          if (key === remoteKey) return;
          remoteKey = key;
          remoteChosen = next;
          needsFrame = true;
        }),
      );

      // ── the tools ────────────────────────────────────────────────────────
      /**
       * What a press lands on: the topmost layer that DRAWS there, not the first box that
       * holds the point. Boxes are only candidates — the empty corner of a circle or the gap
       * between a group's children used to be clickable (QA 2026-09-24, sel-F4). An
       * artboard drawn as a single layer (it has a mask, an effect, a blend mode or less
       * than full opacity) keeps its children out of the index, so it is looked inside
       * rather than given up on (sel-F7).
       */
      const HIT_SLOP = 4;
      const hitTest = (world: { x: number; y: number }): string | null => {
        const slop = touchReach(HIT_SLOP) / camera.zoom;
        // Inside a group, its own layers first; a press on something outside it still
        // takes that something, rather than nothing.
        if (groupEditing.current) {
          const inside = groupEditing.hit(world, slop);
          if (inside) return inside;
        }
        // An effector has a box so it can be moved and drawn, but it paints nothing — so a
        // press falls straight through it. Export slices likewise.
        const takeable = (id: string) => {
          const node = scene.get(id);
          return !!node && node.type !== 'effector' && node.type !== 'slice' && !scene.lockedHere(id) && !scene.hiddenHere(id);
        };
        // The candidates are everything within the hand's tolerance, as `drawsAt` measures
        // it: asked for the point alone, a hairline was a candidate only for a press exactly
        // on it, and lines and thin outlines were all but impossible to click (Farah, 23 Oct).
        const near = { x: world.x - slop, y: world.y - slop, width: slop * 2, height: slop * 2 };
        const inside = (board: string): string | null => {
          const kids = scene.childrenOf(board);
          for (let i = kids.length - 1; i >= 0; i--) {
            const kid = kids[i]!;
            const box = renderer.bounds.worldVisualBounds(kid);
            if (box && intersects(box, near) && takeable(kid) && drawsAt(scene, kid, world, slop)) return kid;
          }
          return null;
        };
        for (const id of renderer.unitsAt(world.x, world.y, slop)) {
          const node = scene.get(id);
          if (!node) continue;
          if (node.type === 'artboard') {
            const found = inside(id);
            if (found) return found;
            continue;
          }
          if (takeable(id) && drawsAt(scene, id, world, slop)) return id;
        }
        return null;
      };

      /**
       * Every layer that draws under a point, at any depth, the topmost first: what a
       * ⌘-click reaches, and what "Select layer" offers. Groups and artboards are looked
       * inside rather than offered — they are what a plain click is for.
       */
      const layersUnder = (world: { x: number; y: number }, limit = Number.POSITIVE_INFINITY, withLocked = false): string[] => {
        const slop = touchReach(HIT_SLOP) / camera.zoom;
        const found: string[] = [];
        // The index can hold a group as a unit of its own and inside its artboard as well.
        const seen = new Set<string>();
        const open = (id: string) => {
          const node = scene.get(id);
          return node?.type === 'group' || node?.type === 'artboard';
        };
        const visit = (id: string) => {
          if (found.length >= limit || seen.has(id)) return;
          seen.add(id);
          const node = scene.get(id);
          if (!node || node.type === 'effector' || node.type === 'slice' || scene.hiddenHere(id) || (!withLocked && scene.lockedHere(id))) return;
          if (open(id)) {
            const kids = scene.childrenOf(id);
            for (let i = kids.length - 1; i >= 0; i--) visit(kids[i]!);
            return;
          }
          const box = renderer.bounds.worldVisualBounds(id);
          if (box && intersects(box, { x: world.x - slop, y: world.y - slop, width: slop * 2, height: slop * 2 }) && drawsAt(scene, id, world, slop)) found.push(id);
        };
        for (const id of renderer.unitsAt(world.x, world.y, slop)) {
          if (found.length >= limit) break;
          visit(id);
        }
        return found;
      };
      /** The deepest layer drawn on top at a point — a ⌘-click — or what a click would take. */
      const deepHitTest = (world: { x: number; y: number }): string | null => layersUnder(world, 1)[0] ?? hitTest(world);

      /**
       * A shape-builder session: the regions a set of shapes divides the page into, one
       * at a time.
       *
       * The whole arrangement is never worked out. It cannot be: with a hundred shapes
       * there are hundreds of regions and building them all is hundreds of path
       * operations before the pointer has even moved — and almost all of them are regions
       * nobody will ever point at.
       *
       * What a region IS, though, is cheap to say: it is the set of shapes covering a
       * place. Two points covered by the same shapes are in the same region, and that set
       * is the region's name. So hovering asks every shape whether it holds the point —
       * which is a fast test, not path arithmetic — and only then works out that one
       * region's outline, once, and keeps it. A hundred shapes cost a hundred cheap
       * questions per move and two path operations per region actually visited.
       *
       * Committing is the same economy. The regions taken are unioned into one shape, and
       * every original has that shape subtracted from it. Nothing is shattered into its
       * pieces: a shape nobody pointed at comes out of this as itself, with its width and
       * its height and its kind, which is what it still is.
       */
      const building = (() => {
        type Held = {
          id: string;
          path: NonNullable<ReturnType<typeof ck.Path.MakeFromCmds>>;
          box: number[];
          /** The outline, walked once: what is drawn, and what "covers this point" is asked of. */
          rings: { x: number; y: number }[][];
        };
        let shapes: Held[] = [];
        /** Each PART of a region, by `<covering shapes>\u0001<part>`. */
        const regions = new Map<string, { path: NonNullable<ReturnType<typeof ck.Path.MakeFromCmds>>; rings: { x: number; y: number }[][] }>();
        /** The part keys of each region worked out so far, by the shapes covering it. */
        const partsBySet = new Map<string, string[]>();

        const forget = () => {
          for (const shape of shapes) shape.path.delete();
          shapes = [];
          for (const region of regions.values()) region.path.delete();
          regions.clear();
          partsBySet.clear();
        };



        /**
         * Everything a shape wears that a path can wear too — not just its paint. The
         * builder used to carry five fields, so every shape it touched lost its shadow,
         * blur, blend mode, hidden state, mask role and animation. The outline is rebuilt
         * in world space, so the transform is the one thing not carried.
         */
        const dress = (id: string) => {
          const node = scene.get(id) as (NodeRecord & Record<string, unknown>) | undefined;
          if (!node) return null;
          const worn: Record<string, unknown> = {};
          for (const key of ['name', 'visible', 'locked', 'opacity', 'isMask', 'animation', 'timing', 'fills', 'strokes', 'strokeStyle', 'effects', 'blendMode']) {
            if (node[key] !== undefined) worn[key] = node[key];
          }
          return worn;
        };
        const parentOf = (id: string) => scene.parentOf(id) ?? ROOT_ID;
        const asNode = (ref: string, subpaths: Subpath[], from: string, position?: { before: string }) => {
          const first = subpaths[0]?.points[0];
          const worn = dress(from);
          if (!first || !worn) return null;
          const parent = parentOf(from);
          // Through the parent's whole transform: a rotated or flipped group put the
          // result in the wrong place when only its translation was taken off.
          const placed = placeInParent(subpaths, parent === ROOT_ID ? null : scene.worldTransform(parent));
          return {
            op: 'create',
            ref,
            parentId: parent,
            ...(position ? { position } : {}),
            node: {
              type: 'path',
              ...worn,
              name: (worn.name as string | undefined) || 'Shape',
              transform: placed.transform,
              path: { subpaths: placed.subpaths },
            },
          };
        };

        return {
          open: (ids: readonly string[]) => {
            forget();
            // In paint order, so "the topmost shape covering this" is simply the last one.
            const order = scene.paintOrder();
            for (const id of [...ids].sort((a, b) => order.indexOf(a) - order.indexOf(b))) {
              const node = scene.get(id);
              // Only filled, closed shapes are material to build with. A line has no area,
              // so any merge deleted it; an open stroke came back closed (a V gained a
              // third side); and a picture, text, group or artboard came back as one bare
              // shape with its contents gone.
              if (!node || !pathConversion(scene, id).ok || scene.lockedHere(id) || scene.hiddenHere(id) || node.type === 'line') continue;
              if (node.type === 'path' && node.path?.subpaths?.some((sub) => !sub.closed)) continue;
              const local = nodeCommands(node);
              if (!local) continue;
              const path = ck.Path.MakeFromCmds([...transformCommands(local, scene.worldTransform(id))]);
              if (!path) continue;
              if (node.type === 'path' && node.path?.fillRule === 'evenodd') path.setFillType(ck.FillType.EvenOdd);
              shapes.push({ id, path, box: [...path.getBounds()], rings: ringsOf(path) });
            }
            return shapes.map((shape) => shape.id);
          },

          outlines: () => shapes.map((shape) => ({ id: shape.id, rings: shape.rings })),

          at: (world: { x: number; y: number }) => {
            // Which shapes cover this place, asked of the outlines that are drawn rather
            // than of the path library. One rule for "inside", tested, and the same one
            // the highlight is drawn from — a hit test that could disagree with the
            // outline on screen is a tool that highlights the wrong part.
            const covering = shapes.filter((shape) => holdsRings(shape.rings, world));
            if (covering.length === 0) return null;
            const owners = covering.map((shape) => shape.id);
            const setKey = owners.join('\u0000');
            /** The part of the region under this place: the one the hand is actually over. */
            const partAt = (keys: readonly string[]) => {
              const key = keys.find((one) => holdsRings(regions.get(one)!.rings, world)) ?? keys[0];
              return key ? { key, rings: regions.get(key)!.rings, owners } : null;
            };
            const known = partsBySet.get(setKey);
            if (known) return partAt(known);

            let region = covering[0]!.path.copy();
            const cut = (other: Held, op: (typeof ck.PathOp)['Intersect']) => {
              const next = ck.Path.MakeFromOp(region, other.path, op);
              region.delete();
              region = next ?? new ck.Path();
            };
            for (const shape of covering.slice(1)) cut(shape, ck.PathOp.Intersect);
            const box = region.getBounds();
            for (const shape of shapes) {
              if (covering.includes(shape)) continue;
              // Only the ones that could possibly reach it: a box test here saves a path
              // operation, and a box that is wrong only costs an operation that does
              // nothing.
              if (shape.box[0]! > box[2]! || shape.box[2]! < box[0]! || shape.box[1]! > box[3]! || shape.box[3]! < box[1]!) continue;
              cut(shape, ck.PathOp.Difference);
            }
            if (ringsOf(region).length === 0) {
              region.delete();
              return null;
            }
            // One piece per connected part (shape-builder-F8).
            const keys: string[] = [];
            for (const part of partsOfRegion(ck, region)) {
              const rings = ringsOf(part);
              if (rings.length === 0) {
                part.delete();
                continue;
              }
              const key = `${setKey}\u0001${keys.length}`;
              regions.set(key, { path: part, rings });
              keys.push(key);
            }
            region.delete();
            partsBySet.set(setKey, keys);
            return partAt(keys);
          },

          commit: (keys: readonly string[], how: 'merge' | 'erase') => {
            const chosen = keys.map((key) => regions.get(key)?.path).filter(Boolean);
            if (chosen.length === 0 || shapes.length === 0) return { built: [], playing: [], outline: [] };
            const merged = unite(ck, chosen.map((piece) => piece!));

            const ops: unknown[] = [];
            // The new shape wears what the topmost shape under the first region wore.
            if (how === 'merge') {
              const owners = (keys[0] ?? '').split('\u0001')[0]!.split('\u0000').filter(Boolean);
              const from = owners[owners.length - 1] ?? shapes[shapes.length - 1]!.id;
              // In the stack where the shape it wears the look of was, not on top of everything.
              const made = asNode('built', subpathsOf(merged), from, { before: from });
              if (made) ops.push(made);
            }

            // And every original loses exactly the ground that was taken. What survives
            // stays in play: a shape builder is used in a sequence of strokes, and a tool
            // that forgot everything but its own answer after each one could only ever be
            // used once.
            const kept: { id: string; ref?: string }[] = [];
            const mergedBox = merged.getBounds();
            for (const shape of shapes) {
              // A shape the taken area does not reach is not changed at all. Every shape in
              // play used to be rewritten — with nothing selected, the whole page turned
              // into paths and jumped to the front for one merge in a corner.
              const b = shape.box;
              const apart = b[0]! > mergedBox[2]! || b[2]! < mergedBox[0]! || b[1]! > mergedBox[3]! || b[3]! < mergedBox[1]!;
              const overlap = apart ? null : ck.Path.MakeFromOp(shape.path, merged, ck.PathOp.Intersect);
              const touched = !!overlap && !overlap.isEmpty() && ringsOf(overlap).length > 0;
              overlap?.delete();
              if (!touched) {
                kept.push({ id: shape.id });
                continue;
              }
              const left = ck.Path.MakeFromOp(shape.path, merged, ck.PathOp.Difference);
              const subpaths = left ? subpathsOf(left) : [];
              left?.delete();
              if (subpaths.length === 0) {
                ops.push({ op: 'delete', id: shape.id });
                continue;
              }
              const node = scene.get(shape.id);
              if (node?.type === 'path') {
                kept.push({ id: shape.id });
                const parent = parentOf(shape.id);
                const placed = placeInParent(subpaths, parent === ROOT_ID ? null : scene.worldTransform(parent));
                ops.push({
                  op: 'set',
                  id: shape.id,
                  props: { path: { subpaths: placed.subpaths }, transform: placed.transform },
                });
                continue;
              }
              // Not a path, so it cannot keep being what it was with a bite out of it.
              const ref = `left${kept.length}`;
              const made = asNode(ref, subpaths, shape.id, { before: shape.id });
              if (made) {
                kept.push({ id: shape.id, ref });
                ops.push(made, { op: 'delete', id: shape.id });
              }
            }
            // The outline of what it BECAME, taken before the pieces are let go: the
            // regions that went into it each have their own edge, and drawing those
            // separately leaves the seam between them showing on a shape that no longer
            // has one.
            const outline = ringsOf(merged);
            merged.delete();
            const result = edit(ops);
            if (!result.ok) {
              console.warn('Building failed', result.errors);
              return { built: [], playing: [], outline: [] };
            }
            const built = result.created.built ? [result.created.built] : [];
            const survivors = kept.map((one) => (one.ref ? result.created[one.ref] : one.id)).filter((id): id is string => !!id);
            forget();
            return { built, playing: [...survivors, ...built], outline };
          },

          /**
           * Every piece the shapes in play make, worked out all at once.
           *
           * `at` never does this, for the reason at the top of this session: most pieces are
           * never pointed at. Split and Keep Overlaps are the two answers that need every
           * one, and they are asked for once, by a button, not on every movement of the hand.
           * Each shape, bottom to top, cuts every piece so far in two — the part under it and
           * the part not — and adds what of itself nothing below covered.
           */
          pieces: () =>
            arrangement(
              ck,
              shapes.map((shape) => shape.path),
            ).map((piece) => ({ owners: piece.owners.map((i) => shapes[i]!), path: piece.path })),

          /**
           * Every piece becomes a shape of its own, wearing what showed there — or, with
           * `overlapsOnly`, only the pieces two or more shapes shared, and the rest goes.
           *
           * One shape per PART, not per piece: a bar across a circle leaves the circle's two
           * halves as one piece (the same shapes cover both), and nobody looking at them
           * sees one thing.
           */
          split: (overlapsOnly: boolean) => {
            if (shapes.length === 0) return { built: [], playing: [], outline: [] };
            const pieces = building.pieces();
            // A shape that overlaps nothing is left exactly as it is: splitting it would only
            // turn a rectangle into a path, and Keep Overlaps would delete it — both of them
            // surprises when nothing is chosen and the whole page is in play.
            const involved = new Set(pieces.filter((piece) => piece.owners.length > 1).flatMap((piece) => piece.owners));
            const ops: unknown[] = [];
            const refs: string[] = [];
            for (const piece of pieces) {
              const alone = piece.owners.length === 1 && !involved.has(piece.owners[0]!);
              if (!alone && (!overlapsOnly || piece.owners.length > 1)) {
                const subs = subpathsOf(piece.path);
                const flat = subs.map((sub) => flattened(sub, 10));
                const top = piece.owners[piece.owners.length - 1]!;
                for (const part of partsOf(flat)) {
                  const ref = `piece${refs.length}`;
                  const made = asNode(ref, part.map((i) => subs[i]!), top.id, { before: top.id });
                  if (made) {
                    ops.push(made);
                    refs.push(ref);
                  }
                }
              }
              piece.path.delete();
            }
            for (const shape of shapes) if (involved.has(shape)) ops.push({ op: 'delete', id: shape.id });
            const untouched = shapes.filter((shape) => !involved.has(shape)).map((shape) => shape.id);
            if (ops.length === 0) return { built: [], playing: untouched, outline: [] };
            const result = edit(ops);
            if (!result.ok) {
              console.warn('Splitting failed', result.errors);
              return { built: [], playing: [], outline: [] };
            }
            const made = refs.map((ref) => result.created[ref]).filter((id): id is string => !!id);
            forget();
            return { built: made, playing: [...made, ...untouched], outline: [] };
          },

          /**
           * Touching shapes that wear the same paint become one.
           *
           * The way to build twenty shapes into a handful: colour them the way the answer
           * should look, then one press. "Touching" includes sharing an edge and nothing
           * more — which is exactly what the pieces Split leaves behind do — so it is asked
           * of the union (do two become fewer parts?) rather than of the overlap, which is
           * empty for two pieces that only meet.
           */
          mergeByColour: () => {
            if (shapes.length < 2) return { built: [], playing: shapes.map((shape) => shape.id), outline: [] };
            const paintOf = (id: string) => {
              const node = scene.get(id) as (NodeRecord & { fills?: unknown; strokes?: unknown }) | undefined;
              return JSON.stringify([node?.fills ?? [], node?.strokes ?? [], node?.opacity ?? 1]);
            };
            const groups = touchingGroups(
              ck,
              shapes.map((shape) => shape.path),
              shapes.map((shape) => paintOf(shape.id)),
            ).map((group) => group.map((i) => shapes[i]!));
            const ops: unknown[] = [];
            const keep: { id: string; ref?: string }[] = [];
            let made = 0;
            for (const group of groups) {
              if (group.length < 2) {
                keep.push({ id: group[0]!.id });
                continue;
              }
              const whole = unite(ck, group.map((shape) => shape.path));
              // The topmost of them is the one that stays, in its place in the stack.
              const top = group[group.length - 1]!;
              const ref = `merged${made++}`;
              const node = asNode(ref, subpathsOf(whole), top.id, { before: top.id });
              whole.delete();
              if (!node) continue;
              ops.push(node);
              keep.push({ id: top.id, ref });
              for (const shape of group) ops.push({ op: 'delete', id: shape.id });
            }
            if (ops.length === 0) return { built: [], playing: shapes.map((shape) => shape.id), outline: [] };
            const result = edit(ops);
            if (!result.ok) {
              console.warn('Merging by colour failed', result.errors);
              return { built: [], playing: [], outline: [] };
            }
            const built = keep.filter((one) => one.ref).map((one) => result.created[one.ref!]).filter((id): id is string => !!id);
            const playing = keep.map((one) => (one.ref ? result.created[one.ref] : one.id)).filter((id): id is string => !!id);
            forget();
            return { built, playing, outline: [] };
          },

          close: () => forget(),
        };
      })();

      /** What every tool is allowed to do, and nothing more. */
      const context: ToolContext = {
        page,
        scene,
        get selection() {
          return selection;
        },
        get style() {
          return options.styleRef.current;
        },
        get unitsPerPixel() {
          return 1 / camera.zoom;
        },
        get modifiers() {
          return modifiers;
        },
        get units() {
          return options.unitsRef?.current ?? 'px';
        },
        get clicks() {
          return clicks;
        },
        edit: (ops) => edit(ops),
        setSelection: (ids) => setSelection(ids),
        stopCapturing: () => undo.stopCapturing(),
        notice: (message: string) => options.onNotice?.(message),
        finish: () => options.onToolDone(),
        refresh: () => {
          needsFrame = true;
        },
        hit: (world) => hitTest(world),
        deepHit: (world) => deepHitTest(world),
        boardLabelAt: (world) => labelAtWorld(world),
        // An engine without `lift` (older than 2026-11-02) simply draws the drag through its tiles.
        liftBoards: (ids) => {
          (renderer as { lift?: (boards: readonly string[] | null) => void }).lift?.(ids);
          needsFrame = true;
        },
        get selectionScope() { return groupEditing.current; },
        enterGroup: (id, world) => {
          if (!groupEditing.enter(id)) return false;
          options.onChooseTool?.('select');
          const child = groupEditing.hit(world, touchReach(HIT_SLOP) / camera.zoom);
          setSelection(child ? [child] : []);
          return true;
        },
        drawWith: (patch) => options.onDrawWith?.(patch),
        railChanged: () => options.onRailChanged?.(),
        pictureSize: (assetId) => {
          // What is loaded first: the painter lays out that, and an asset's stored size can be absent.
          const loaded = images.get(assetId);
          if (loaded) return { width: loaded.width(), height: loaded.height() };
          const asset = assetsMap(open.root).get(assetId) as (Asset & { width?: number; height?: number }) | undefined;
          return asset?.width && asset.height ? { width: asset.width, height: asset.height } : null;
        },
        croppingChanged: (id) => options.onCroppingChanged?.(id),
        chooseTool: (id) => options.onChooseTool?.(id),
        duplicate: (offset) => duplicate(offset ?? 10, false),
        settleIntoArtboards: (ids) => settleIntoArtboards(ids),
        showFields: () => options.onShowFields?.(),
        setCursor: (css) => {
          setCanvasCursor(canvas, css);
        },
        get snap() {
          return snapping();
        },
        get guides() {
          return liveGuides();
        },
        catchGuides: (on) => {
          litGuides = on;
          if (on.length === 0) litLines = [];
        },
        place: (world, ignore, axes) => place(world, ignore, axes),
        artboardAt: (world) => renderer.unitsAt(world.x, world.y).find((id) => scene.get(id)?.type === 'artboard'),
        reveal: (box) => {
          // Left alone if it is already in view, clear of the chrome: moving the camera
          // under somebody who can see what they made would only lose their place.
          const frame = canvas.getBoundingClientRect();
          const room = inset();
          const shown = onScreen(box);
          const inView =
            shown.left >= frame.left + (room.left ?? 0) &&
            shown.right <= frame.right - ((room as { right?: number }).right ?? 0) &&
            shown.top >= frame.top + (room.top ?? 0) &&
            shown.bottom <= frame.bottom - ((room as { bottom?: number }).bottom ?? 0);
          if (!inView) setCamera(fitBox(contentBounds()));
        },
        nearby: (box) => renderer.unitsIn(box),
        snapNeighbours: (box, moving) => snapNeighbours(box, moving),
        boundsOf: (id) => renderer.bounds.worldGeometryBounds(id),
        unite: (rings, others) => {
          const drawn = rings.filter((ring) => ring.length > 2);
          if (drawn.length === 0) return null;
          // Every ring in one path as its own contour, then unioned with nothing: that is
          // what asks Skia to work out the ground they cover between them, overlaps and
          // all, rather than leaving them stacked or trusting a winding rule to guess.
          //
          // All wound the same way first. A contour's direction is what tells a winding
          // rule whether it is adding ground or taking it away, and pieces of a brush
          // stroke come out wound whichever way the hand happened to be travelling — so
          // half the loops of a figure of eight were filling and half were not.
          const cmds: number[] = [];
          for (const ring of drawn.map((ring) => (ringArea(ring) < 0 ? [...ring].reverse() : ring))) {
            cmds.push(MOVE, ring[0]!.x, ring[0]!.y);
            for (const at of ring.slice(1)) cmds.push(LINE, at.x, at.y);
            cmds.push(CLOSE);
          }
          let path = ck.Path.MakeFromCmds(cmds);
          if (!path) return null;
          const empty = new ck.Path();
          const merge = (other: ReturnType<typeof ck.Path.MakeFromCmds>) => {
            if (!other) return;
            const next = ck.Path.MakeFromOp(path!, other, ck.PathOp.Union);
            if (next) {
              path!.delete();
              path = next;
            }
            other.delete();
          };
          merge(ck.Path.MakeFromOp(path, empty, ck.PathOp.Union));

          // Only what it actually TOUCHES. Callers find candidates by bounding box, and a
          // box is a poor question: a long diagonal shape has a box covering half the
          // page, so a stroke nowhere near it would be swallowed into it — two separate
          // drawings quietly becoming one object. Asking for the overlap costs one more
          // path operation and is the difference between merging and hoovering.
          const merged: string[] = [];
          for (const id of others) {
            const node = scene.get(id);
            const local = node && nodeCommands(node);
            if (!local) continue;
            const other = ck.Path.MakeFromCmds([...transformCommands(local, scene.worldTransform(id))]);
            if (!other) continue;
            const shared = ck.Path.MakeFromOp(path, other, ck.PathOp.Intersect);
            const meets = !!shared && shared.toCmds().length > 0;
            shared?.delete();
            if (!meets) {
              other.delete();
              continue;
            }
            merged.push(id);
            merge(other);
          }
          empty.delete();
          // Wound so the holes are holes. A union answers with contours that are only
          // right under the fill rule it chose for them, and it likes to choose even-odd:
          // a ring comes back as two loops going the same way round, which even-odd reads
          // as a hole and the winding rule reads as solid. All we keep is the loops, so
          // the rule is ours to answer for — and a stroke round a circle was coming out a
          // disc. This turns the holes the other way so they are holes to anybody.
          const wound = path.makeAsWinding();
          if (wound) {
            path.delete();
            path = wound;
          }
          // Without the slivers, and the outline first: what comes back is the shape. A
          // sliver is under a square SCREEN pixel (never more than a unit²): a fixed unit²
          // threw away whole strokes of a small brush zoomed far in, with no word
          // (QA 2026-09-25, pencil-fill-brush-F13).
          const subpaths = realRings(subpathsOfCommands([...path.toCmds()]), Math.min(1, 1 / (camera.zoom * camera.zoom)));
          path.delete();
          return { subpaths, merged };
        },
        /**
         * A node's outline cut by regions: what falls inside each, and what falls outside
         * them all.
         *
         * The knife wants the first — a stroke drawn across a shape divides the page into
         * two, and the pieces are what the shape has in each. The eraser wants the second
         * — the brush covered some ground and the shape keeps everything else. One call
         * because it is one question asked from opposite ends, and because the answer has
         * to be exact: pieces that happen to overlap are not a cut, and a shape put back
         * together from them would show the seams.
         */
        carve: (() => {
          /**
           * The last brush worked out as one area, kept for the run of calls that asks
           * about it shape by shape. A long eraser scribble used to cost one Intersect per
           * brush ring per shape, all on release, and froze the editor for up to a second
           * (QA 2026-09-24, cut-slice-measure-F11).
           */
          let brush: { regions: readonly (readonly { x: number; y: number }[])[]; path: ReturnType<typeof ck.Path.MakeFromCmds> } | null = null;
          const together = (regions: readonly (readonly { x: number; y: number }[])[]) => {
            if (brush?.regions === regions) return brush.path;
            brush?.path?.delete();
            // Every ring wound the same way, then unioned with nothing, as `unite` does: a
            // brush ring's direction depends on which way the hand was travelling, and
            // opposite windings cancelled where they overlapped.
            const cmds: number[] = [];
            for (const ring of regions.filter((one) => one.length > 2).map((one) => (ringArea(one) < 0 ? [...one].reverse() : one))) {
              cmds.push(MOVE, ring[0]!.x, ring[0]!.y);
              for (const at of ring.slice(1)) cmds.push(LINE, at.x, at.y);
              cmds.push(CLOSE);
            }
            const laid = cmds.length > 0 ? ck.Path.MakeFromCmds(cmds) : null;
            const empty = new ck.Path();
            const path = laid ? (ck.Path.MakeFromOp(laid, empty, ck.PathOp.Union) ?? laid.copy()) : null;
            laid?.delete();
            empty.delete();
            brush = { regions, path };
            return path;
          };
          return (id: string, regions: readonly (readonly { x: number; y: number }[])[], options?: { together?: boolean }) => {
          const node = scene.get(id);
          const local = node && nodeCommands(node);
          if (!local) return null;
          const mine = ck.Path.MakeFromCmds([...transformCommands(local, scene.worldTransform(id))]);
          if (!mine) return null;
          if (node.type === 'path' && node.path?.fillRule === 'evenodd') mine.setFillType(ck.FillType.EvenOdd);

          if (options?.together) {
            const area = together(regions);
            if (!area) {
              mine.delete();
              return null;
            }
            const read = (path: ReturnType<typeof ck.Path.MakeFromCmds>) => (path ? subpathsOf(path) : []);
            const under = ck.Path.MakeFromOp(mine, area, ck.PathOp.Intersect);
            const left = ck.Path.MakeFromOp(mine, area, ck.PathOp.Difference);
            const answer = { inside: [read(under)], outside: read(left) };
            under?.delete();
            left?.delete();
            mine.delete();
            return answer;
          }

          const drawn = regions.filter((ring) => ring.length > 2);
          const cmds: number[] = [];
          for (const ring of drawn) {
            cmds.push(MOVE, ring[0]!.x, ring[0]!.y);
            for (const at of ring.slice(1)) cmds.push(LINE, at.x, at.y);
            cmds.push(CLOSE);
          }
          const pieces = drawn.map((ring) => {
            const one: number[] = [MOVE, ring[0]!.x, ring[0]!.y];
            for (const at of ring.slice(1)) one.push(LINE, at.x, at.y);
            one.push(CLOSE);
            return ck.Path.MakeFromCmds(one);
          });

          const read = (path: ReturnType<typeof ck.Path.MakeFromCmds>) =>
            path ? subpathsOf(path) : [];
          const inside = pieces.map((piece) => {
            if (!piece) return [];
            const shared = ck.Path.MakeFromOp(mine, piece, ck.PathOp.Intersect);
            const out = read(shared);
            shared?.delete();
            return out;
          });

          let outside: Subpath[] = [];
          const all = cmds.length > 0 ? ck.Path.MakeFromCmds(cmds) : null;
          if (all) {
            const left = ck.Path.MakeFromOp(mine, all, ck.PathOp.Difference);
            outside = read(left);
            left?.delete();
            all.delete();
          }
          for (const piece of pieces) piece?.delete();
          mine.delete();
          return { inside, outside };
          };
        })(),
        build: building,
        rulerOf: (id) => {
          const node = scene.get(id);
          if (node?.type !== 'path') return null;
          const sub = node.path?.subpaths?.[0];
          if (!sub || sub.points.length < 2) return null;
          return rulerIn(sub.points, sub.closed, scene.worldTransform(id), IDENTITY);
        },
        node: (id) => scene.get(id),
        text: editing,
        points: pathEditing,
      };

      /**
       * The slot an add-on's tool sits in.
       *
       * One fixed entry in the map that forwards to whatever add-on is in it, so the
       * tool machinery — adopting the chosen tool every frame, activate and deactivate
       * on a change — works for add-ons exactly as it does for the built-in tools, and
       * nothing about it has to know they exist. Swapping the add-on while the slot is
       * in hand leaves the old one and picks up the new one, as choosing a tool does.
       */
      let addonTarget: Tool | null = null;
      const addonSlot: Tool = {
        id: 'addon',
        activate: (c) => addonTarget?.activate?.(c),
        deactivate: (c) => addonTarget?.deactivate?.(c),
        onPointerDown: (e, w, c) => addonTarget?.onPointerDown?.(e, w, c),
        onPointerMove: (e, w, c) => addonTarget?.onPointerMove?.(e, w, c),
        onPointerUp: (e, w, c) => addonTarget?.onPointerUp?.(e, w, c),
        onDoubleClick: (e, w, c) => addonTarget?.onDoubleClick?.(e, w, c),
        onKeyDown: (e, c) => addonTarget?.onKeyDown?.(e, c),
        draw: (o, c) => addonTarget?.draw?.(o, c),
        rail: (c) => addonTarget?.rail?.(c) ?? null,
      };

      const blending = blendTool();
      const tools: Record<ToolId, Tool> = {
        select: selectTool(),
        rect: shapeTool('rect'),
        ellipse: shapeTool('ellipse'),
        polygon: shapeTool('polygon'),
        star: shapeTool('star'),
        line: shapeTool('line'),
        arc: arcTool(),
        pen: penTool(LOCAL_ORIGIN),
        draw: drawTool(),
        liquify: liquifyTool(),
        width: widthTool(),
        cut: cutTool(),
        warp: transformTool(),
        slice: sliceTool(),
        builder: builderTool(),
        blend: blending,
        measure: measureTool(),
        cloner: clonerTool(),
        artboard: artboardTool(),
        text: textTool(DEFAULT_FAMILY),
        addon: addonSlot,
      };
      let modifiers = { ctrl: false, shift: false, alt: false, meta: false };
      let current: Tool = tools.select;
      current.activate?.(context);
      pointsLeft = () => {
        current.onPointsClosed?.(context);
        options.onRailChanged?.();
        needsFrame = true;
      };
      /** Puts the tool in hand down, once — early, from the layout cleanup, when a file closes. */
      let toolDown = false;
      const putToolDown = () => {
        if (toolDown) return;
        toolDown = true;
        current.deactivate?.(context);
      };
      putToolDownRef.current = putToolDown;
      cleanups.push(() => {
        putToolDown();
        if (putToolDownRef.current === putToolDown) putToolDownRef.current = null;
      });

      /** Follows the chrome: choosing a tool leaves the last one cleanly. */
      const useTool = (id: ToolId) => {
        if (tools[id] === current) return;
        current.deactivate?.(context);
        // Typing and point editing belong to the tool they were started with. Left live,
        // Delete went on removing letters from the old text and anchors from the old path
        // after another tool was picked (QA 2026-09-24, text-F2, select-points-F6).
        editing?.stop();
        pathEditing.close();
        // A Draw the Path still waiting would take this tool's first drag (anim-F6).
        cancelDrawing();
        // Whatever the last tool left behind is its own step, apart from what comes next.
        undo.stopCapturing();
        setCanvasCursor(canvas, null);
        // The pen's hover lights guides and alignment lines; picked away from by key, they
        // stayed on screen until the next click (QA 2026-09-24, view-snapping-F19).
        litGuides = [];
        litLines = [];
        current = tools[id];
        current.activate?.(context);
        options.onRailChanged?.();
        needsFrame = true;
      };

      // ── pointer ──────────────────────────────────────────────────────────

      /**
       * How many presses in a row the current one is, counted here because nothing else
       * will: a PointerEvent's `detail` is 0 by specification, so the browser's own click
       * count never reaches a canvas. Close in time AND close in place, since two presses
       * a screen apart are two presses however quickly they came.
       */
      let clicks = 0;
      let lastPress = { at: 0, x: 0, y: 0 };
      const DOUBLE_MS = 450;
      const DOUBLE_SLOP = 4;

      const onPointerDown = (e: PointerEvent) => {
        if (handOnCanvas) return;
        // The right button belongs to the quick ring (contextmenu); it used to use the tool
        // in hand as well, and its capture kept the ring from ever seeing the release
        // (QA 2026-09-24, menus-shortcuts-quickbar-F18). Middle button still pans.
        if (e.button !== 0 && e.button !== 1) return;
        // A new gesture is a new undo step. Steps used to close only where each action
        // remembered to close its own, so anything that forgot — a pen path, a new text,
        // a panel — merged with the next thing done, and one Cmd+Z took back both.
        if (!current.holdsStep?.()) undo.stopCapturing();
        pressed = true;
        pressOrigin = { x: e.clientX, y: e.clientY };
        dragging = false;
        // A click only changes selection. Do not fade the quick bar until this
        // press actually moves (the selection tool uses the same 3 CSS-pixel slop).
        // Capture is a convenience — it keeps the moves coming if the pointer leaves the
        // canvas — and it throws for a pointer the browser no longer considers active.
        // Losing the whole press to that is far worse than losing the capture.
        try {
          canvas.setPointerCapture(e.pointerId);
        } catch {
          // Nothing to do: the press is still worth handling.
        }
        const world = toWorld(e.clientX, e.clientY);

        const now = e.timeStamp || performance.now();
        const sameSpot = Math.abs(e.clientX - lastPress.x) <= DOUBLE_SLOP && Math.abs(e.clientY - lastPress.y) <= DOUBLE_SLOP;
        clicks = sameSpot && now - lastPress.at <= DOUBLE_MS ? clicks + 1 : 1;
        lastPress = { at: now, x: e.clientX, y: e.clientY };

        if (layerPick) {
          const chosen = pickAt(world);
          const then = layerPick.then;
          layerPick = null;
          needsFrame = true;
          then(chosen);
          return;
        }
        // The eyedropper: one click, then back to what was in hand.
        if (picking) {
          const taken = picking;
          picking = false;
          const hit = hitTest(world);
          const fills = (hit ? (scene.get(hit) as { fills?: { type: string; color?: Color }[] } | undefined)?.fills : undefined) ?? [];
          const solid = fills.find((paint) => paint.type === 'solid');
          if (solid?.color) taken(solid.color);
          return;
        }
        // Panning belongs to the canvas, not to a tool: every tool pans the same way.
        // Middle button or space held. Option used to pan here too, which is the one
        // modifier every editor spends on "drag a copy of this" — and it is the reason
        // option-drag did nothing but move the page.
        if (e.button === 1 || spaceHeld || options.panRef?.current) {
          panning = world;
          return;
        }
        // A press on the canvas ends a preview: playing hides every handle and preview, and
        // the only Stop used to be back in the Animate panel (QA 2026-09-24, anim-F7).
        const wasPlaying = playing && playing.held === undefined ? playing.id : null;
        stopPlaying();
        // A viewer's press is for looking: the play mark plays its page, anything else takes
        // hold of the page and pans it. Nothing reaches a tool, a guide or a handle.
        if (readOnly()) {
          const played = e.button === 0 ? markAt(e.clientX, e.clientY) : null;
          if (played) {
            if (played !== wasPlaying) playPageRef.current?.(played);
            return;
          }
          panning = world;
          return;
        }
        // Drawing a path takes the whole gesture: no tool, no selection, no guides. The
        // layer is already chosen — this press is only saying where its journey starts.
        if (drawing) {
          drawing.from = world;
          drawing.points = [{ x: 0, y: 0 }];
          needsFrame = true;
          return;
        }
        // A gradient's handle, when its picker has put them on the canvas.
        const handle = e.button === 0 ? gradientHandleAt(world) : null;
        if (handle) {
          gradientHeld = handle;
          needsFrame = true;
          return;
        }
        /**
         * Guides belong to the canvas too, for exactly the reason panning does: you pull
         * one out of a ruler with whatever is in hand, and a tool that had to know about
         * them would be every tool having to know about them.
         *
         * A press on a ruler starts a guide; a press on a guide takes hold of the one
         * that is there. Neither reaches the tool, so a click on the ruler strip does not
         * also deselect the drawing behind it.
         */
        const box = canvas.getBoundingClientRect();
        const strip = view().rulers ? bandAt(e.clientX - box.left, e.clientY - box.top, rulers()) : null;
        if (strip) {
          // With Guides hidden a ruler gives none: each drag used to add one nobody could
          // see, and they all appeared together when Guides came back on (QA 2026-09-24,
          // view-snapping-F6). The press is still the ruler's, not the art's under it.
          // The corner square gives no guide either; it only keeps the press off the art
          // painted under it (view-snapping-F16).
          if (strip !== 'corner' && view().guides) holding = { id: null, axis: strip, started: false, at: strip === 'x' ? world.x : world.y };
          return;
        }
        // The play mark outside an animated board's corner plays that page, with whatever
        // is in hand, and the press goes no further. Pressed again while the page plays,
        // it was a Stop.
        const marked = e.button === 0 ? markAt(e.clientX, e.clientY) : null;
        if (marked) {
          if (marked !== wasPlaying) playPageRef.current?.(marked);
          return;
        }
        /**
         * Taking hold of a guide belongs to the arrow, and only where the press would
         * otherwise hit nothing.
         *
         * Two separate rules, both learnt the same way. A pen click that landed on a
         * guide and moved the guide instead of putting down a point would break the one
         * thing guides are for — so only the arrow grabs one. And a guide is a hairline
         * drawn in FRONT of the artwork: let it take presses over a shape and a guide
         * lying across a rectangle makes that rectangle unclickable, which is exactly
         * what happened the first time this was tried. Between a shape somebody can see
         * and a line one pixel wide over the top of it, the shape is what they meant.
         *
         * A guide that crosses artwork is still movable — grab it where it runs clear,
         * which on a line the width of the page it almost always does. Pulling a NEW one
         * out of a ruler works with anything in hand, as it does in Illustrator.
         */
        // A press outside the group being worked in steps out of it — only as far as the
        // point needs, so a neighbour one level up is taken, not the whole outer group.
        if (options.toolRef.current === 'select' && groupEditing.current) groupEditing.retreat(world, 12 / camera.zoom);
        const grabbed = options.toolRef.current === 'select' && !hitTest(world) && !labelAt(e.clientX, e.clientY) ? guideUnder(e.clientX, e.clientY) : null;
        if (grabbed) {
          holding = { id: grabbed.id, axis: grabbed.axis, started: true, at: grabbed.at };
          needsFrame = true;
          return;
        }
        // A press with a tool stops playback first. While a page played, the pen went on
        // adding points nobody could see — the overlay is not drawn during playback — and
        // saving them (QA 2026-09-25, pen-F17).
        stopPlaying();
        useTool(options.toolRef.current);
        toolPressed = true;
        const clickedBoard = artboardAt(world.x, world.y);
        if (clickedBoard) activeArtboard.choose(clickedBoard);
        current.onPointerDown?.(e, world, context);
        // Let the tool handle grips and object selection first. Empty artboard space
        // counts as canvas; clicking a layer or manipulating its handles keeps Properties.
        if (options.toolRef.current === 'select' && selection.length === 0 && !hitTest(world)) {
          emptyCanvasPressRef.current?.();
        }
        if (clickedBoard) viewChanged();
      };

      const onPointerMove = (e: PointerEvent) => {
        noteModifiers(e);
        if (handOnCanvas) return;
        if (pressed && !dragging && e.buttons !== 0 && Math.hypot(e.clientX - pressOrigin.x, e.clientY - pressOrigin.y) >= 3) {
          dragging = true;
          viewChanged();
        }
        const world = toWorld(e.clientX, e.clientY);
        publish.cursor(world);
        if (layerPick) {
          const over = pickAt(world);
          if (over !== layerPick.hovered) { layerPick.hovered = over; needsFrame = true; }
          canvas.style.cursor = over ? 'pointer' : 'crosshair';
          return;
        }
        // A name lit by the hover goes back to quiet once the hand is busy with a drag.
        if (e.buttons !== 0 && labelLit) {
          labelLit = null;
          needsFrame = true;
        }
        // The rulers' marks follow the pointer whatever else is happening, which is what
        // makes a ruler answer a question rather than just sit there.
        pointerAt = world;
        if (view().rulers || holding) needsFrame = true;
        if (panning && e.buttons !== 0) {
          setCamera(panBy(camera, e.movementX, e.movementY));
          return;
        }
        if (options.panRef?.current && e.buttons === 0) { current.onPointerLeave?.(context); return; }
        if (gradientHeld && e.buttons !== 0) {
          const held = gradientInHand();
          const back = held ? invert(held.world) : null;
          if (held && back && gradientEdit) {
            const local = applyToPoint(back, world.x, world.y);
            const next = dragGradientHandle(held.paint, held.box, gradientHeld, local);
            const targetIndex = gradientEdit.index ?? 0;
            const paints = held.list.map((paint, index) => (index === targetIndex ? next : paint));
            edit([{ op: 'set', id: held.node.id, props: held.node.type === 'text' && gradientEdit.which === 'fills' ? { style: { ...held.node.style, fills: paints } } : { [gradientEdit.which]: paints } }]);
          }
          needsFrame = true;
          return;
        }
        if (gradientEdit && e.buttons === 0) {
          // Over a handle, a press is the handle's — and the picker must not take it as a
          // click outside itself and close under the hand.
          const over = gradientHandleAt(world) !== null;
          if (over) canvas.dataset.keepsPopover = 'true';
          else delete canvas.dataset.keepsPopover;
          context.setCursor(over ? 'grab' : null);
          if (over) return;
        }
        if (drawing && e.buttons !== 0 && drawing.points.length > 0) {
          const last = drawing.points[drawing.points.length - 1]!;
          const dx = world.x - drawing.from.x;
          const dy = world.y - drawing.from.y;
          // A point every half screen-pixel of travel, not one per event: a trackpad can
          // fire a dozen events without the pointer having gone anywhere, and each of
          // those is a point stored, sent to every other editor and walked every frame.
          const step = 0.5 / Math.max(camera.zoom, 1e-6);
          if (Math.hypot(dx - last.x, dy - last.y) >= step) drawing.points.push({ x: dx, y: dy });
          needsFrame = true;
          return;
        }
        if (holding && e.buttons !== 0) {
          const raw = holding.axis === 'x' ? world.x : world.y;
          let at = guidePlace(holding.axis, holding.id, raw);
          // Caught by nothing, it is kept at the precision its label says: the label read
          // 348 px while 347.6 was stored, and a shape snapped to it then showed a
          // different X in the inspector (QA 2026-09-24, view-snapping-F18). A guide that
          // caught an edge stays exactly on the edge.
          if (at === raw) {
            const said = options.unitsRef?.current ?? 'px';
            const unit = said === 'ft-in' ? 'in' : said;
            const places = said === 'ft-in' ? 1 : decimalsFor(unit, true);
            const scale = 10 ** places;
            at = fromUnit(Math.round(toUnit(at, unit) * scale) / scale, unit);
          }
          // Made on the first MOVE rather than on the press, so a click on a ruler that
          // goes nowhere leaves nothing behind to tidy up.
          if (!holding.started) {
            holding = { ...holding, id: addGuide(page, holding.axis, at, LOCAL_ORIGIN), started: true, at };
          } else if (holding.id) {
            moveGuide(page, holding.id, at, LOCAL_ORIGIN);
            holding = { ...holding, at };
          }
          return;
        }
        /**
         * Over a ruler or over a guide, the pointer says what the press would do, and
         * the tool is not told about the move at all.
         *
         * Both halves matter. A ruler you can pull a guide out of and a ruler you cannot
         * look identical, so the cursor is the only thing that says which this is — and a
         * tool that went on hover-testing the artwork under a ruler strip would light up
         * shapes the strip is covering.
         */
        if (e.buttons === 0) {
          const box = canvas.getBoundingClientRect();
          const strip = view().rulers ? bandAt(e.clientX - box.left, e.clientY - box.top, rulers()) : null;
          const over = strip || options.toolRef.current !== 'select' || hitTest(world) ? null : guideUnder(e.clientX, e.clientY);
          // A strip only offers a guide while Guides are shown (view-snapping-F6).
          const pull = view().guides ? strip : null;
          const wanted = pull === 'x' || over?.axis === 'x' ? 'ew-resize' : pull === 'y' || over?.axis === 'y' ? 'ns-resize' : null;
          // Over a strip or the corner square that offers nothing, the arrow — and the tool
          // hears nothing, so it does not light art hidden under the rulers (view-snapping-F16).
          if (!wanted && strip) {
            setCanvasCursor(canvas, null);
            furnitureCursor = true;
            return;
          }
          if (wanted) {
            setCanvasCursor(canvas, wanted);
            furnitureCursor = true;
            // A ruler is off the drawing as far as the tool is concerned: its hover preview
            // goes, as it does over the chrome (QA 2026-09-24, artboards-F17).
            if (strip) current.onPointerLeave?.(context);
            return;
          }
          // Put it back on the way out. Not every tool sets a cursor on every move, so
          // leaving ours behind would strand a resize arrow over the middle of the page.
          // Over a play mark, the hand that says it can be pressed; the tool hears
          // nothing, so the art under the mark does not light up. Not while a page plays:
          // the mark is not drawn then.
          const mark = playing ? null : markAt(e.clientX, e.clientY);
          if (mark !== markLit) {
            markLit = mark;
            needsFrame = true;
          }
          // Over a board's name it takes the full ink, which is how it says a press takes the board.
          const named = mark || options.toolRef.current !== 'select' ? null : labelAt(e.clientX, e.clientY);
          if (named !== labelLit) {
            labelLit = named;
            needsFrame = true;
          }
          if (mark) {
            setCanvasCursor(canvas, 'pointer');
            furnitureCursor = true;
            current.onPointerLeave?.(context);
            return;
          }
          if (furnitureCursor) {
            setCanvasCursor(canvas, null);
            furnitureCursor = false;
          }
        }
        // Hovering belongs to the tool in HAND; a press belongs to the tool that took it.
        // The frame adopts the chosen tool, so these are the same thing by the next frame
        // — this covers the moves that land in the gap between choosing one and drawing.
        const hovering = e.buttons === 0 ? (tools[options.toolRef.current] ?? current) : current;
        hovering.onPointerMove?.(e, world, context);
      };

      const onPointerUp = (e: PointerEvent) => {
        const world = toWorld(e.clientX, e.clientY);
        const wasPanning = panning !== null;
        panning = null;
        if (pressed) {
          pressed = false;
          dragging = false;
          viewChanged();
        }
        // Whatever the last press was aiming at, it has landed: the marks explaining it
        // go with the gesture rather than staying on the glass afterwards.
        litLines = [];
        litGuides = [];
        needsFrame = true;
        if (wasPanning) return;
        if (gradientHeld) {
          gradientHeld = null;
          undo.stopCapturing();
          return;
        }
        if (drawing) {
          const { id, points, onDone } = drawing;
          drawing = null;
          // Thinned first, then rounded: smoothing hundreds of jittery points keeps the
          // jitter and just softens it, while smoothing the few that carry the shape is
          // what a person means by "clean it up". Tolerance in SCREEN pixels, so a path
          // drawn at 10% zoom is thinned as loosely as it was drawn.
          const enough = points.length > 1 && measurePath(points).length * camera.zoom > 4;
          if (!enough) {
            onDone(false);
          } else {
            const thin = simplifyPath(points, 2 / Math.max(camera.zoom, 1e-6));
            // Fitted under the limit along its whole length — cutting it off at the limit
            // stopped a long spiral partway (QA 2026-09-24, anim-F12). Then turned from the
            // page's axes into the layer's parent's, where poses live: inside a turned or
            // scaled group a rightward drag played downward (anim-F13).
            const drawn = fitPath(smoothPath(thin), MOST_PATH_POINTS).map((point) => {
              const along = deltaToParent(scene, id, point.x, point.y);
              return { x: along.dx, y: along.dy };
            });
            const had = (scene.get(id) as { animation?: LayerAnimation } | undefined)?.animation;
            // What it already had is kept where it still fits the schema. A stored animation
            // that did not used to throw here, mid-release: the path was lost and the panel's
            // button stayed on "Now Drag Across the Page" for good.
            const kept = LayerAnimationSchema.safeParse({ ...(had ?? {}), motion: 'path', path: drawn });
            const animation = kept.success ? kept.data : LayerAnimationSchema.parse({ motion: 'path', path: drawn });
            edit([{ op: 'set', id, props: { animation } }]);
            undo.stopCapturing();
            onDone(true);
          }
          needsFrame = true;
          return;
        }
        if (holding) {
          /**
           * A guide dropped back on a ruler is a guide taken away.
           *
           * The gesture every editor has, and the reason there is no delete button for
           * one: you put it there by pulling it out, so you get rid of it by pushing it
           * back. Dropping one off the far edge of the window does NOT remove it — that
           * is a drag that went too far, not a decision.
           */
          const box = canvas.getBoundingClientRect();
          // On a strip, the corner, or past the near edge of a ruler (view-snapping-F7).
          const back = view().rulers && backInRulers(e.clientX - box.left, e.clientY - box.top, rulers());
          if (holding.id && back) removeGuide(page, holding.id, LOCAL_ORIGIN);
          holding = null;
          undo.stopCapturing();
          needsFrame = true;
          return;
        }
        const forTool = toolPressed;
        toolPressed = false;
        if (forTool) current.onPointerUp?.(e, world, context);
      };

      /** The hand has gone; the marks that said where it was should go with it. */
      const onPointerLeave = () => {
        // The tool's own marks under the hand go too — the brush circle stayed at the
        // canvas edge over the toolbar (QA 2026-09-25, pencil-fill-brush-F6).
        current.onPointerLeave?.(context);
        // Over the chrome the pointer is not on the page: the others' copy of it fades.
        publish.cursor(null);
        if (labelLit) {
          labelLit = null;
          needsFrame = true;
        }
        if (pointerAt === null) return;
        pointerAt = null;
        needsFrame = true;
      };

      const onDoubleClick = (e: MouseEvent) => {
        // Both presses went to the ruler, so the double click is the ruler's too — it used
        // to open the points or the text of a shape lying under the strip (QA 2026-09-24,
        // view-snapping-F15).
        const box = canvas.getBoundingClientRect();
        if (view().rulers && bandAt(e.clientX - box.left, e.clientY - box.top, rulers()) !== null) return;
        // And both went to a play mark: a quick second press is a Stop, not an open.
        if (markAt(e.clientX, e.clientY)) return;
        // Opening a text or a path to change it is not for a viewer.
        if (readOnly()) return;
        const world = toWorld(e.clientX, e.clientY);
        if (current.onDoubleClick?.(e, world, context)) e.preventDefault();
      };

      /**
       * What is held down, kept between events.
       *
       * A tool that only shows something while a key is held cannot learn that from the
       * pointer, because the hand is usually still while the key goes down — and a key
       * event tells you about the moment, not about the state. So the state is kept, and
       * a frame is asked for when it changes, which is what makes holding a key on its
       * own able to change what is drawn.
       */
      const noteModifiers = (e: KeyboardEvent | PointerEvent | MouseEvent) => {
        const next = { ctrl: e.ctrlKey, shift: e.shiftKey, alt: e.altKey, meta: e.metaKey };
        if (next.ctrl === modifiers.ctrl && next.shift === modifiers.shift && next.alt === modifiers.alt && next.meta === modifiers.meta) return;
        modifiers = next;
        needsFrame = true;
      };

      const onWheel = (e: WheelEvent) => {
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) setCamera(zoomAt(camera, e.offsetX, e.offsetY, Math.exp(-e.deltaY * 0.01)));
        else setCamera(panBy(camera, -e.deltaX * WHEEL_PAN_SPEED, -e.deltaY * WHEEL_PAN_SPEED));
      };

      /**
       * What a delete should name, out of what is chosen.
       *
       * Deleting a node takes its whole subtree, so naming a parent AND one of its
       * children fails on the child — it is gone by then — and a batch is all or nothing,
       * so NOTHING goes. A band drawn round a drawing catches parents and children
       * together, which makes that the ordinary case rather than an odd one.
       *
       * Locked nodes are dropped rather than forced through: a lock exists to stop
       * exactly this, and one of them in the selection would otherwise fail the batch and
       * take everything else down with it.
       */
      // Ids no longer on the page are dropped too: one left in the selection (a shape the
      // eraser rubbed out) failed the all-or-nothing batch, so Delete deleted nothing
      // (QA 2026-09-24, cut-slice-measure-F20).
      const deletable = (ids: readonly string[]): string[] => scene.topmost(ids).filter((id) => scene.has(id) && !scene.lockedHere(id));
      /**
       * What deleting these takes with it: a cloner left holding nothing — which has no box,
       * so nobody could see or click it on the canvas again (QA 2026-09-24, cloner-F23) —
       * and the effectors only a departing cloner named, which sat in Layers pushing
       * nothing (cloner-F16). One batch, so one Undo brings all of it back.
       */
      const deletions = (doomed: readonly string[]): string[] => {
        const going = scene.topmost([...doomed, ...emptiedCloners(scene, doomed)]);
        return [...going, ...effectorsLeftBehind(scene, going)];
      };
      /**
       * The ops for a delete: `deletions`, and a blend it leaves with one end or none goes
       * too, its last end handed back where the blend sat. Deleting both ends left an empty
       * 'Blend' in Layers that nothing on the canvas could see or reach
       * (QA 2026-09-25, blend-F22). A locked blend or end is left as it is.
       */
      const deleteOps = (doomed: readonly string[]): unknown[] => {
        const going = deletions(doomed);
        const gone = new Set(going);
        const ops: unknown[] = going.map((id) => ({ op: 'delete', id }));
        const blends = new Set<string>();
        for (const id of going) {
          const parent = scene.parentOf(id);
          if (parent && !gone.has(parent) && scene.get(parent)?.type === 'blend') blends.add(parent);
        }
        for (const blend of blends) {
          const left = scene.childrenOf(blend).filter((child) => !gone.has(child));
          if (left.length > 1 || scene.lockedHere(blend) || left.some((child) => scene.lockedHere(child))) continue;
          const parent = scene.parentOf(blend) ?? ROOT_ID;
          for (const child of left) ops.push({ op: 'move', id: child, parentId: parent, position: { before: blend }, keepWorldTransform: true });
          ops.push({ op: 'delete', id: blend });
        }
        return ops;
      };
      /** The layers an effect edit reaches: one or several, less any the engine would refuse as locked. */
      const effectTargets = (id: string | readonly string[]): string[] => (typeof id === 'string' ? [id] : [...id]).filter((one) => !scene.get(one)?.locked);
      /**
       * The layers a command may change, saying when some were left alone. One locked layer
       * in a batch refuses the whole batch, and align, arrange and restyle used to fail that
       * way without a word (QA 2026-09-24, menus-shortcuts-quickbar-F8).
       */
      const unlocked = (ids: readonly string[]): string[] => {
        const free = deletable(ids);
        const skipped = scene.topmost(ids).filter((id) => scene.has(id)).length - free.length;
        if (skipped > 0) options.onNotice?.(skipped === 1 ? 'One locked layer was left as it is.' : `${skipped} locked layers were left as they are.`);
        return free;
      };

      const onKeyDown = (e: KeyboardEvent) => {
        noteModifiers(e);
        // While typing, the text takes the key: Delete removes a letter, not the layer.
        if (editing?.current && editing.handleKey(e)) return;
        // Space is the pan key, even over a chrome button that kept focus from a CLICK:
        // Chrome then pressed that button again on the key's release, so Undo ran twice
        // (QA 2026-09-24, view-snapping-F5). A button reached with Tab (focus-visible)
        // still takes Space, which is how a keyboard presses it.
        if (e.key === ' ' && !menuIsOpen() && clickedButton(e.target)) {
          e.preventDefault();
          (e.target as HTMLElement).blur();
          spaceHeld = true;
          return;
        }
        // An open menu owns the keyboard, and a field owns the keys it types or moves with;
        // everything else is a shortcut, whatever happens to have focus (a slider just
        // dragged, a button just clicked).
        if (menuIsOpen() || keyBelongsToField(e.target, e.key) || keyBelongsToControl(e.target, e.key)) return;
        // Esc leaves a waiting Draw the Path and stops a preview before it does anything
        // else. Neither could be left: Esc only cleared the selection, so the next drag with
        // any tool became a motion path, and a preview ran on with every handle hidden
        // (QA 2026-09-24, anim-F6, anim-F7).
        if (e.key === 'Escape' && layerPick) {
          e.preventDefault();
          const then = layerPick.then;
          layerPick = null;
          needsFrame = true;
          then(null);
          return;
        }
        if (e.key === 'Escape' && (drawing || playing)) {
          e.preventDefault();
          cancelDrawing();
          stopPlaying();
          return;
        }
        // Typing into a text, but focus wandered off to a panel (a font was just chosen):
        // the letter still goes into the text, instead of switching tools
        // (QA 2026-09-24, text-F9).
        if (editing?.current && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          e.preventDefault();
          editing.insert(e.key);
          editing.refocus();
          return;
        }
        // Any other key that does something is a command of its own: close the step before
        // it. Modifiers and the space bar are held DURING drags, and must not split them.
        if (!HELD_KEYS.has(e.key) && !current.holdsStep?.()) undo.stopCapturing();
        // A viewer looks: the keys that move the view work, the keys that edit do nothing.
        if (readOnly()) {
          if (e.key === ' ') spaceHeld = true;
          const looking = viewKeyOf(e);
          if (looking) {
            e.preventDefault();
            const middle = viewCentre();
            if (looking === 'zoom-in') setCamera(zoomAt(camera, middle.x, middle.y, 1.25));
            else if (looking === 'zoom-out') setCamera(zoomAt(camera, middle.x, middle.y, 1 / 1.25));
            else if (looking === 'fit') setCamera(fitBox(contentBounds()));
            else setCamera(actualSizeCamera());
          }
          return;
        }
        // A held transform gets Escape before point editing can use it to leave the path.
        // Cancel the drag in place and keep the chosen anchors ready for another try.
        if (e.key === 'Escape' && current.holdsStep?.() && current.onKeyDown?.(e, context)) return;
        // While editing points, Delete removes anchors rather than the whole path. Whatever
        // the key did to the points, the strip and the Point fields read it again — ⌘A
        // chose every anchor and they went on showing the old choice. A key that closed the
        // path hands the tool back its ordinary way of working, as Done does; the points
        // closing says so itself (QA 2026-09-25, select-points-F9, F17).
        if (pathEditing.active && pathEditing.handleKey(e)) {
          options.onRailChanged?.();
          needsFrame = true;
          return;
        }
        // Then the tool: a pen mid-path keeps Enter and Escape for itself.
        if (current.onKeyDown?.(e, context)) return;
        if (e.key === ' ') spaceHeld = true;
        const meta = e.metaKey || e.ctrlKey;
        /**
         * The view's own zoom keys, as the Zoom tooltips promise.
         *
         * Nothing answered ⌘+ and ⌘−, so the browser zoomed every panel instead and the
         * drawing went soft; ⌘0 fitted the drawing AND reset the browser's zoom, and ⌘1
         * set 100% and then Chrome jumped to the first tab (QA 2026-09-24, view-snapping-F2).
         */
        if (meta && !e.altKey) {
          // About the middle of the free space, not the window (view-snapping-F11).
          const middle = viewCentre();
          if (e.key === '=' || e.key === '+') {
            e.preventDefault();
            setCamera(zoomAt(camera, middle.x, middle.y, 1.25));
            return;
          }
          if (e.key === '-' || e.key === '_') {
            e.preventDefault();
            setCamera(zoomAt(camera, middle.x, middle.y, 1 / 1.25));
            return;
          }
          if (e.key === '0') {
            e.preventDefault();
            setCamera(fitBox(contentBounds()));
            return;
          }
          if (e.key === '1') {
            e.preventDefault();
            setCamera(actualSizeCamera());
            return;
          }
        }
        if (meta && e.key.toLowerCase() === 'z') {
          e.preventDefault();
          if (e.shiftKey) undo.redo();
          else undo.undo();
          return;
        }
        // ⌘A takes everything on the page, ⌘C/⌘X/⌘V the usual three.
        if (meta && e.key.toLowerCase() === 'a') {
          e.preventDefault();
          selectEverything();
          return;
        }
        // ⌥⌘C and ⌥⌘V copy and paste a layer's look (Figma's Copy/Paste properties). By the
        // key's place, not its letter: Option turns C into ç on a Mac.
        if (meta && e.altKey && !e.shiftKey && (e.code === 'KeyC' || e.code === 'KeyV')) {
          e.preventDefault();
          if (e.code === 'KeyC') copyProperties();
          else pasteProperties();
          return;
        }
        // With nothing chosen ⌘C is the browser's: copying nothing emptied both clipboards,
        // so the next paste did nothing and text on the page could not be copied
        // (QA 2026-09-24, menus-shortcuts-quickbar-F19).
        if (meta && e.key.toLowerCase() === 'c' && selection.length > 0) {
          e.preventDefault();
          copy();
          return;
        }
        if (meta && e.key.toLowerCase() === 'x') {
          e.preventDefault();
          cutSelection();
          return;
        }
        // ⌘V is left to the browser's own paste event where there is one, because that
        // event carries the clipboard's text without asking anybody for permission —
        // which is what lets a shape travel between two files. If no such event arrives,
        // a moment later, what was copied in this tab is pasted instead.
        if (meta && e.key.toLowerCase() === 'v') {
          pasteWanted = true;
          window.setTimeout(() => {
            if (!pasteWanted) return;
            pasteWanted = false;
            paste();
          }, 60);
          return;
        }
        // ⌘G groups, ⇧⌘G takes a group apart.
        if (meta && e.key.toLowerCase() === 'g') {
          e.preventDefault();
          if (e.shiftKey) ungroup();
          else group();
          return;
        }
        // ⌘D duplicates, a little off the original so it can be seen and dragged.
        if (meta && e.key.toLowerCase() === 'd') {
          e.preventDefault();
          duplicate();
          return;
        }
        /**
         * ⌘] and ⌘[ move a shape one step through the stack, ⇧⌘] and ⇧⌘[ all the way — as
         * the Arrange menu says, through the same arrange() it uses. ⌘] used to send it to
         * the front, and with Shift the key reads '}' or '{', so the advertised ⇧⌘] did
         * nothing here and switched Chrome's tabs instead (QA 2026-09-24,
         * menus-shortcuts-quickbar-F12). Matched by `code` for that reason.
         */
        const bracket = e.code === 'BracketRight' || e.key === ']' || e.key === '}' ? ']' : e.code === 'BracketLeft' || e.key === '[' || e.key === '{' ? '[' : null;
        if (meta && bracket) {
          e.preventDefault();
          if (selection.length === 0) return;
          handleRef.current.arrange(bracket === ']' ? (e.shiftKey ? 'front' : 'forward') : e.shiftKey ? 'back' : 'backward');
          return;
        }
        if (!meta && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
          if (selection.length === 0) return;
          e.preventDefault();
          // A nudge is one unit, or ten with Shift — the distance a hand expects.
          const step = e.shiftKey ? 10 : 1;
          // A run of one-unit nudges, told once it ends (onKeyUp): the hints offer ⇧ for ten (lib/hints).
          if (!e.shiftKey) {
            nudgeRun += 1;
            window.clearTimeout(nudgeTold);
          }
          const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
          const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
          edit(
            deletable(selection).flatMap((id) => {
              const t = scene.get(id)?.transform;
              // A nudge is along the page's axes, whatever the layer sits in.
              const along = deltaToParent(scene, id, dx, dy);
              return t ? [{ op: 'set', id, props: { transform: [t[0], t[1], t[2], t[3], t[4]! + along.dx, t[5]! + along.dy] } }] : [];
            }),
          );
          return;
        }
        if (e.key === 'Backspace' || e.key === 'Delete') {
          if (selection.length === 0) return;
          e.preventDefault();
          const doomed = deletable(selection);
          if (doomed.length > 0) {
            edit(deleteOps(doomed));
            undo.stopCapturing();
          }
          setSelection([]);
          return;
        }
        if (e.key === 'Enter' && selection.length === 1 && scene.get(selection[0]!)?.type === 'text' && editing && !e.shiftKey) {
          e.preventDefault();
          editing.start(selection[0]!, 'all');
          return;
        }
        // Down and up the tree without the mouse: Return chooses what a group holds,
        // ⇧Return the group round the selection.
        if (e.key === 'Enter' && !e.shiftKey && selection.length > 0 && selection.every((id) => scene.get(id)?.type === 'group')) {
          const inside = selection.flatMap((id) => scene.childrenOf(id)).filter((id) => scene.choosable(id));
          if (inside.length > 0) {
            e.preventDefault();
            setSelection(inside);
            // Return took the hand inside a group: the hint that teaches it is done (lib/hints).
            emitHintSignal('shortcut.enterGroup');
          }
          return;
        }
        if (e.key === 'Enter' && e.shiftKey && selection.length > 0) {
          const parents = new Set(selection.map((id) => scene.parentOf(id)));
          const parent = parents.size === 1 ? [...parents][0] : undefined;
          if (parent && scene.get(parent)?.type === 'group' && scene.choosable(parent)) {
            e.preventDefault();
            setSelection([parent]);
          }
          return;
        }
        if (e.key === 'Escape') {
          const group = groupEditing.exit();
          setSelection(group ? [group] : []);
          return;
        }
        // Bare 0 and 1 only: with ⌘ held they were answered above, and with anything
        // else held they are somebody else's shortcut.
        if (meta || e.altKey) return;
        if (e.key === '0') setCamera(fitBox(contentBounds()));
        if (e.key === '1') setCamera(actualSizeCamera());
      };
      /** Set by ⌘V and cleared by the paste event, so only one of the two ever lands. */
      let pasteWanted = false;
      /** One-unit nudges in a row, and the timer that tells the run once the hand stops. */
      let nudgeRun = 0;
      let nudgeTold = 0;

      const onKeyUp = (e: KeyboardEvent) => {
        noteModifiers(e);
        if (e.key === ' ') spaceHeld = false;
        current.onKeyUp?.(e, context);
        // A run of nudges is one undo step only while the key is down.
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
          undo.stopCapturing();
          window.clearTimeout(nudgeTold);
          if (nudgeRun > 0) {
            nudgeTold = window.setTimeout(() => {
              emitHintSignal('layer.nudged', { run: nudgeRun });
              nudgeRun = 0;
            }, 1200);
          }
        }
      };

      /**
       * What was copied: each shape as a plain record, with its subtree listed parents
       * first so it can be created again in one pass. Kept here rather than only in the
       * system clipboard, which a browser will not always hand back without a prompt.
       */
      interface Clipping {
        readonly id: string;
        /** Where it sat when copied, so a paste in the same file lands back in there. */
        readonly parent?: string;
        /** Its transform on the page, so it lands where it was whatever it is pasted into. */
        readonly world?: readonly number[];
        /** Its bounds on the page when copied. */
        readonly box?: { x: number; y: number; width: number; height: number };
        /** The artboard it was on, so a paste onto another one can keep its place there. */
        readonly board?: { id: string; width: number; height: number; world: readonly number[] };
        readonly node: Record<string, unknown>;
        readonly children: { id: string; parent: string; node: Record<string, unknown> }[];
      }
      let clipboard: Clipping[] = [];
      /** Whether `clipboard` reached the system clipboard too. Until it has, the in-tab copy is the one pasted. */
      let clipboardWritten = true;

      /**
       * A node with the scene's bookkeeping taken off, ready to be created again.
       *
       * `id`, `parentId` and `order` say where a node SITS; they are the scene's answer,
       * not the node's own description, and a create op says where it sits in its own
       * fields. Handing them back inside the node is what broke copy, paste and duplicate
       * together: the schema is strict, so it rejected all three with "Unrecognized keys"
       * and every one of them failed silently.
       */
      const bare = (node: NodeRecord): Record<string, unknown> => {
        const { id: _id, parentId: _parent, order: _order, ...rest } = node as NodeRecord & Record<string, unknown>;
        return rest as Record<string, unknown>;
      };

      const plain = (id: string): Record<string, unknown> | null => {
        const node = scene.get(id);
        return node ? bare(node) : null;
      };

      const snapshot = (id: string): Clipping | null => {
        const node = plain(id);
        if (!node) return null;
        const children: Clipping['children'] = [];
        const walk = (parent: string) => {
          for (const child of scene.childrenOf(parent)) {
            const record = plain(child);
            if (!record) continue;
            children.push({ id: child, parent, node: record });
            walk(child);
          }
        };
        walk(id);
        let boardId = scene.parentOf(id);
        while (boardId && boardId !== ROOT_ID && scene.get(boardId)?.type !== 'artboard') boardId = scene.parentOf(boardId);
        const boardNode = boardId && boardId !== ROOT_ID ? (scene.get(boardId) as NodeRecord & { width: number; height: number }) : null;
        const board = boardNode && boardId ? { id: boardId, width: boardNode.width, height: boardNode.height, world: [...scene.worldTransform(boardId)] } : undefined;
        const found = renderer.bounds.worldGeometryBounds(id);
        const box = found ? { x: found.x, y: found.y, width: found.width, height: found.height } : undefined;
        return { id, parent: scene.parentOf(id) ?? ROOT_ID, world: [...scene.worldTransform(id)], box, board, node, children };
      };

      /**
       * The pictures, shaders and fonts a set of clippings draws with.
       *
       * They live in the FILE (its root maps), not on the layers, so a copy pasted into
       * another file named a picture, a Noise fill or a font that file had never heard of:
       * grey boxes, no noise, Inter — and a reload did not help (QA 2026-09-24,
       * menus-shortcuts-quickbar-N14). They travel with the layers now.
       */
      interface ClipResources {
        assets: Record<string, unknown>;
        shaders: Record<string, unknown>;
        fonts: Record<string, unknown>;
      }
      const resourcesOf = (clips: readonly Clipping[]): ClipResources => {
        const out: ClipResources = { assets: {}, shaders: {}, fonts: {} };
        if (!open) return out;
        const assets = assetsMap(open.root);
        const shaders = shadersMap(open.root);
        const fonts = fontsMap(open.root);
        const scan = (value: unknown, key?: string): void => {
          if (Array.isArray(value)) {
            for (const item of value) scan(item);
            return;
          }
          if (value && typeof value === 'object') {
            for (const [k, v] of Object.entries(value)) scan(v, k);
            return;
          }
          if (typeof value !== 'string') return;
          if (key === 'assetId' && assets.has(value)) out.assets[value] = assets.get(value);
          else if (key === 'shaderId' && shaders.has(value)) out.shaders[value] = shaders.get(value);
          // A text names its face as `fontFamily`. Only `family` was looked for, which no layer
          // has, so no font ever travelled: a copy in another file — and a template saved
          // from a page in a brand's face — was set in the fallback.
          else if ((key === 'fontFamily' || key === 'family') && fonts.has(value)) out.fonts[value] = fonts.get(value);
        };
        for (const clip of clips) {
          scan(clip.node);
          for (const child of clip.children) scan(child.node);
        }
        return out;
      };

      /** Adds what the pasted layers draw with and this file lacks; never replaces its own. */
      const adoptResources = (found: Partial<ClipResources> | undefined) => {
        if (!open || !found) return;
        const into = (map: { has(key: string): boolean; set(key: string, value: never): unknown }, entries: Record<string, unknown> | undefined) => {
          for (const [key, value] of Object.entries(entries ?? {})) {
            if (!map.has(key) && value && typeof value === 'object') map.set(key, value as never);
          }
        };
        open.root.transact(() => {
          into(assetsMap(open.root), found.assets);
          into(shadersMap(open.root), found.shaders);
          into(fontsMap(open.root), found.fonts);
        }, LOCAL_ORIGIN);
      };

      const copy = () => {
        clipboard = selection.map(snapshot).filter((item): item is Clipping => item !== null);
        // Also to the system clipboard, so a shape can travel between files and tabs —
        // with what it draws with, which the other file may not have. Until the browser says
        // it is written, the system clipboard still holds whatever was there before, and a
        // paste must not take that for words copied elsewhere (review E3).
        const copied = clipboard;
        clipboardWritten = false;
        void navigator.clipboard
          ?.writeText(JSON.stringify({ artwork: copied, resources: resourcesOf(copied) }))
          .then(() => {
            if (clipboard === copied) clipboardWritten = true;
          })
          .catch(() => undefined);
      };

      /**
       * ⌥⌘C: the look of the chosen layer — fill, border, effects, opacity, blend, corners and
       * a text's type — held to be put on others (Figma's Copy properties). Several chosen:
       * the first one's look.
       */
      const copyProperties = (): boolean => {
        const node = selection.map((id) => scene.get(id)).find((one) => one && propertiesOf(one as never));
        const properties = node ? propertiesOf(node as never) : null;
        if (!node || !properties) {
          options.onNotice?.('Choose a layer to copy its properties.');
          return false;
        }
        const family = properties.textStyle?.fontFamily;
        const font = typeof family === 'string' ? (fontsMap(open.root).get(family) as DocumentFont | undefined) : undefined;
        holdLook({ properties, ...(font ? { font } : {}) });
        options.onNotice?.('Properties copied.');
        return true;
      };
      /** ⌥⌘V: the held look on every chosen layer that can take it, as one undo step. */
      const pasteProperties = (): boolean => {
        const look = heldLook();
        if (!look) {
          options.onNotice?.('Copy a layer’s properties first (⌥⌘C).');
          return false;
        }
        const ops: { op: 'set'; id: string; props: Record<string, unknown> }[] = [];
        for (const id of unlocked(selection)) {
          const node = scene.get(id);
          if (!node) continue;
          const props = propertiesFor(node as never, look.properties);
          if (Object.keys(props).length > 0) ops.push({ op: 'set', id, props });
        }
        if (ops.length === 0) return false;
        // The font a text's type names goes in first, as setFont does, or it would draw in
        // the fallback in a file that did not have it.
        const font = look.font;
        if (font && !fontsMap(open.root).has(font.family) && ops.some((one) => 'style' in one.props)) {
          open.root.transact(() => fontsMap(open.root).set(font.family, font as never), LOCAL_ORIGIN);
        }
        const result = edit(ops);
        if (!result.ok) {
          console.warn('Could not paste properties', result.errors);
          options.onNotice?.('Those properties could not be pasted here.');
          return false;
        }
        for (const one of ops) {
          if (!('style' in one.props)) continue;
          refitText(one.id, one.props);
          awaitingFonts.add(one.id);
        }
        undo.stopCapturing();
        return true;
      };

      /** ⌘X and Edit ▸ Cut: copied, then taken off the page as one step. */
      const cutSelection = () => {
        copy();
        const cut = deletable(selection);
        if (cut.length > 0) {
          edit(deleteOps(cut));
          setSelection([]);
          undo.stopCapturing();
        }
      };

      /** The resources written beside the layers on the system clipboard, if any. */
      const clipboardResources = (text: string): Partial<ClipResources> | undefined => {
        try {
          const read = JSON.parse(text) as { resources?: unknown };
          return read.resources && typeof read.resources === 'object' ? (read.resources as Partial<ClipResources>) : undefined;
        } catch {
          return undefined;
        }
      };

      /** What a clipping looks like, for text off the system clipboard we did not write. */
      const clippings = (text: string): Clipping[] | null => {
        try {
          const read = JSON.parse(text) as { artwork?: unknown };
          if (!Array.isArray(read.artwork)) return null;
          const out = read.artwork.filter(
            (item): item is Clipping =>
              !!item && typeof item === 'object' && typeof (item as Clipping).id === 'string' && !!(item as Clipping).node,
          );
          return out.length > 0 ? out : null;
        } catch {
          return null;
        }
      };

      /** The artboard a page point falls inside, frontmost first, or none. */
      const artboardAt = (x: number, y: number): string | null => {
        const boards = scene.childrenOf(ROOT_ID).filter((id) => scene.get(id)?.type === 'artboard');
        for (const id of [...boards].reverse()) {
          const board = scene.get(id) as NodeRecord & { width: number; height: number };
          const inverse = invertMatrix(scene.worldTransform(id));
          if (!inverse) continue;
          const at = applyToPoint(inverse, x, y);
          if (at.x >= 0 && at.y >= 0 && at.x <= board.width && at.y <= board.height) return id;
        }
        return null;
      };

      /**
       * Where a pasted copy goes.
       *
       * Paste used to put everything at the top of the page, so a shape copied off an
       * artboard came back drawn over it but outside it — and everything that works on an
       * artboard's own layers (an animation style, the page's animation, an artboard export)
       * passed it by without a word. Now, as Figma does it:
       *   1. back into the parent it was copied from, when that is still in this file;
       *   2. otherwise into the one artboard that is selected;
       *   3. otherwise into the artboard it lands on;
       *   4. otherwise the page.
       * Copied off one artboard and pasted while another is the one being worked on (it is
       * chosen, or was pressed, or holds what is chosen), it goes onto that one, in the same
       * place when the two are the same size and laid out the same way when they are not
       * (`placeOnBoard`; Farah, 23 Oct). `exact` is where it was, without paste's step.
       */
      const pasteParent = (clipping: Clipping, world: Matrix, exact?: Matrix): { parent: string; world: Matrix } => {
        // An artboard only ever sits on the page; asking for another parent failed the
        // whole paste, silently.
        if (clipping.node.type === 'artboard') return { parent: ROOT_ID, world };
        const working = exact && clipping.board && clipping.box ? activeArtboard.resolve(selection) : null;
        const target = working ? (scene.get(working) as NodeRecord & { width: number; height: number }) : null;
        if (working && target && exact && clipping.board && clipping.box && working !== clipping.board.id) {
          const from = clipping.board;
          const inverse = invertMatrix(from.world as Matrix);
          if (inverse) {
            const corners = [
              applyToPoint(inverse, clipping.box.x, clipping.box.y),
              applyToPoint(inverse, clipping.box.x + clipping.box.width, clipping.box.y + clipping.box.height),
            ];
            const box = {
              x: Math.min(corners[0]!.x, corners[1]!.x),
              y: Math.min(corners[0]!.y, corners[1]!.y),
              width: Math.abs(corners[1]!.x - corners[0]!.x),
              height: Math.abs(corners[1]!.y - corners[0]!.y),
            };
            const local = placeOnBoard(multiplyMatrix(inverse, exact), box, from, target);
            return { parent: working, world: multiplyMatrix(scene.worldTransform(working), local) };
          }
        }
        const size = clipping.node as { width?: number; height?: number };
        const centreOf = (m: Matrix) => applyToPoint(m, (size.width ?? 0) / 2, (size.height ?? 0) / 2);
        // A chosen artboard wins over where the copy came from: pasting a logo onto boards
        // 2 to 10 used to stack nine copies back on board 1, out of view. It lands where it
        // sat on its own board, or in the middle when that would be outside this one.
        const chosen = selection.length === 1 && scene.get(selection[0]!)?.type === 'artboard' ? selection[0]! : null;
        if (chosen) {
          const board = scene.get(chosen) as { width?: number; height?: number } | undefined;
          const at = scene.worldTransform(chosen);
          const from = clipping.parent && clipping.parent !== chosen && scene.get(clipping.parent)?.type === 'artboard' ? scene.worldTransform(clipping.parent) : null;
          let placed: Matrix = from ? [world[0], world[1], world[2], world[3], world[4] - from[4]! + at[4]!, world[5] - from[5]! + at[5]!] : world;
          const centre = centreOf(placed);
          const inside = centre.x >= at[4]! && centre.y >= at[5]! && centre.x <= at[4]! + (board?.width ?? 0) && centre.y <= at[5]! + (board?.height ?? 0);
          if (!inside) {
            const dx = at[4]! + (board?.width ?? 0) / 2 - centre.x;
            const dy = at[5]! + (board?.height ?? 0) / 2 - centre.y;
            placed = [placed[0], placed[1], placed[2], placed[3], placed[4] + dx, placed[5] + dy];
          }
          return { parent: chosen, world: placed };
        }
        // Inside a group being edited, the paste goes into that group: it is where the
        // person is working, whatever the copy came out of.
        const scope = groupEditing.current;
        if (scope) return { parent: scope, world };
        // Back where it came from — unless that is hidden or locked, where the copy would
        // be invisible or untouchable. A group counts only while it is being edited: copied
        // out of one and pasted after Esc, the copy kept landing back inside it. Left, it
        // goes onto the artboard round that group instead.
        let from = clipping.parent && clipping.parent !== ROOT_ID && scene.get(clipping.parent) ? clipping.parent : undefined;
        while (from && from !== ROOT_ID && scene.get(from)?.type === 'group') from = scene.parentOf(from);
        if (from && from !== ROOT_ID && scene.get(from) && !scene.hiddenHere(from) && !scene.lockedHere(from)) {
          return { parent: from, world };
        }
        const centre = centreOf(world);
        return { parent: artboardAt(centre.x, centre.y) ?? ROOT_ID, world };
      };

      /**
       * Dropped layers join the artboard they now sit on, as in Figma.
       *
       * Decided by each layer's centre, and only for layers on the page or directly in an
       * artboard: a layer inside a group moves with its group and stays in it, and an
       * artboard never goes inside another. The layer keeps its place on the canvas —
       * only its parent, and so its transform relative to that parent, changes.
       */
      const settleIntoArtboards = (ids: readonly string[]) => {
        const ops: unknown[] = [];
        for (const id of scene.topmost([...ids])) {
          const node = scene.get(id);
          if (!node || node.type === 'artboard') continue;
          const parent = scene.parentOf(id) ?? ROOT_ID;
          if (parent !== ROOT_ID && scene.get(parent)?.type !== 'artboard') continue;
          const box = renderer.bounds.worldGeometryBounds(id);
          if (!box) continue;
          const target = artboardAt(box.x + box.width / 2, box.y + box.height / 2) ?? ROOT_ID;
          if (target !== parent) ops.push({ op: 'move', id, parentId: target, position: 'front', keepWorldTransform: true });
        }
        if (ops.length > 0 && !edit(ops).ok) console.warn('Could not move the dropped layers into their artboard');
      };

      /**
       * Points the copies at each other rather than at the originals.
       *
       * A copied cloner kept riding the ORIGINAL path and being pushed by the ORIGINAL
       * effectors, and a copied text kept the original path — so editing the copy's own
       * path did nothing and deleting the original broke the copy (QA 2026-09-24,
       * cloner-N4). Links to anything that was not copied with it stay as they are.
       * Part of the same undo step as the copy.
       */
      const relink = (refOf: ReadonlyMap<string, string>, created: Readonly<Record<string, string | undefined>>) => {
        const copied = new Map([...refOf].flatMap(([id, ref]) => (created[ref] ? [[id, created[ref]!] as const] : [])));
        const ops = relinkCopies(scene, copied);
        if (ops.length > 0 && !edit(ops).ok) console.warn('Could not relink the copies');
      };

      /**
       * Pastes what was copied, a little off the original, and selects it. Given a point on
       * the page (Paste here), the copies land with their top-left corner there instead.
       */
      const paste = (from: Clipping[] = clipboard, here?: { x: number; y: number }) => {
        const clipboard = from;
        if (clipboard.length === 0) return;
        const corner = here ? pastedCorner(clipboard) : null;
        const ops: unknown[] = [];
        const refs: string[] = [];
        const refOf = new Map<string, string>();
        clipboard.forEach((clipping, index) => {
          const ref = `paste${index}`;
          refs.push(ref);
          // Worked out on the page and then brought into the parent, so the copy lands
          // ten units off the original whatever it was inside and wherever that sits.
          // A clipping from before `world` existed carries its own transform as the page one.
          const t = (clipping.world ?? (clipping.node.transform as number[] | undefined) ?? [1, 0, 0, 1, 0, 0]) as Matrix;
          const offset: Matrix = here && corner
            ? [t[0], t[1], t[2], t[3], t[4] - corner.x + here.x, t[5] - corner.y + here.y]
            : [t[0], t[1], t[2], t[3], t[4] + 10, t[5] + 10];
          const { parent: parentId, world } = pasteParent(clipping, offset, here ? undefined : t);
          const inverse = parentId === ROOT_ID ? null : invertMatrix(scene.worldTransform(parentId));
          const local = inverse ? multiplyMatrix(inverse, world) : world;
          ops.push({
            op: 'create',
            ref,
            parentId,
            node: { ...clipping.node, transform: [...local] },
          });
          // Each child is created under the copy of its own parent, which exists
          // already because the subtree was listed parents first.
          const byOriginal = new Map<string, string>([[clipping.id, ref]]);
          refOf.set(clipping.id, ref);
          clipping.children.forEach((child, childIndex) => {
            const childRef = `${ref}_${childIndex}`;
            byOriginal.set(child.id, childRef);
            refOf.set(child.id, childRef);
            ops.push({ op: 'create', ref: childRef, parentId: `$${byOriginal.get(child.parent) ?? ref}`, node: child.node });
          });
        });
        const result = edit(ops);
        if (!result.ok) {
          console.warn('Pasting failed', result.errors);
          options.onNotice?.('That could not be pasted here.');
          return;
        }
        relink(refOf, result.created);
        setSelection(refs.map((ref) => result.created[ref]!).filter(Boolean));
        undo.stopCapturing();
      };

      /** Where the copied layers start on the page: the smallest of their own corners. */
      const pastedCorner = (from: readonly Clipping[]) => {
        let x = Infinity;
        let y = Infinity;
        for (const clipping of from) {
          const t = (clipping.world ?? (clipping.node.transform as number[] | undefined) ?? [1, 0, 0, 1, 0, 0]) as Matrix;
          x = Math.min(x, t[4]);
          y = Math.min(y, t[5]);
        }
        return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
      };

      /**
       * Copies a node and everything inside it. A group whose children were left
       * behind is an empty box, so the subtree is copied with it, each child pointing
       * at the copy of its parent by the ref it was created under.
       */
      const copyTree = (id: string, parent: string, ref: string, offset: number, ops: unknown[], refOf?: Map<string, string>): void => {
        const node = scene.get(id);
        if (!node) return;
        refOf?.set(id, ref);
        const rest = bare(node);
        const t = node.transform;
        ops.push({
          op: 'create',
          ref,
          parentId: parent,
          // A copy goes straight above its original, not to the top of the stack
          // (QA 2026-09-24, sel-F5). A child is made inside its copied parent, in order.
          ...(parent.startsWith('$') ? {} : { position: { after: id } }),
          node: { ...rest, transform: [t[0], t[1], t[2], t[3], t[4]! + offset, t[5]! + offset] },
        });
        scene.childrenOf(id).forEach((child, index) => copyTree(child, `$${ref}`, `${ref}_${index}`, 0, ops, refOf));
      };

      /** Everything drawn on the page, artboards themselves left out. */
      const selectEverything = () => {
        const scope = groupEditing.current;
        if (scope) {
          setSelection(scene.childrenOf(scope).filter(id => scene.choosable(id)));
          return;
        }
        // What a hand could pick: no hidden or locked layers, no export slices. A locked
        // background in Select All used to make every move and nudge after it refuse.
        setSelection(scene.childrenOf(ROOT_ID).flatMap((id) => (scene.get(id)?.type === 'artboard' ? scene.childrenOf(id) : [id])).filter((id) => scene.choosable(id)));
      };

      /**
       * Copies the selection and selects the copies, answering with their ids.
       *
       * `offset` is how far off the original they land: ten for ⌘D, so the copy can be
       * seen and taken hold of, and none for an option-drag, where the hand is about to
       * carry the copy somewhere itself.
       */
      const duplicate = (offset = 10, settle = true): string[] => {
        if (selection.length === 0) return [];
        const ops: unknown[] = [];
        const refOf = new Map<string, string>();
        const refs = selection.map((id, index) => {
          const ref = `copy${index}`;
          copyTree(id, scene.parentOf(id) ?? ROOT_ID, ref, offset, ops, refOf);
          return ref;
        });
        const result = edit(ops);
        if (!result.ok) {
          console.warn('Duplicating failed', result.errors);
          return [];
        }
        relink(refOf, result.created);
        const made = refs.map((ref) => result.created[ref]!).filter(Boolean);
        setSelection(made);
        if (settle) undo.stopCapturing();
        return made;
      };

      const copyBeside = (id: string, props: Record<string, unknown>): string | null => {
        const box = renderer.bounds.worldGeometryBounds(id);
        if (!scene.get(id) || !box) return null;
        const gap = Math.round(Math.max(16, Math.max(box.width, box.height) * 0.08));
        // Whatever stands on the page or on an artboard, the original included.
        const standing = [...scene.childrenOf(ROOT_ID), ...scene.childrenOf(ROOT_ID).filter(child => scene.get(child)?.type === 'artboard').flatMap(board => scene.childrenOf(board))]
          .filter(other => scene.get(other)?.type !== 'artboard').map(other => renderer.bounds.worldGeometryBounds(other)).filter((other): other is NonNullable<typeof other> => !!other);
        let x = box.x + box.width + gap;
        const inWay = (at: number) => standing.filter(other => at < other.x + other.width && at + box.width > other.x && box.y < other.y + other.height && box.y + box.height > other.y);
        for (let step = 0, hit = inWay(x); step < 64 && hit.length; step++, hit = inWay(x)) x = Math.max(...hit.map(other => other.x + other.width + gap));
        const ops: unknown[] = [];
        const refOf = new Map<string, string>();
        copyTree(id, scene.parentOf(id) ?? ROOT_ID, 'beside', 0, ops, refOf);
        const first = ops[0] as { node: Record<string, unknown> };
        first.node = { ...first.node, ...props };
        undo.stopCapturing();
        const result = edit(ops);
        if (!result.ok) { console.warn('Could not place the copy', result.errors); return null; }
        const made = result.created.beside;
        if (!made) return null;
        relink(refOf, result.created);
        // Moved in page terms (an artboard's own transform does not bend the step), then
        // into whichever artboard it now stands on, or none.
        if (edit([{ op: 'move', id: made, parentId: ROOT_ID, position: 'front', keepWorldTransform: true }]).ok) {
          const t = scene.get(made)!.transform;
          edit([{ op: 'set', id: made, props: { transform: [t[0], t[1], t[2], t[3], t[4]! + (x - box.x), t[5]!] } }]);
          settleIntoArtboards([made]);
        }
        undo.stopCapturing();
        setSelection([made]);
        return made;
      };

      /**
       * Puts the chosen shapes in a group. They keep their place on the canvas — the
       * group is a container, not a move — and the group lands where the frontmost of
       * them was, so the stack does not jump.
       */
      const group = () => {
        if (selection.length < 2) return;
        const order = scene.paintOrder();
        // Topmost and unlocked: grouping a group together with its own children left an
        // empty group behind and could lift the art off its artboard.
        const chosen = deletable(selection).sort((a, b) => order.indexOf(a) - order.indexOf(b));
        if (chosen.length < 2) return;
        const parents = new Set(chosen.map((id) => scene.parentOf(id) ?? ROOT_ID));
        // The frontmost layer's own parent — its artboard — when they come from several.
        // They used to be lifted onto the page and off their artboards
        // (QA 2026-09-24, menus-shortcuts-quickbar-F10).
        const parent = parents.size === 1 ? [...parents][0]! : (scene.parentOf(chosen[chosen.length - 1]!) ?? ROOT_ID);
        const result = edit([
          // In the frontmost member's slot, as the comment above says — it used to land on
          // top of everything (QA 2026-09-24, sel-F5).
          { op: 'create', ref: 'group', parentId: parent, position: { before: chosen[chosen.length - 1]! }, node: { type: 'group', name: 'Group' } },
          ...chosen.map((id) => ({ op: 'move', id, parentId: '$group', position: 'front', keepWorldTransform: true })),
        ]);
        if (!result.ok) return;
        setSelection([result.created.group!]);
        undo.stopCapturing();
      };

      /** Takes a group apart, leaving its children where they were. */
      const ungroup = () => {
        const groups = scene.topmost(selection).filter((id) => {
          const node = scene.get(id);
          // A blend comes apart here too, and taking it apart is exactly Release: its
          // ends come back as objects and the steps between them stop existing.
          return node?.type === 'group' || node?.type === 'boolean' || node?.type === 'blend' || node?.type === 'cloner';
        });
        if (groups.length === 0) return;
        const freed: string[] = [];
        const ops: unknown[] = [];
        const made: string[] = [];
        for (const [at, id] of groups.entries()) {
          // A cloner comes apart into what it DRAWS — every copy a layer of its own, where
          // the arrangement and the effectors put it — not into the few sources it was
          // copying. Handing back the sources threw the whole arrangement away.
          if (scene.get(id)?.type === 'cloner') {
            const expansion = expandable(scene, id, 'parent', `u${at}_`);
            if (expansion.dropped > 0) {
              // Not cut short: the copies past the ceiling would be deleted with the cloner.
              options.onNotice?.(tooManyCopies(expansion, 'be ungrouped'));
              continue;
            }
            if (expansion.ops.length > 0) {
              // With the effectors only it named, which push nothing now; what it held
              // lives on in the copies (QA 2026-09-24, cloner-F16).
              const leftBehind = effectorsLeftBehind(scene, [id], scene.childrenOf(id));
              ops.push(...expansion.ops, { op: 'delete', id }, ...leftBehind.map((each) => ({ op: 'delete', id: each })));
              made.push(...expansion.made);
              continue;
            }
            // Nothing to expand (a path mode with no path, say): hand back what it was
            // copying rather than deleting the cloner with the source inside it
            // (QA 2026-09-24, cloner-F1). Falls through to the plain release below.
          }
          const parent = scene.parentOf(id) ?? ROOT_ID;
          for (const child of scene.childrenOf(id)) {
            // force: a locked child keeps its place on the page and stays locked; one used
            // to make the whole Ungroup fail (QA 2026-09-24, sel-F10).
            // Into the group's own slot, in their order — not to the top of the stack.
            ops.push({ op: 'move', id: child, parentId: parent, position: { before: id }, keepWorldTransform: true, force: true });
            freed.push(child);
          }
          ops.push({ op: 'delete', id });
        }
        if (ops.length === 0) return;
        const result = edit(ops);
        if (!result.ok) return;
        setSelection([...freed, ...made.map((ref) => result.created[ref]).filter((one): one is string => Boolean(one))]);
        undo.stopCapturing();
      };

      /**
       * An SVG file brought in as one group of editable layers, where a picture would land:
       * centred on the drop (or the middle of the view), inside the artboard under that
       * point, shrunk to fit the view if it is bigger, and fanned out a little from the SVGs
       * dropped with it. One undo step. Answers the new group's id, or null when the batch
       * was refused; throws when the file cannot be read as an SVG at all.
       *
       * Read twice: once for its size and the pictures it holds, then — with those
       * pictures uploaded — for the batch, placed. The engine does the reading; what needs
       * a browser (fetching and uploading the pictures) is done here, in between.
       */
      const placeVector = async (file: File, at: { x: number; y: number } | undefined, index: number): Promise<string | null> => {
        checkImageSize(file);
        const source = await file.text();
        const name = svgLayerName(file);
        const first = svgToArtwork(source, { name });

        const assets = new Map<string, string>();
        if (first.pictures.length > 0) {
          began(first.pictures.length);
          let settled: PromiseSettledResult<{ href: string; picture: Awaited<ReturnType<typeof uploadPicture>> } | null>[];
          try {
            settled = await Promise.allSettled(
              first.pictures.map(async (href, n) => {
                const bytes = await pictureFile(href, n);
                return bytes ? { href, picture: await uploadPicture(open.documentId, bytes) } : null;
              }),
            );
          } finally {
            ended(first.pictures.length);
          }
          if (disposed) return null;
          const uploaded = settled.flatMap((one) => (one.status === 'fulfilled' && one.value ? [one.value] : []));
          const downsize = downsizingNotice(uploaded.map(one => one.picture));
          if (downsize) options.onNotice?.(downsize);
          open.root.transact(() => {
            for (const { href, picture } of uploaded) {
              assets.set(href, picture.mediaAssetId);
              assetsMap(open.root).set(picture.mediaAssetId, {
                kind: 'image',
                name: picture.name,
                mediaAssetId: picture.mediaAssetId,
                src: picture.url,
                mime: picture.mime,
                width: picture.width,
                height: picture.height,
              } as never);
            }
          });
        }

        const { width: viewWidth, height: viewHeight } = viewSize();
        const middle = at ?? { x: camera.x + viewWidth / 2 / camera.zoom, y: camera.y + viewHeight / 2 / camera.zoom };
        const step = 24 / camera.zoom;
        const centre = { x: middle.x + index * step, y: middle.y + index * step };
        const parent = renderer.unitsAt(centre.x, centre.y).find((id) => scene.get(id)?.type === 'artboard');
        const parentOrigin = parent ? scene.worldTransform(parent) : [1, 0, 0, 1, 0, 0];
        const scale = fitScale(first.width, first.height, { width: viewWidth, height: viewHeight }, camera.zoom);
        const read = svgToArtwork(source, {
          name,
          scale,
          parentId: parent ?? ROOT_ID,
          origin: { x: centre.x - (first.width * scale) / 2 - parentOrigin[4]!, y: centre.y - (first.height * scale) / 2 - parentOrigin[5]! },
          picture: (href) => assets.get(href),
        });

        // One step of its own, however long the pictures took to go up.
        undo.stopCapturing();
        const result = edit(read.ops);
        if (!result.ok) {
          console.warn('The SVG’s layers were refused', result.errors);
          return null;
        }
        // Text is measured by the shaper, which the engine does not have: until it is,
        // a text layer has no size to select or snap to.
        for (const ref of read.texts) {
          const id = result.created[ref];
          if (id) refitText(id);
        }
        undo.stopCapturing();
        const notice = svgNotice(read.notes);
        if (notice) options.onNotice?.(notice);
        return result.created[read.ref] ?? null;
      };

      /** The page a drag is over — the one what is carried will land on — outlined while it is. */
      const aimAt = (board: string | null) => {
        if (board === aimedBoard) return;
        aimedBoard = board;
        needsFrame = true;
      };
      const onDragOver = (e: DragEvent) => {
        const types = e.dataTransfer?.types ?? [];
        if (!types.includes('Files') && !isCarried(types)) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        const world = toWorld(e.clientX, e.clientY);
        aimAt(artboardAt(world.x, world.y));
      };
      const onDragLeave = () => aimAt(null);
      /**
       * Every picture dropped, not only the first, fanned out a little so they do not land
       * exactly on top of each other. The drop is always taken: a PDF left to the browser
       * used to replace the editor with the file (QA 2026-09-24, files-doc-F6).
       */
      const onDrop = (e: DragEvent) => {
        e.preventDefault();
        aimAt(null);
        // Taken and put down: a viewer's drop is not for the file, and not for the browser either.
        if (readOnly()) return;
        // Something dragged out of Assets: it places itself, where it was let go.
        if (isCarried(e.dataTransfer?.types ?? [])) {
          dropCarried(toWorld(e.clientX, e.clientY));
          return;
        }
        const dropped = [...(e.dataTransfer?.files ?? [])];
        const pictures = dropped.filter((item) => item.type.startsWith('image/') || isSvgFile(item) || isClipFile(item));
        // One undo step for the whole drop, and a word for whatever was not a picture
        // rather than a silent miss (QA 2026-09-24, files-doc-F12).
        const skipped = dropped.length - pictures.length;
        if (skipped > 0) options.onNotice?.(skipped === 1 ? 'One file wasn’t a picture or a video and was left out.' : `${skipped} files weren’t pictures or videos and were left out.`);
        if (pictures.length === 0) return;
        const at = toWorld(e.clientX, e.clientY);
        // One picture dropped ON the image that is chosen goes into it, rather than on top
        // of it: choosing a picture and dropping another over it is asking for a swap.
        const over = pictures.length === 1 && !isSvgFile(pictures[0]!) && !isClipFile(pictures[0]!) ? renderer.unitsAt(at.x, at.y).find((id) => selection.includes(id) && scene.get(id)?.type === 'image') : undefined;
        if (over) void handleRef.current.replacePicture([over], pictures[0]!);
        else void handleRef.current.placePicture(pictures, at);
      };
      /** Files dropped anywhere else in the editor — the toolbar, a panel — are refused, not opened. */
      const refuseStrayDrop = (e: DragEvent) => {
        if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
      };
      window.addEventListener('dragover', refuseStrayDrop);
      window.addEventListener('drop', refuseStrayDrop);
      cleanups.push(() => {
        window.removeEventListener('dragover', refuseStrayDrop);
        window.removeEventListener('drop', refuseStrayDrop);
      });
      /**
       * A paste: a picture if one was copied, otherwise shapes.
       *
       * The browser's own event rather than the ⌘V key, because this one arrives with the
       * clipboard's text already on it — reading the clipboard any other way asks the
       * person for permission, and nobody expects to be asked to paste. It is what lets a
       * shape copied in one file be pasted into another.
       */
      const onPaste = (e: ClipboardEvent) => {
        // A paste into a field is the field's, picture or not.
        if (keyBelongsToField(e.target, 'v')) return;
        if (readOnly()) return;
        const pictures = [...(e.clipboardData?.files ?? [])].filter((item) => item.type.startsWith('image/'));
        if (pictures.length > 0) {
          e.preventDefault();
          // This paste is the picture; ⌘V's fallback must not paste the shapes copied
          // in this tab a moment later as well.
          pasteWanted = false;
          void handleRef.current.placePicture(pictures);
          return;
        }
        // SVG code copied out of Figma, Illustrator or a text editor arrives as text (some
        // apps also offer it as image/svg+xml). It goes the way a dropped .svg file does —
        // in as layers, or as a picture if it cannot be read — rather than being taken for
        // shapes copied here and pasting whatever this tab copied last.
        const svg = [e.clipboardData?.getData('image/svg+xml') ?? '', e.clipboardData?.getData('text/plain') ?? ''].find(looksLikeSvg);
        if (svg) {
          e.preventDefault();
          pasteWanted = false;
          void handleRef.current.placePicture(new File([svg], PASTED_SVG, { type: 'image/svg+xml' }));
          return;
        }
        if (!handleRef.current.pasteText(e.clipboardData?.getData('text/plain') ?? '')) return;
        e.preventDefault();
      };
      canvas.addEventListener('dragover', onDragOver);
      canvas.addEventListener('dragleave', onDragLeave);
      canvas.addEventListener('drop', onDrop);
      window.addEventListener('paste', onPaste);
      cleanups.push(() => {
        canvas.removeEventListener('dragover', onDragOver);
        canvas.removeEventListener('dragleave', onDragLeave);
        canvas.removeEventListener('drop', onDrop);
        window.removeEventListener('paste', onPaste);
      });

      const openContextMenu = (at: { x: number; y: number }) => {
        if (!options.onQuickMenu || readOnly()) return;
        const world = toWorld(at.x, at.y);
        // Point editing owns context clicks before group/layer hit testing can replace
        // its selection. In particular, curve handles can sit outside the layer's bounds.
        if (options.toolRef.current === 'select' && pathEditing.contextSelect(world)) {
          options.onRailChanged?.();
          needsFrame = true;
          options.onQuickMenu(at);
          return;
        }
        if (groupEditing.current) groupEditing.retreat(world, 12 / camera.zoom);
        const hit = hitTest(world);
        const artboardId = contextArtboardAt(scene, world);
        // A context click inside the selected frame belongs to that selection, even
        // between its members or over transparent pixels. Ordinary clicks still hit-test.
        const selectedFrame = handleRef.current.selectionPlace()?.box;
        const insideSelection = !!selectedFrame && at.x >= selectedFrame.left && at.x <= selectedFrame.right && at.y >= selectedFrame.top && at.y <= selectedFrame.bottom;
        setSelection(contextSelection(selection, hit, (ancestor, child) =>
          scene.isAncestor(ancestor, child) && drawsAt(scene, child, world, touchReach(HIT_SLOP) / camera.zoom), insideSelection, artboardId));
        options.onQuickMenu({ ...at, artboardId });
      };
      const onContextMenu = (e: MouseEvent) => {
        if (!options.onQuickMenu) return;
        e.preventDefault();
        // A finger's long-press is the gestures' own (onHold), on every phone alike; Android's
        // contextmenu for the same press would open the menu a second time.
        if ((e as PointerEvent).pointerType === 'touch') return;
        if (readOnly()) return;
        openContextMenu({ x: e.clientX, y: e.clientY });
      };
      canvas.addEventListener('contextmenu', onContextMenu);
      cleanups.push(() => canvas.removeEventListener('contextmenu', onContextMenu));

      // The hand's own gestures, which sit above the tools.
      cleanups.push(
        watchGestures(canvas, {
          onUndo: () => undo.undo(),
          onRedo: () => undo.redo(),
          onFullScreen: () => options.onFullScreen?.(),
          onFit: () => setCamera(fitBox(contentBounds())),
          onMenu: openContextMenu,
          // Only where a right-click would mean the menu: the arrow in hand, not a pen mid-path.
          onHold: (at) => {
            if (options.toolRef.current === 'select' && !readOnly()) openContextMenu(at);
          },
          onZoom: (factor, at) => {
            const box = canvas.getBoundingClientRect();
            setCamera(zoomAt(camera, at.x - box.left, at.y - box.top, factor));
          },
          onPan: (dx, dy) => setCamera(panBy(camera, dx, dy)),
          onMultiTouch: (active) => {
            handOnCanvas = active;
            // A tool half-way through a drag lets go rather than finishing it wrong. One
            // that can abandon a stroke does, so a pinch or a two-finger undo never saves
            // the half-drawn line first (QA 2026-09-25, pencil-fill-brush-F8).
            if (active) {
              if (current.onPointerCancel) {
                toolPressed = false;
                current.onPointerCancel(context);
              } else current.deactivate?.(context);
            } else if (!current.onPointerCancel) current.activate?.(context);
          },
        }),
      );

      /**
       * A press anywhere in the chrome — a panel, a menu, a slider — starts a new step too.
       * Panel commands close their step after they edit, not before, so an edit left open
       * by the canvas (a text just typed) used to be swept into the first panel command
       * after it: one Cmd+Z then took back the Animation Style AND the text.
       */
      const onChromePress = (e: PointerEvent) => {
        if (e.target === canvas || current.holdsStep?.()) return;
        undo.stopCapturing();
      };
      document.addEventListener('pointerdown', onChromePress, true);
      cleanups.push(() => document.removeEventListener('pointerdown', onChromePress, true));

      // A finger's Shift and Option are the chips by the zoom pill, and its reach is wider (touchInput.ts):
      // every press reaches the tools through these, so no tool needs to know about either.
      const doubleWithKeys = (e: MouseEvent) => onDoubleClick(withHeldKeys(e));
      const downWithKeys = (e: PointerEvent) => {
        noteTouch(e);
        onPointerDown(withHeldKeys(e));
      };
      const moveWithKeys = (e: PointerEvent) => {
        noteTouch(e);
        onPointerMove(withHeldKeys(e));
      };
      const upWithKeys = (e: PointerEvent) => onPointerUp(withHeldKeys(e));
      canvas.addEventListener('dblclick', doubleWithKeys);
      canvas.addEventListener('pointerdown', downWithKeys);
      canvas.addEventListener('pointermove', moveWithKeys);
      canvas.addEventListener('pointerleave', onPointerLeave);
      window.addEventListener('pointerup', upWithKeys);
      // A press the system takes away (an iPad gesture, a palm) never sends a pointerup.
      // The tool is ended the way a second finger ends it — its deactivate settles what
      // moved and closes the step — and `pressed` does not stay true. Not handed on as a
      // release: a cancel's coordinates can be 0,0, and a release there would draw or
      // drop a guide at the corner (QA 2026-09-24, sel-F23).
      const onPointerCancel = () => {
        panning = null;
        if (pressed) {
          pressed = false;
          dragging = false;
          viewChanged();
        }
        litLines = [];
        litGuides = [];
        if (holding) {
          holding = null;
          undo.stopCapturing();
        }
        if (toolPressed) {
          toolPressed = false;
          // A tool that can abandon what the press was making does: the draw tool's
          // deactivate KEEPS a stroke, which is right for a tool change and wrong for a
          // press the system took away (QA 2026-09-25, pencil-fill-brush-F14).
          if (current.onPointerCancel) current.onPointerCancel(context);
          else {
            current.deactivate?.(context);
            current.activate?.(context);
          }
        }
        needsFrame = true;
      };
      canvas.addEventListener('pointercancel', onPointerCancel);
      canvas.addEventListener('wheel', onWheel, { passive: false });
      window.addEventListener('keydown', onKeyDown);
      window.addEventListener('keyup', onKeyUp);
      cleanups.push(() => canvas.removeEventListener('pointercancel', onPointerCancel));
      /**
       * Keys let go of in another app are never heard here. Space held while switching away
       * left every drag panning until Space was tapped again (QA 2026-09-24,
       * view-snapping-F12), so leaving the window lets go of everything held.
       */
      const letGoOfKeys = () => {
        if (document.visibilityState === 'visible' && document.hasFocus()) return;
        spaceHeld = false;
        panning = null;
        modifiers = { ctrl: false, shift: false, alt: false, meta: false };
        needsFrame = true;
      };
      /**
       * A panel field committed while text is being typed (a font size and Return) lets go
       * of the keyboard to the page. Backspace and the arrows still reached the text through
       * the canvas's key handler, but letters went on to the tool shortcuts — the text was
       * left half in edit mode (QA 2026-09-24, inspector-numbers-F14). When a field hands
       * the keyboard to nobody, it goes back to the text.
       */
      const backToText = (event: FocusEvent) => {
        if (!editing?.current || event.relatedTarget) return;
        const left = event.target as HTMLElement | null;
        if (!left || !['INPUT', 'TEXTAREA', 'SELECT'].includes(left.tagName) || left.getAttribute('aria-label') === 'Text') return;
        window.setTimeout(() => {
          const active = document.activeElement;
          if (editing?.current && (!active || active === document.body)) editing.refocus();
        }, 0);
      };
      document.addEventListener('focusout', backToText);
      cleanups.push(() => document.removeEventListener('focusout', backToText));
      window.addEventListener('blur', letGoOfKeys);
      document.addEventListener('visibilitychange', letGoOfKeys);
      cleanups.push(() => {
        window.removeEventListener('blur', letGoOfKeys);
        document.removeEventListener('visibilitychange', letGoOfKeys);
      });
      cleanups.push(() => {
        canvas.removeEventListener('dblclick', doubleWithKeys);
        canvas.removeEventListener('pointerdown', downWithKeys);
        canvas.removeEventListener('pointermove', moveWithKeys);
        canvas.removeEventListener('pointerleave', onPointerLeave);
        window.removeEventListener('pointerup', upWithKeys);
        canvas.removeEventListener('wheel', onWheel);
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('keyup', onKeyUp);
      });

      /** The artboard an export means: the one holding the selection, else the first. */
      /**
       * What is playing, if anything.
       *
       * Held here rather than in React because the frame loop reads it sixty times a
       * second and a ref change is neither a render nor an event. The chrome is told
       * when it stops through the callback it handed over.
       */
      let playing: { id: string; from: number; length: number; onStop: (finished: boolean) => void; held?: number; drawn?: boolean } | null = null;
      /**
       * A move being tried on one layer (`preview`): played round and round with a beat of
       * rest between showings, and never while a page plays.
       */
      let trying: { from: number; length: number; at: (seconds: number) => Poses } | null = null;
      /** The rest between two showings of a move being tried. */
      const TRY_REST = 0.45;
      /**
       * Drawing a motion path: which layer it is for, and what the hand has done so far.
       *
       * Armed from the panel and disarmed by the drag itself, so it is a mode that lasts
       * exactly one gesture. A mode you have to remember to leave is a mode that will
       * eat somebody's next click.
       */
      let drawing: { id: string; from: { x: number; y: number }; points: { x: number; y: number }[]; onDone: (ok: boolean) => void } | null = null;
      /**
       * Where the playhead was left when nothing plays: the page is shown as stored, and
       * playing starts from here. It used to be forgotten the moment a playing stopped —
       * so a pause was lost to the next click on the page, or to the pointer passing over
       * a move on its way to the Play button (Tarek, 2026-09-30).
       */
      let resting: { id: string; seconds: number } | null = null;
      const stopPlaying = (finished = false) => {
        if (!playing) return;
        const { onStop } = playing;
        resting = { id: playing.id, seconds: finished ? playing.length : (playing.held ?? Math.min(playing.length, (performance.now() - playing.from) / 1000)) };
        playing = null;
        needsFrame = true;
        onStop(finished);
      };
      /**
       * Disarms a Draw the Path that is waiting for its drag. Esc, another tool, pressing
       * the button again and closing the panel all call it: armed, it took the next drag
       * made with ANY tool as the earlier layer's path (QA 2026-09-24, anim-F6).
       */
      const cancelDrawing = () => {
        if (!drawing) return;
        const { onDone } = drawing;
        drawing = null;
        needsFrame = true;
        onDone(false);
      };

      /**
       * What every SVG this canvas writes is written with.
       *
       * Pictures leave as links to where their bytes live, so an exported file shows the
       * drawing rather than a hole where a photograph was. Text leaves broken where the
       * shaper broke it on screen, rather than where the typing did. And the canvas's own
       * renderer answers what the document alone cannot — a boolean's outline, a stroke
       * set inside its edge, a picture of a shader — so the file is what the page shows.
       */
      const svgShared = (pictureScale?: number) => {
        const assetUrl = (assetId: string) => (assetsMap(open.root).get(assetId) as Asset | undefined)?.src ?? null;
        const textLines = text ? (node: Parameters<typeof text.lines>[0]) => text.lines(node) : undefined;
        // A CMYK file's SVG carries its colours as they will print, as the canvas shows them.
        const colour = proofer ? proofer.proof.bind(proofer) : undefined;
        // Pictures of layers on the canvas's GPU: the pictures it shows are textures, which draw to no raster surface.
        return { assetUrl, tools: exportTools(ck, renderer, scene, { surface: (width, height) => gl.offscreen(width, height) }), ...(pictureScale ? { pictureScale } : {}), ...(textLines ? { textLines } : {}), ...(colour ? { colour } : {}) };
      };

      /**
       * Where something added from the library lands: the middle of the page being worked
       * on, in that page's own space — or the middle of the view when there is no page.
       * Each one lands a little down and right of the last, so three shapes added in a row
       * are three shapes to see rather than one.
       */
      let landed = 0;
      const landing = (dropped?: DropPoint): { parent: string | null; centre: { x: number; y: number }; side: number; page: number; tall: number } => {
        // Dragged and let go: centred where it was let go, on the page under it — which is
        // then the page being worked on. Sized to that page, as a press sizes it to its own.
        if (dropped) {
          const under = artboardAt(dropped.x, dropped.y);
          const box = under ? renderer.bounds.worldGeometryBounds(under) : null;
          if (under && box) {
            activeArtboard.choose(under);
            const origin = scene.worldTransform(under);
            return { parent: under, centre: { x: dropped.x - origin[4]!, y: dropped.y - origin[5]! }, side: Math.min(box.width, box.height), page: box.width, tall: box.height };
          }
          const { width, height } = viewSize();
          return { parent: null, centre: dropped, side: Math.min(width, height) / camera.zoom, page: width / camera.zoom, tall: height / camera.zoom };
        }
        const step = (landed++ % 6) * 0.03;
        const board = exportBoard();
        const box = board ? renderer.bounds.worldGeometryBounds(board) : null;
        if (board && box) {
          const origin = scene.worldTransform(board);
          const side = Math.min(box.width, box.height);
          return { parent: board, centre: { x: box.x - origin[4]! + box.width / 2 + side * step, y: box.y - origin[5]! + box.height / 2 + side * step }, side, page: box.width, tall: box.height };
        }
        const { width, height } = viewSize();
        const side = Math.min(width, height) / camera.zoom;
        return { parent: null, centre: { x: camera.x + width / 2 / camera.zoom + side * step, y: camera.y + height / 2 / camera.zoom + side * step }, side, page: width / camera.zoom, tall: height / camera.zoom };
      };
      /**
       * Before something from the catalogue lands: whatever was being typed or drawn is
       * finished, and the hand goes back to Select — what arrives is there to be picked
       * and changed, which no other tool does.
       */
      const handBack = () => {
        current.deactivate?.(context);
        editing?.stop();
        if (options.toolRef.current !== 'select') options.onToolDone();
      };
      /** One node onto the page, as one undo step, chosen afterwards. Answers its id. */
      const add = (node: Record<string, unknown>, parent: string | null): string | null => {
        current.deactivate?.(context);
        editing?.stop();
        undo.stopCapturing();
        const result = edit([{ op: 'create', ref: 'added', parentId: parent ?? ROOT_ID, node }]);
        const id = result.ok ? (result.created.added ?? null) : null;
        if (id) setSelection([id]);
        undo.stopCapturing();
        return id;
      };

      /** A sketch asked for from a layer smaller than this, in document units, fills the page instead. */
      const SKETCH_LEAST_SIDE = 160;
      /** How big a sketch's own canvas is, along its long side. */
      const SKETCH_LEAST_CANVAS = 1080;
      const SKETCH_MOST_SIDE = 1920;

      /**
       * A sketch from the library on the page: in the place of the layer it was asked for from,
       * just above it, or filling the page when that layer is the page or too small to show one.
       * Its cover is on the canvas at once; the sketch takes over as soon as it has drawn.
       */
      const placeSketch = (sketch: LibrarySketch, targets: readonly string[]): void => {
        const at = landing();
        const chosen = targets.find((id) => scene.get(id) && scene.get(id)!.type !== 'artboard' && !scene.lockedHere(id));
        const box = chosen ? renderer.bounds.worldGeometryBounds(chosen) : null;
        // A sketch that reads another layer (Letter Boxes' "Text") draws around it and past it, as
        // on Lumens' canvas: it gets the whole page, not the box of what was selected.
        const readsLayer = sketch.controls.some(control => control.type === 'shape');
        const fits = !readsLayer && !!box && Math.min(box.width, box.height) >= SKETCH_LEAST_SIDE;
        const parent = fits ? (scene.parentOf(chosen!) ?? ROOT_ID) : (at.parent ?? ROOT_ID);
        const width = Math.round(fits ? box!.width : at.parent ? at.page : at.side);
        const height = Math.round(fits ? box!.height : at.parent ? at.tall : at.side);
        const origin = parent === ROOT_ID ? [1, 0, 0, 1, 0, 0] : scene.worldTransform(parent);
        const x = fits ? box!.x - origin[4]! : at.parent ? 0 : at.centre.x - width / 2;
        const y = fits ? box!.y - origin[5]! : at.parent ? 0 : at.centre.y - height / 2;
        // What the code sees as p.width: the size it is written for, whatever the layer's own.
        const long = Math.max(width, height);
        const scale = Math.min(SKETCH_MOST_SIDE, Math.max(SKETCH_LEAST_CANVAS, long)) / long;
        const assetId = newId();
        open.root.transact(() => assetsMap(open.root).set(assetId, {
          kind: 'sketch', name: sketch.name, width: Math.round(width * scale), height: Math.round(height * scale),
          ...(sketch.cover ? { src: sketch.cover } : {}),
          sketch: { code: sketch.code, controls: sketch.controls, origin: sketch.origin, ...(sketch.presets ? { presets: sketch.presets } : {}), ...(sketch.pictures ? { pictures: sketch.pictures } : {}) },
        } as never));
        current.deactivate?.(context);
        editing?.stop();
        undo.stopCapturing();
        const result = edit([{
          op: 'create', ref: 'sketch', parentId: parent, ...(fits ? { position: { after: chosen! } } : {}),
          node: {
            type: 'image', name: sketch.name, assetId, width, height, fit: 'crop',
            clip: { duration: SKETCH_DEFAULT_SECONDS }, sketch: { seed: sketch.seed, values: sketch.values },
            transform: [1, 0, 0, 1, x, y],
          },
        }]);
        if (!result.ok) throw Error('Could not add this sketch.');
        if (result.created.sketch) setSelection([result.created.sketch]);
        undo.stopCapturing();
      };

      const exportBoard = (): string | null => activeArtboard.resolve(selection, () => {
        // Use the view only to initialise context (or recover after board deletion).
        // Re-evaluating it on every zoom made Properties jump between nearby boards.
        const all = scene.childrenOf(ROOT_ID).filter((id) => scene.get(id)?.type === 'artboard');
        const size = viewSize();
        const middle = { x: camera.x + size.width / 2 / camera.zoom, y: camera.y + size.height / 2 / camera.zoom };
        let nearest: string | null = null;
        let best = Infinity;
        for (const id of all) {
          const box = renderer.bounds.worldGeometryBounds(id);
          if (!box) continue;
          const dx = Math.max(box.x - middle.x, 0, middle.x - (box.x + box.width));
          const dy = Math.max(box.y - middle.y, 0, middle.y - (box.y + box.height));
          const away = Math.hypot(dx, dy);
          if (away < best) {
            best = away;
            nearest = id;
          }
        }
        return nearest ?? all[0] ?? null;
      });

      /** Every artboard the selection touches: chosen itself, or holding something chosen. */
      /**
       * What "export the selection" means: the layers chosen, each once (a group, not the
       * group and its children again), without the artboards — those are exported as
       * artboards — and without what is never drawn or is switched off.
       */
      const exportLayerIds = (): string[] =>
        scene.topmost(selection).filter((id) => {
          const node = scene.get(id);
          return !!node && node.type !== 'artboard' && node.type !== 'slice' && node.type !== 'effector' && node.visible !== false;
        });
      /** The box those layers draw in, shadows and strokes included. */
      const exportLayersBox = (ids: readonly string[]): Rect | null => {
        const boxes = ids.map((id) => renderer.bounds.worldVisualBounds(id)).filter((box): box is Rect => !!box && box.width > 0 && box.height > 0);
        if (boxes.length === 0) return null;
        const left = Math.floor(Math.min(...boxes.map((box) => box.x)));
        const top = Math.floor(Math.min(...boxes.map((box) => box.y)));
        return { x: left, y: top, width: Math.ceil(Math.max(...boxes.map((box) => box.x + box.width))) - left, height: Math.ceil(Math.max(...boxes.map((box) => box.y + box.height))) - top };
      };
      const chosenBoards = (): string[] => {
        const out: string[] = [];
        for (const id of selection) {
          let at: string | undefined = id;
          while (at) {
            if (scene.get(at)?.type === 'artboard') {
              if (!out.includes(at)) out.push(at);
              break;
            }
            at = scene.parentOf(at);
          }
        }
        return out;
      };

      // ── add-ons ──────────────────────────────────────────────────────────
      /**
       * A copy of a node and everything under it, as ops that make it again.
       *
       * Read from the scene rather than from the snapshot, because a faithful copy needs
       * every stored field and the snapshot is pruned so that it can be small. The root
       * copy takes the ref it was given; its descendants take refs named after it.
       */
      const cloneOps: AddonBridge['host']['cloneOps'] = ({ sourceId, ref, parent, position, overrides, nextRef }) => {
        const ops: unknown[] = [];
        const walk = (id: string, as: string, into: string, place: unknown, extra: Readonly<Record<string, unknown>>) => {
          const node = scene.get(id);
          if (!node) return;
          const { id: _id, parentId: _parent, order: _order, ...fields } = node as NodeRecord & Record<string, unknown>;
          // Only overrides this kind of node can hold. A path has no width and a group no
          // height; passing them through would have the engine refuse the whole batch, so
          // a grid of copies would fail the moment somebody selected a path.
          const allowed = NODE_KEYS[node.type];
          const kept = Object.fromEntries(Object.entries(extra).filter(([key]) => allowed.has(key)));
          ops.push({ op: 'create', ref: as, parentId: into, position: place, node: { ...fields, ...kept } });
          for (const child of scene.childrenOf(id)) walk(child, nextRef(), `$${as}`, 'front', {});
        };
        walk(sourceId, ref, parent, position, overrides);
        return ops;
      };

      /**
       * How big some text will be, shaped by the thing that shapes the document's text.
       *
       * A throwaway text node, parsed through the schema so every default is the
       * schema's, measured by the same paragraph builder that draws the page. A family
       * the document does not carry falls back to the editor's own, as a text layer does.
       */
      const measureText: AddonBridge['host']['measureText'] = (string, style) => {
        const size = style?.size ?? 16;
        if (!text) return { width: string.length * size * 0.55, height: size * 1.2 };
        const carried = style?.family && fontsMap(open.root).has(style.family) ? style.family : DEFAULT_FAMILY;
        const node = {
          ...TextSchema.parse({
            type: 'text',
            parentId: ROOT_ID,
            order: 'a0',
            runs: [{ text: string, style: {} }],
            sizing: 'auto-width',
            style: { fontFamily: carried, fontSize: size, fontWeight: style?.weight ?? 400, fontStyle: style?.italic ? 'italic' : 'normal' },
          }),
          id: 'measuring000',
        };
        const measured = text.measure(node);
        return { width: measured.longestLine || measured.width, height: measured.height };
      };

      const addonBridge: AddonBridge = {
        documentId: open.documentId,
        snapshot: (reads) =>
          buildSnapshot({
            scene,
            root: open.root,
            reads,
            selection,
            artboardId: exportBoard(),
            boundsOf: (id) => renderer.bounds.worldGeometryBounds(id),
          }),
        host: { place: (at) => place(at), measureText, cloneOps },
        apply: (out, meta) => {
          if (out.ops.length === 0) return { ok: true, changed: 0, selected: [], runId: null };
          const runId = crypto.randomUUID();
          // Both sides: the capture window is a day long, and without the opening call
          // the run merges into whatever was done before it — so taking the add-on back
          // would take the person's own work with it.
          undo.stopCapturing();
          // What the add-on made on the page joins the artboard it sits on, as anything drawn
          // there would — inside the same run, so undo takes both together
          // (QA 2026-09-24, library-addons-F4).
          const result = labelled(pageRoom, { origin: 'plugin', runId }, () => {
            const made = edit([...out.ops]);
            if (made.ok) settleIntoArtboards(Object.values(made.created).filter((id) => (scene.parentOf(id) ?? ROOT_ID) === ROOT_ID));
            return made;
          });
          undo.stopCapturing();
          if (!result.ok) {
            const first = result.errors[0];
            return { ok: false, message: first ? first.message : 'the engine refused the change' };
          }
          const selected = out.select
            .map((target) => (target.startsWith('$') ? result.created[target.slice(1)] : target))
            .filter((id): id is string => typeof id === 'string' && scene.has(id));
          if (selected.length > 0) setSelection(selected);
          needsFrame = true;
          void addonsApi
            .recordRun({ pluginId: meta.pluginId, versionHash: meta.versionHash, documentId: open.documentId, runId, ops: out.ops.length, ms: meta.ms })
            .catch((error: unknown) => console.warn('Could not record the add-on run', error));
          return { ok: true, changed: result.changed.length, selected, runId };
        },
      };

      // ── what the chrome can do ───────────────────────────────────────────
      handleRef.current = {
        canvasRef,
        proofer: () => proofer,
        zoomBy: (factor) => {
          const middle = viewCentre();
          setCamera(zoomAt(camera, middle.x, middle.y, factor));
        },
        fit: () => setCamera(fitBox(contentBounds())),
        actualSize: () => setCamera(actualSizeCamera()),
        panBy: (dx, dy) => setCamera(panBy(camera, dx, dy)),
        select: (ids) => setSelection(ids),
        deleteSelection: () => {
          if (selection.length === 0) return;
          const doomed = deletable(selection);
          if (doomed.length > 0) edit(deleteOps(doomed));
          setSelection([]);
        },
        group: () => group(),
        ungroup: () => ungroup(),
        expandBlend: () => blending.expand(context),
        createBlend: () => blending.make(context),
        repeat: () => {
          // A press elsewhere first: what was being typed or drawn is finished.
          handBack();
          const made = wrapInCloner(context);
          needsFrame = true;
          return made;
        },
        addVariation: (clonerId, recipe) => {
          const made = RECIPES.find((one) => one.id === recipe);
          if (made) addVariation(context, clonerId, made, { choose: false });
          needsFrame = true;
        },
        removeVariation: (effectorId) => {
          removeVariation(context, effectorId);
          needsFrame = true;
        },
        makeLayers: (clonerId) => {
          makeLayers(context, clonerId);
          needsFrame = true;
        },
        stopRepeating: (clonerId) => {
          stopRepeating(context, clonerId);
          needsFrame = true;
        },
        repeatSource: (clonerId) => {
          // The source as it stands on the page: a repeat is placed unscaled, so its own size is its size.
          let box: Rect | null = null;
          for (const kid of scene.childrenOf(clonerId)) {
            const one = renderer.bounds.worldGeometryBounds(kid);
            if (!one) continue;
            box = box ? { x: Math.min(box.x, one.x), y: Math.min(box.y, one.y), width: Math.max(box.x + box.width, one.x + one.width) - Math.min(box.x, one.x), height: Math.max(box.y + box.height, one.y + one.height) - Math.min(box.y, one.y) } : one;
          }
          return box ? { width: box.width, height: box.height } : null;
        },
        repeatPaths: (clonerId) => {
          const inside = new Set(scene.childrenOf(clonerId));
          const outlined = new Set(['path', 'rect', 'ellipse', 'polygon', 'star', 'line']);
          // Shapes on the repeat's own page (or, off every page, the bare canvas), nearest
          // first — the one somebody means is nearly always the one beside it.
          const board = artboardOf(clonerId);
          const middle = (id: string) => {
            const box = renderer.bounds.worldGeometryBounds(id);
            return box ? { x: box.x + box.width / 2, y: box.y + box.height / 2 } : null;
          };
          const from = middle(clonerId);
          const away = (id: string) => {
            const to = middle(id);
            return from && to ? Math.hypot(to.x - from.x, to.y - from.y) : Infinity;
          };
          const found = scene
            .paintOrder()
            .filter((id) => id !== clonerId && !inside.has(id) && outlined.has(scene.get(id)?.type ?? '') && artboardOf(id) === board)
            .map((id) => ({ id, away: away(id) }))
            .sort((a, b) => a.away - b.away)
            .slice(0, 30);
          return distinctNames(found.map(({ id }) => ({ id, name: scene.get(id)?.name || 'Shape' })));
        },
        releaseBlend: () => blending.release(context),
        rasterize: async () => {
          const ids = scene.topmost(unlocked(selection)).filter((id) => {
            const type = scene.get(id)?.type;
            return !!type && type !== 'artboard' && type !== 'slice' && type !== 'effector';
          });
          if (ids.length === 0) return;
          // Drawn in the order they are stacked, so the picture is the pile as it looks.
          const order = new Map(scene.paintOrder().map((id, index) => [id, index]));
          ids.sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
          let box: Rect | null = null;
          for (const id of ids) box = union(box, renderer.bounds.worldVisualBounds(id));
          if (!box || box.width <= 0 || box.height <= 0) return;
          // The pictures inside at full size: the canvas may be showing smaller copies.
          const pictures = new Set<string>();
          const gather = (id: string) => {
            const node = scene.get(id) as { assetId?: string; fills?: Paint[]; strokes?: Paint[]; style?: { fills?: Paint[] } } | undefined;
            if (node?.assetId) pictures.add(node.assetId);
            for (const paint of [...(node?.fills ?? []), ...(node?.style?.fills ?? []), ...(node?.strokes ?? [])]) if (paint.type === 'image') pictures.add(paint.assetId);
            for (const child of scene.childrenOf(id)) gather(child);
          };
          for (const id of ids) gather(id);
          await images.originals(pictures);
          // As sharp as an uploaded picture is kept (2,400 pixels on the long side), and never
          // more than four pixels to a unit.
          const scale = Math.min(4, 2400 / Math.max(box.width, box.height));
          const surface = ck.MakeSurface(Math.max(1, Math.ceil(box.width * scale)), Math.max(1, Math.ceil(box.height * scale)));
          if (!surface) return;
          let png: Uint8Array | null = null;
          try {
            const canvas = surface.getCanvas();
            canvas.clear(ck.TRANSPARENT);
            canvas.scale(scale, scale);
            canvas.translate(-box.x, -box.y);
            for (const id of ids) {
              canvas.save();
              const parent = scene.parentOf(id);
              if (parent && parent !== ROOT_ID) canvas.concat(toCanvasKit(scene.worldTransform(parent)));
              renderer.painter.drawNode(canvas, id);
              canvas.restore();
            }
            const image = surface.makeImageSnapshot();
            png = image.encodeToBytes() ?? null;
            image.delete();
          } finally {
            surface.dispose();
          }
          if (!png) return;
          const first = scene.get(ids[ids.length - 1]!);
          const name = ids.length === 1 && first?.name ? first.name : 'Picture';
          began(1);
          let picture: Awaited<ReturnType<typeof uploadPicture>>;
          try {
            picture = await uploadPicture(open.documentId, new File([png as BlobPart], `${name}.png`, { type: 'image/png' }));
          } catch (error) {
            options.onNotice?.(error instanceof Error ? error.message : 'That could not be made into a picture.');
            return;
          } finally {
            ended(1);
          }
          if (disposed) return;
          const downsize = downsizingNotice([picture]);
          if (downsize) options.onNotice?.(downsize);
          open.root.transact(() => {
            assetsMap(open.root).set(picture.mediaAssetId, {
              kind: 'image',
              name: picture.name,
              mediaAssetId: picture.mediaAssetId,
              src: picture.url,
              mime: picture.mime,
              width: picture.width,
              height: picture.height,
            } as never);
          });
          // Where the topmost of them sat: in its parent, just above it — the stack reads the
          // same afterwards. Placed in the parent's own axes, so a turned group keeps it square
          // to the page, as it was drawn.
          const top = ids[ids.length - 1]!;
          const parent = scene.parentOf(top) ?? ROOT_ID;
          const back = parent === ROOT_ID ? null : invert(scene.worldTransform(parent));
          const transform = back ? multiplyMatrix(back, [1, 0, 0, 1, box.x, box.y]) : [1, 0, 0, 1, box.x, box.y];
          undo.stopCapturing();
          const result = edit([
            {
              op: 'create',
              ref: 'raster',
              parentId: parent,
              position: { after: top },
              node: { type: 'image', name, assetId: picture.mediaAssetId, width: box.width, height: box.height, fit: 'fill', transform },
            },
            ...ids.map((id) => ({ op: 'delete', id })),
          ]);
          if (result.ok && result.created.raster) setSelection([result.created.raster]);
          else options.onNotice?.('That could not be made into a picture.');
          undo.stopCapturing();
        },
        outlineText: async () => {
          if (!text) return;
          const ids = scene.topmost(unlocked(selection)).filter((id) => scene.get(id)?.type === 'text');
          if (ids.length === 0) return;
          const faceFor = await glyphFaces();
          if (disposed) return;
          const ops: unknown[] = [];
          const made: string[] = [];
          let missing = 0;
          for (const id of ids) {
            const node = scene.get(id) as (TextNode & { id: string }) | undefined;
            if (!node) continue;
            const shaped = text.shapedGlyphs(node);
            if (!shaped) continue;
            const bound = node.onPath ? scene.get(node.onPath.id) : undefined;
            const sub = bound?.type === 'path' ? bound.path?.subpaths?.[0] : undefined;
            const along = sub && sub.points.length >= 2 ? rulerIn(sub.points, sub.closed, scene.worldTransform(bound!.id), scene.worldTransform(id)) : null;
            const outlined = outlineText(node, shaped, faceFor, along);
            missing += outlined.missing;
            const letters = outlined.words.reduce((sum, word) => sum + word.letters.length, 0) + outlined.bars.length;
            if (letters === 0) continue;
            const name = node.name || textOf(node).slice(0, 40) || 'Text';
            // What the layer wears, kept on what takes its place.
            const kept = {
              name,
              visible: node.visible,
              opacity: node.opacity,
              isMask: node.isMask,
              transform: node.transform,
              ...(node.animation ? { animation: node.animation } : {}),
              ...(node.timing ? { timing: node.timing } : {}),
            };
            const look = { strokes: node.strokes, strokeStyle: node.strokeStyle };
            const pathOf = (piece: OutlinePiece) => ({ type: 'path', name: piece.text, ...look, fills: piece.fills, path: { fillRule: 'nonzero', subpaths: piece.subpaths } });
            const ref = `outline${made.length}`;
            const parentId = scene.parentOf(id) ?? ROOT_ID;
            const only = outlined.words.length === 1 && outlined.bars.length === 0 ? outlined.words[0]! : null;
            if (letters === 1) {
              const piece = outlined.words[0]?.letters[0] ?? outlined.bars[0]!;
              ops.push({ op: 'create', ref, parentId, position: { after: id }, node: { ...pathOf(piece), ...kept, effects: node.effects, blendMode: node.blendMode } });
            } else {
              // A shape a letter, a group a word (Tarek, 2 Nov), all held in one group that wears
              // the layer's effects, so a shadow still falls from the whole line and not from
              // each letter. One word is that group itself. Each is put at the back as it is
              // made, so the Layers list reads in the order the words do.
              ops.push({ op: 'create', ref, parentId, position: { after: id }, node: { type: 'group', ...kept, effects: node.effects, blendMode: node.blendMode === 'normal' ? 'pass-through' : node.blendMode } });
              let n = 0;
              const letter = (piece: OutlinePiece, into: string) => ops.push({ op: 'create', ref: `${ref}_${n++}`, parentId: into, position: 'back', node: pathOf(piece) });
              if (only) only.letters.forEach((piece) => letter(piece, `$${ref}`));
              else {
                for (const word of outlined.words) {
                  const wordRef = `${ref}_${n++}`;
                  ops.push({ op: 'create', ref: wordRef, parentId: `$${ref}`, position: 'back', node: { type: 'group', name: word.text } });
                  word.letters.forEach((piece) => letter(piece, `$${wordRef}`));
                }
                outlined.bars.forEach((piece) => letter(piece, `$${ref}`));
              }
            }
            ops.push({ op: 'delete', id });
            made.push(ref);
          }
          if (made.length === 0) {
            options.onNotice?.('Those letters could not be turned into outlines: their font has not loaded.');
            return;
          }
          undo.stopCapturing();
          const result = edit(ops);
          if (result.ok) setSelection(made.map((ref) => result.created[ref]).filter((id): id is string => !!id));
          else options.onNotice?.('That text could not be turned into outlines.');
          undo.stopCapturing();
          if (missing > 0) options.onNotice?.(`${missing} ${missing === 1 ? 'letter was' : 'letters were'} left out: the font for ${missing === 1 ? 'it' : 'them'} has not loaded.`);
        },
        arrange: (how) => {
          const chosen = selection.filter((id) => scene.get(id) && !scene.get(id)!.locked);
          if (chosen.length === 0) return;
          if (how === 'front' || how === 'back') {
            edit(chosen.map((id) => ({ op: 'move', id, position: how })));
            undo.stopCapturing();
            return;
          }
          // One step: past the nearest neighbour that is not itself being moved, taken
          // front-most first going forward (and back-most first going back) so two chosen
          // layers side by side move together instead of trading places.
          const order = scene.paintOrder();
          const sorted = [...chosen].sort((a, b) => order.indexOf(a) - order.indexOf(b));
          if (how === 'forward') sorted.reverse();
          const ops: unknown[] = [];
          for (const id of sorted) {
            const parent = scene.parentOf(id) ?? ROOT_ID;
            const siblings = scene.childrenOf(parent);
            const at = siblings.indexOf(id);
            const step = how === 'forward' ? 1 : -1;
            let next = at + step;
            while (next >= 0 && next < siblings.length && chosen.includes(siblings[next]!)) next += step;
            const neighbour = siblings[next];
            if (!neighbour) continue;
            ops.push({ op: 'move', id, parentId: parent, position: how === 'forward' ? { after: neighbour } : { before: neighbour } });
          }
          if (ops.length > 0 && edit(ops).ok) undo.stopCapturing();
        },
        flip: (axis) => {
          const ops: unknown[] = [];
          for (const id of selection) {
            const node = scene.get(id);
            if (!node || node.locked || node.type === 'artboard') continue;
            const box = renderer.bounds.worldGeometryBounds(id);
            if (!box) continue;
            const cx = box.x + box.width / 2;
            const cy = box.y + box.height / 2;
            const mirror = multiplyMatrix(
              multiplyMatrix([1, 0, 0, 1, cx, cy], axis === 'x' ? [-1, 0, 0, 1, 0, 0] : [1, 0, 0, -1, 0, 0]),
              [1, 0, 0, 1, -cx, -cy],
            );
            const parent = scene.parentOf(id) ?? ROOT_ID;
            const parentWorld = parent === ROOT_ID ? ([1, 0, 0, 1, 0, 0] as const) : scene.worldTransform(parent);
            const inverse = invertMatrix(parentWorld);
            if (!inverse) continue;
            ops.push({ op: 'set', id, props: { transform: multiplyMatrix(inverse, multiplyMatrix(mirror, scene.worldTransform(id))) } });
          }
          if (ops.length > 0 && edit(ops).ok) undo.stopCapturing();
        },
        selectAll: () => selectEverything(),
        layersAt: (at) => {
          const leaves = layersUnder(toWorld(at.x, at.y), 12);
          const groups: string[] = [];
          for (const id of leaves) {
            for (let up = scene.parentOf(id); up && up !== ROOT_ID && scene.get(up)?.type === 'group'; up = scene.parentOf(up)) {
              if (!groups.includes(up) && scene.choosable(up)) groups.push(up);
            }
          }
          return [...leaves, ...groups];
        },
        lockedAt: (at) => {
          // The locked layers under the point, or the locked group or page they are in: the
          // ones whose own lock an Unlock takes off.
          const out: string[] = [];
          for (const id of layersUnder(toWorld(at.x, at.y), 12, true)) {
            for (let up: string | null | undefined = id; up && up !== ROOT_ID; up = scene.parentOf(up)) {
              if (scene.get(up)?.locked && !out.includes(up)) out.push(up);
            }
          }
          return out;
        },
        selectionPlace: () => {
          const boxes = selection.map((id) => renderer.bounds.worldGeometryBounds(id)).filter((box): box is Rect => !!box);
          if (boxes.length === 0) return null;
          const x = Math.min(...boxes.map((box) => box.x));
          const y = Math.min(...boxes.map((box) => box.y));
          const union = { x, y, width: Math.max(...boxes.map((box) => box.x + box.width)) - x, height: Math.max(...boxes.map((box) => box.y + box.height)) - y };
          const boards = new Set(selection.map(artboardOf));
          const shared = boards.size === 1 ? [...boards][0] : null;
          const board = shared ? renderer.bounds.worldGeometryBounds(shared) : null;
          return { box: onScreen(union), board: board ? onScreen(board) : null };
        },
        onViewChange: (listener) => {
          viewListeners.add(listener);
          return () => viewListeners.delete(listener);
        },
        dragging: () => dragging,
        insertShape: (kind, at) => {
          current.deactivate?.(context);
          editing?.stop();
          undo.stopCapturing();
          const world = toWorld(at.x, at.y);
          // Use the shape tool's normal click creation, including style, parent and undo.
          const tool = shapeTool(kind);
          const event = new PointerEvent('pointerdown', { button: 0 });
          tool.onPointerDown?.(event, world, context);
          tool.onPointerUp?.(event, world, context);
        },
        duplicate: () => duplicate(),
        copyBeside: (id, props) => copyBeside(id, props),
        copySelection: () => {
          if (selection.length === 0) return false;
          copy();
          return true;
        },
        cutSelection: () => cutSelection(),
        hasCopy: () => clipboard.length > 0,
        copyProperties: () => copyProperties(),
        pasteProperties: () => pasteProperties(),
        hasProperties: () => heldLook() !== null,
        pasteText: (text: string, at?: { x: number; y: number }) => {
          const read = clippings(text);
          // Words copied in another app are words: they arrive as a text layer. They used
          // to paste whatever this tab had copied last, or nothing at all (QA 2026-10-05,
          // ART-EDGE-48). Shapes copied here travel as text too, and are read first.
          // Shapes copied here whose system copy was never confirmed are pasted as shapes:
          // the text on the system clipboard is older than them.
          const stale = !read && clipboard.length > 0 && !clipboardWritten;
          const words = read || stale || options.textEnabledRef?.current === false ? '' : text.replace(/\r\n?/g, '\n').trim();
          if (words) {
            pasteWanted = false;
            handleRef.current.addText({ text: words.slice(0, PASTED_TEXT_MOST), size: 0.03, weight: 400 });
            return true;
          }
          if (!read && clipboard.length === 0) return false;
          pasteWanted = false;
          if (read) adoptResources(clipboardResources(text));
          paste(read ?? clipboard, at ? toWorld(at.x, at.y) : undefined);
          return true;
        },
        setFont: (id, family, font) => {
          // The faces go in first: a family named by a layer whose files are not in the
          // document yet would draw in the fallback until they arrived.
          if (font) open.root.transact(() => fontsMap(open.root).set(font.family, font), LOCAL_ORIGIN);
          const node = scene.get(id) as { style?: Record<string, unknown> } | undefined;
          edit([{ op: 'set', id, props: { style: { ...(node?.style ?? {}), fontFamily: family } } }]);
          refitText(id);
          awaitingFonts.add(id);
          undo.stopCapturing();
        },
        addFont: (font) => {
          const fonts = fontsMap(open.root);
          const had = fonts.get(font.family) as DocumentFont | undefined;
          const faces = [...(had?.faces ?? [])];
          for (const face of font.faces) if (!faces.some((f) => f.weight === face.weight && f.italic === face.italic)) faces.push(face);
          open.root.transact(() => fonts.set(font.family, { ...(had ?? font), faces } as never), LOCAL_ORIGIN);
          undo.stopCapturing();
        },
        fontsInDocument: () => [...fontsMap(open.root).values()] as DocumentFont[],
        anchor: () => pathEditing.lone,
        placeAnchor: (at) => {
          pathEditing.placeLone(at);
          undo.stopCapturing();
        },
        cropPicture: (id) => {
          useTool('select');
          tools.select.cropPicture?.(id, context);
          needsFrame = true;
        },
        selectMode: (id) => {
          useTool('select');
          const top = tools.select.rail?.(context)?.top;
          if (top?.kind === 'buttons') top.items.find(mode => mode.id === id)?.run({ more: false });
        },
        rail: (tool) => {
          // The rail answers to the tool the person has CHOSEN, which the canvas adopts
          // on the next frame — read from the ref so the rail is right immediately rather
          // than a frame later.
          const chosen = tools[tool ?? options.toolRef.current] ?? current;
          return chosen.rail?.(context) ?? null;
        },
        animationTarget: () => {
          const id = exportBoard();
          const board = id ? scene.get(id) : null;
          if (!id || board?.type !== 'artboard') return null;
          /**
           * Read through the schema, and treat anything that does not fit as absent.
           *
           * The scene reads a page LOOSELY on purpose — a hundred thousand nodes cannot
           * afford a parse each — so a shape written by a different build reaches this
           * panel exactly as it was stored. Spreading that into an edit writes it back
           * unchanged, the op rejects it for the same reason, and the control sits there
           * showing NaN while every press does nothing. Falling back to "no animation"
           * costs the person one choice and gets them a document the editor agrees with.
           */
          const fits = <T,>(schema: { safeParse: (value: unknown) => { success: boolean; data?: unknown } }, value: unknown): T | null => {
            if (value === undefined || value === null) return null;
            const tried = schema.safeParse(value);
            return tried.success ? (tried.data as T) : null;
          };
          // The artboard itself does not count as a chosen layer: picking a page IS how
          // you get at the page's own animation, so it must not also read as a layer.
          // Nor does a layer that is not ON this page: a shape off every artboard was
          // listed as a layer of the nearest one and took a motion that never played
          // (QA 2026-09-24, anim-F9).
          const onPage = (one: string) => {
            for (let at = scene.parentOf(one); at; at = scene.parentOf(at)) if (at === id) return true;
            return false;
          };
          const chosen = selection.filter((one) => one !== id && scene.get(one));
          const ids = chosen.filter(onPage);
          const first = ids[0] ? scene.get(ids[0]) : null;
          return {
            offPage: chosen.length > 0 && ids.length === 0,
            page: {
              id,
              name: board.name || 'Page',
              animation: fits<Animation>(AnimationSchema, board.animation),
              duration: typeof board.duration === 'number' ? board.duration : 5,
              length: playableLength(scene, id) ?? (typeof board.duration === 'number' ? board.duration : 5),
              moves: playableLength(scene, id) !== null,
            },
            layer: first
              ? {
                  id: ids[0]!,
                  ids,
                  name: first.name || 'Layer',
                  type: first.type,
                  animation: fits<LayerAnimation>(LayerAnimationSchema, (first as { animation?: unknown }).animation),
                  inherited: inheritedAnimation(scene, ids[0]!),
                  timing: fits<Timing>(TimingSchema, (first as { timing?: unknown }).timing),
                }
              : null,
          };
        },
        setAnimation: (id, animation) => {
          if (scene.get(id)?.type !== 'artboard') return;
          edit([{ op: 'set', id, props: { animation: animation ?? undefined } }]);
          // One step per slider drag, not per notch (anim-F4).
          seal();
        },
        setLayerAnimation: (ids, animation, timing) => {
          /**
           * A change is worked out per layer, from that layer's OWN animation.
           *
           * The panel shows the first chosen layer, and every slider or motion used to
           * write that layer's whole animation onto all of them — each one's own motion,
           * delay and hand-drawn path gone without a word (QA 2026-09-24, anim-F3).
           */
          const ops: { op: 'set'; id: string; props: { animation: LayerAnimation | undefined; timing?: Timing | undefined } }[] = [];
          for (const id of ids) {
            const node = scene.get(id);
            if (!node) continue;
            if (typeof animation !== 'function') {
              ops.push({ op: 'set', id, props: { animation: animation ?? undefined } });
              continue;
            }
            const stored = LayerAnimationSchema.safeParse((node as { animation?: unknown }).animation);
            const own = stored.success ? (stored.data as LayerAnimation) : null;
            const next = animation(own, own ? null : inheritedAnimation(scene, id));
            if (next !== own) ops.push({ op: 'set', id, props: { animation: next ?? undefined } });
          }
          if (timing !== undefined) for (const op of ops) op.props.timing = timing ?? undefined;
          if (ops.length === 0) return;
          edit(ops);
          seal();
        },
        setLayerTiming: (ids, timing) => {
          const reached = ids.filter((id) => scene.get(id));
          if (reached.length === 0) return;
          edit(reached.map((id) => ({ op: 'set' as const, id, props: { timing: timing ?? undefined } })));
          seal();
        },
        setDuration: (id, seconds) => {
          if (scene.get(id)?.type !== 'artboard') return;
          edit([{ op: 'set', id, props: { duration: Math.min(60, Math.max(0.1, seconds)) } }]);
          seal();
        },
        magicAnimate: (id, styleId, settings) => {
          const plan = planMagic(scene, id, styleId, settings);
          if (!plan) return false;
          edit([
            // The recipe is recorded beside what it wrote, with how it was adjusted: a
            // recipe played quicker is still that recipe, and says so.
            { op: 'set', id, props: { animation: plan.page, recipe: plan.recipe } },
            // A layer the style has no part for gets its own animation REMOVED rather
            // than left alone: a page half-wearing the last style you tried is not the
            // style you just picked, and picking one twice should look the same twice.
            ...plan.layers.map((one) => ({ op: 'set' as const, id: one.id, props: { animation: one.animation ?? undefined } })),
          ]);
          // One step — or, while a slider of its gear is held, one step for the drag.
          seal();
          return true;
        },
        drawPath: (id, onDone) => {
          if (!scene.get(id)) return;
          stopPlaying();
          drawing = { id, from: { x: 0, y: 0 }, points: [], onDone };
          needsFrame = true;
        },
        cancelPath: () => cancelDrawing(),
        isDrawingPath: () => drawing !== null,
        play: (id, onStop, from = 0) => {
          // Asks the page whether ANYTHING on it moves, rather than whether the artboard
          // itself carries an animation: one typewritten headline is something to watch,
          // and refusing to play it reads as the feature being broken.
          const length = playableLength(scene, id);
          if (length === null) return false;
          stopPlaying();
          trying = null;
          // From the end is from the start: there is nothing after the end to watch.
          const start = from > 0 && from < length - 0.02 ? from : 0;
          playing = { id, from: performance.now() - start * 1000, length, onStop };
          needsFrame = true;
          return true;
        },
        seek: (id, seconds, onStop) => {
          const length = playableLength(scene, id);
          if (length === null) return false;
          const at = Math.max(0, Math.min(length, seconds));
          // Moved again while already held on this page: the same holding, a new instant.
          if (playing && playing.id === id && playing.held !== undefined) {
            playing.held = at;
            playing.length = length;
            playing.onStop = onStop;
            playing.drawn = false;
            return true;
          }
          stopPlaying();
          trying = null;
          playing = { id, from: performance.now() - at * 1000, length, onStop, held: at };
          needsFrame = true;
          return true;
        },
        preview: (layerId, animation, hold = 0) => {
          const solo = animation ? soloPlay(scene, layerId, animation, hold) : null;
          if (!solo) {
            if (trying) needsFrame = true;
            if (playing) playing.drawn = false;
            trying = null;
            return;
          }
          // Pointing at another move ends a PLAYING — two clocks on one page is none —
          // but not a holding: a paused page is paused until somebody plays it.
          if (playing && playing.held === undefined) stopPlaying();
          trying = { from: performance.now(), length: solo.length, at: solo.at };
        },
        stop: () => stopPlaying(),
        forgetPlayhead: () => {
          stopPlaying();
          resting = null;
        },
        isPlaying: () => playing !== null,
        playhead: () =>
          playing
            ? { id: playing.id, seconds: playing.held ?? Math.min(playing.length, (performance.now() - playing.from) / 1000), held: playing.held !== undefined, resting: false }
            : resting
              ? { id: resting.id, seconds: resting.seconds, held: false, resting: true }
              : null,
        recipeOf: (id) => {
          const board = scene.get(id);
          if (board?.type !== 'artboard' || !board.animation) return null;
          const stored = AnimationSchema.safeParse(board.animation);
          if (!stored.success) return null;
          const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
          const own = (layer: string) => {
            const value = (scene.get(layer) as { animation?: unknown } | undefined)?.animation;
            if (value === undefined || value === null) return null;
            const parsed = LayerAnimationSchema.safeParse(value);
            return parsed.success ? parsed.data : value;
          };
          const wears = (styleId: string, settings?: RecipeSettings): Recipe | null => {
            const plan = planMagic(scene, id, styleId, settings);
            if (!plan || !same(stored.data, plan.page)) return null;
            return plan.layers.every((one) => same(own(one.id), one.animation)) ? plan.recipe : null;
          };
          // What the page says it wears, adjustments and all — believed only if that is
          // still what is on it.
          const said = RecipeSchema.safeParse((board as { recipe?: unknown }).recipe);
          if (said.success) {
            const { id: styleId, ...settings } = said.data;
            const worn = wears(styleId, settings);
            if (worn) return worn;
          }
          for (const style of MAGIC_STYLES) {
            const worn = wears(style.id);
            if (worn) return worn;
          }
          return null;
        },
        recipeChoices: (id, recipe) => magicChoices(scene, id, recipe),
        timelineOf: (id) => {
          // When each layer arrives, loops and leaves is the engine's own answer — the
          // numbers playback uses — so the strip and the page can never disagree.
          const schedule = stageSchedule(scene, id);
          if (!schedule) return null;
          return {
            duration: schedule.duration,
            length: schedule.length,
            children: schedule.layers.map((layer) => {
              const node = scene.get(layer.id);
              const stored = LayerAnimationSchema.safeParse((node as { animation?: unknown } | undefined)?.animation);
              const animation = stored.success ? stored.data as LayerAnimation : inheritedAnimation(scene, layer.id);
              return { ...layer, name: node?.name || node?.type || 'Layer', animation };
            }),
          };
        },
        clearAnimation: (id, layers) => {
          if (scene.get(id)?.type !== 'artboard') return;
          stopPlaying();
          const ops: { op: 'set'; id: string; props: { animation?: undefined; timing?: undefined; recipe?: undefined } }[] = [{ op: 'set', id, props: { animation: undefined, recipe: undefined } }];
          if (layers) {
            // Everything on the page that moves, comes or goes — however deep, and its
            // window with it: a layer still set to appear at two seconds is still an
            // animation, and "none" that leaves one behind plays a page that is not still.
            for (const one of scene.subtree(id)) {
              if (one === id) continue;
              const node = scene.get(one) as { animation?: unknown; timing?: unknown } | undefined;
              if (!node) continue;
              const props: { animation?: undefined; timing?: undefined } = {};
              if (node.animation !== undefined) props.animation = undefined;
              if (node.timing !== undefined) props.timing = undefined;
              if ('animation' in props || 'timing' in props) ops.push({ op: 'set', id: one, props });
            }
          }
          edit(ops);
          seal();
        },
        addonBridge: () => addonBridge,
        setAddonTool: (next) => {
          if (next === addonTarget) return;
          const holding = current === addonSlot;
          if (holding) addonTarget?.deactivate?.(context);
          addonTarget = next;
          if (holding) addonTarget?.activate?.(context);
          options.onRailChanged?.();
          needsFrame = true;
        },
        guideCount: () => guides.length,
        clearGuides: () => {
          clearGuides(page, LOCAL_ORIGIN);
          undo.stopCapturing();
          needsFrame = true;
        },
        move: (id, target) => {
          if (id === target.before || id === target.after) return;
          const position = target.before ? { before: target.before } : target.after ? { after: target.after } : 'front';
          // The layer keeps its place on the canvas: moving it through the stack, or into
          // a group, changes what covers what and nothing else.
          edit([{ op: 'move', id, parentId: target.parentId, position, keepWorldTransform: true }]);
          undo.stopCapturing();
        },
        thumbnailOf: async (id) => {
          const box = renderer.bounds.worldGeometryBounds(id);
          if (!box || box.width <= 0 || box.height <= 0) return null;
          const scale = Math.min(1, 96 / Math.max(box.width, box.height));
          const width = Math.max(1, Math.round(box.width * scale)), height = Math.max(1, Math.round(box.height * scale));
          const surface = gl.offscreen(width, height);
          if (!surface) return null;
          let pixels: Uint8Array | null = null;
          try {
            surface.getCanvas().clear(ck.TRANSPARENT);
            renderer.drawNodes(surface.getCanvas(), [id], box, scale);
            surface.flush();
            const image = surface.makeImageSnapshot();
            pixels = image.readPixels(0, 0, { width, height, colorType: ck.ColorType.RGBA_8888, alphaType: ck.AlphaType.Unpremul, colorSpace: ck.ColorSpace.SRGB }) as Uint8Array | null;
            image.delete();
          } finally {
            surface.delete();
          }
          if (!pixels) return null;
          const out = new OffscreenCanvas(width, height);
          out.getContext('2d')?.putImageData(new ImageData(new Uint8ClampedArray(pixels), width, height), 0, 0);
          const blob = await out.convertToBlob({ type: 'image/png' });
          return await new Promise<string | null>((resolve) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = () => resolve(null); reader.readAsDataURL(blob); });
        },
        pickLayer: (candidates, then) => {
          layerPick?.then(null);
          layerPick = { candidates: new Set(candidates.filter((id) => scene.get(id))), hovered: null, then };
          needsFrame = true;
        },
        pickColour: (then) => {
          // The eyedropper is one click long: the next press takes a colour and the
          // tool goes back to what it was.
          picking = then ?? ((color) => options.onPickedColour?.(color));
          needsFrame = true;
        },
        fileColours: () =>
          countFileColours(
            (function* () {
              for (const id of scene.ids()) {
                const node = scene.get(id) as { fills?: Paint[]; strokes?: Paint[]; style?: { fills?: Paint[] } } | undefined;
                if (!node) continue;
                yield node.fills;
                yield node.strokes;
                yield node.style?.fills;
              }
            })(),
          ),
        addArtboard: (preset, turned = false, custom) => {
          const chosen = ARTBOARD_PRESETS.find((item) => item.id === preset) ?? ARTBOARD_PRESETS[1];
          const size = custom ?? { name: chosen.name, preset: chosen.id, ...sizeOf(chosen, turned) };
          // Beside the last one with a gap, so a row of boards reads left to right
          // rather than piling up at the origin — placed as the Artboard tool places one
          // (QA 2026-09-24, artboards-F7).
          const { x, y } = besideBoards(scene);
          const result = edit([
            {
              op: 'create',
              ref: 'board',
              parentId: ROOT_ID,
              ...behindLooseArt(scene),
              node: {
                type: 'artboard',
                name: size.name,
                preset: size.preset,
                width: size.width,
                height: size.height,
                transform: [1, 0, 0, 1, Math.round(x), Math.round(y)],
                fills: custom?.fills ?? [{ type: 'solid', color: { r: 1, g: 1, b: 1, a: 1 } }],
              },
            },
          ]);
          if (!result.ok) return;
          setSelection([result.created.board!]);
          undo.stopCapturing();
          // A new board off the edge of the view is a board nobody can see.
          setCamera(fitBox(contentBounds()));
        },
        editGradient: (target) => {
          // A handle dragged when the picker goes away (Esc, a tool key) never reaches the
          // pointerup that closes its undo step, and the next edit joined the drag
          // (QA 2026-09-24, paint-F21). Letting go of it here closes the step.
          if (gradientHeld) undo.stopCapturing();
          gradientEdit = target;
          gradientHeld = null;
          if (!target) {
            delete canvas.dataset.keepsPopover;
            setCanvasCursor(canvas, null);
          }
          needsFrame = true;
        },
        arrangeArtboards: ({ order, columns, gap }) => {
          const all = scene.childrenOf(ROOT_ID).filter((id) => scene.get(id)?.type === 'artboard');
          const boxes = new Map<string, Rect>();
          for (const id of all) {
            const node = scene.get(id) as { transform?: number[]; width?: number; height?: number } | undefined;
            if (node?.transform) boxes.set(id, { x: node.transform[4]!, y: node.transform[5]!, width: node.width ?? 0, height: node.height ?? 0 });
          }
          const known = all.filter((id) => boxes.has(id));
          if (known.length === 0) return;
          // The list's order (the file's), or the order given by a drag in the list — never
          // an order read off where the boards sit (see boardGrid.ts for why).
          const sequence = order ? [...order.filter((id) => boxes.has(id)), ...known.filter((id) => !order.includes(id))] : known;
          const origin = { x: Math.min(...known.map((id) => boxes.get(id)!.x)), y: Math.min(...known.map((id) => boxes.get(id)!.y)) };
          const places = boardGrid(sequence.map((id) => boxes.get(id)!), columns, gap, origin);
          const ops: unknown[] = [];
          // The boards swap among the slots boards already hold, so the file's order
          // becomes the sequence without any board passing loose art. They all used to go to
          // the front, even with the order unchanged, and hid the part of a loose drawing
          // hanging over a page (QA 2026-09-24, artboards-F12).
          // An explicit layout command: a locked board is laid out with the rest (force)
          // rather than refusing the whole batch — one locked board used to stop Tidy Up,
          // Columns, Gap and list reordering for every board (QA 2026-09-24, artboards-F2).
          const placing = new Set(sequence);
          for (const { id, position } of boardSlotMoves(scene.childrenOf(ROOT_ID), (id) => placing.has(id), sequence)) {
            ops.push({ op: 'move', id, parentId: ROOT_ID, position, force: true });
          }
          sequence.forEach((id, index) => ops.push({ op: 'set', id, props: { transform: [1, 0, 0, 1, places[index]!.x, places[index]!.y] }, force: true }));
          if (!edit(ops).ok) {
            // Said with its reason when the reason is size: a page near the limit put the
            // next one past the edge of the canvas and Tidy Up just stopped working
            // (QA 2026-09-24, artboards-F16).
            const past = places.some((at) => Math.abs(at.x) > COORD_LIMIT || Math.abs(at.y) > COORD_LIMIT);
            options.onNotice?.(past ? 'The artboards are too big to lay out in rows this wide — try fewer columns.' : 'The artboards could not be arranged.');
            return;
          }
          undo.stopCapturing();
          setCamera(fitBox(contentBounds()));
        },
        sizeArtboards: (ids, preset) => {
          const chosen = ARTBOARD_PRESETS.find((item) => item.id === preset);
          if (!chosen) return;
          const ops: unknown[] = [];
          for (const id of ids) {
            const node = scene.get(id) as { type?: string; width?: number; height?: number } | undefined;
            if (node?.type !== 'artboard') continue;
            ops.push({ op: 'set', id, props: { ...sizedLike(chosen, node.width ?? 0, node.height ?? 0), preset: chosen.id } });
          }
          if (ops.length === 0 || !edit(ops).ok) return;
          undo.stopCapturing();
        },
        exportBoards: (scope) => {
          const all = scene.childrenOf(ROOT_ID).filter((id) => scene.get(id)?.type === 'artboard');
          const ids = scope === 'all' ? all : scope === 'selected' ? all.filter((id) => chosenBoards().includes(id)) : scope === 'layers' ? [] : [exportBoard()].filter((id): id is string => !!id);
          return ids.map((id) => {
            const board = scene.get(id) as { name?: string; width?: number; height?: number } | undefined;
            return { id, name: board?.name || 'Artboard', width: board?.width ?? 0, height: board?.height ?? 0 };
          });
        },
        artboards: () => {
          const here = exportBoard();
          return scene.childrenOf(ROOT_ID).flatMap((id) => {
            const node = scene.get(id);
            return node?.type === 'artboard' ? [{ id, name: node.name ?? 'Artboard', current: id === here }] : [];
          });
        },
        focusArtboard: (id) => {
          const box = renderer.bounds.worldGeometryBounds(id);
          if (!box) return;
          setSelection([id]);
          setCamera(fitBox(box));
        },
        showArtboard: (id) => {
          const box = renderer.bounds.worldGeometryBounds(id);
          if (box) setCamera(fitBox(box));
        },
        viewKey: () => `${camera.x}|${camera.y}|${camera.zoom}`,
        goTo: (view) => {
          const next = cameraAt(view, viewCentre());
          if (next) setCamera(next);
        },
        boxOf: (id) => {
          const box = scene.has(id) ? renderer.bounds.worldGeometryBounds(id) : null;
          return box ? onScreen(box) : null;
        },
        worldAt: (at) => toWorld(at.x, at.y),
        worldBoxOf: (id) => {
          const box = scene.has(id) ? renderer.bounds.worldGeometryBounds(id) : null;
          return box ? { x: box.x, y: box.y, width: box.width, height: box.height } : null;
        },
        screenAt: (at) => {
          const frame = canvas.getBoundingClientRect();
          return { x: frame.left + (at.x - camera.x) * camera.zoom, y: frame.top + (at.y - camera.y) * camera.zoom };
        },
        placePicture: async (files, at) => {
          const given = files instanceof File ? [files] : [...files];
          if (given.length === 0) return;
          // An SVG comes in as layers — paths, shapes, text — that can be edited, rather
          // than as pixels. One that cannot be read that way (not really an SVG, or more
          // than one edit can hold) goes on down the picture route below, so nothing that
          // came in before stops coming in.
          const list = given.filter((file) => !isSvgFile(file));
          const vectors = given.filter(isSvgFile);
          if (vectors.length > 0) {
            const placed: string[] = [];
            for (const [index, file] of vectors.entries()) {
              const group = await placeVector(file, at, index).catch((error: unknown) => {
                console.warn('The SVG could not be read as layers, so it is placed as a picture', error);
                if (error instanceof Error && error.name === 'SvgImportError' && !/not an SVG/.test(error.message)) {
                  options.onNotice?.(`${error.message} It was placed as a picture for now.`);
                }
                return null;
              });
              if (disposed) return;
              if (group) placed.push(group);
              else list.push(file);
            }
            if (placed.length > 0) setSelection(placed);
            if (list.length === 0) return;
          }
          // Said while it runs: nothing showed that a picture was still on its way up
          // (QA 2026-09-24, files-doc-F11, files-doc-F23).
          began(list.length);
          let settled: PromiseSettledResult<Awaited<ReturnType<typeof uploadPicture>>>[];
          // Each file counted off as it lands, so the message counts up as they go.
          let landed = 0;
          try {
            settled = await uploadPictures(open.documentId, list, { projectId: options.projectRef?.current ?? null }, () => {
              landed += 1;
              ended(1);
            });
          } finally {
            ended(list.length - landed);
          }
          // The file was left while the pictures went up: its documents are gone, and
          // writing into them was a picture that never appeared (files-doc-F23).
          if (disposed) return;
          // Whatever goes wrong on the way up is said, not swallowed: a HEIC or a text file
          // named .png used to do nothing at all, with only an unhandled rejection to show.
          const failed = settled.flatMap((one) => (one.status === 'rejected' ? [one.reason as unknown] : []));
          const pictures = settled.flatMap((one) => (one.status === 'fulfilled' ? [one.value] : []));
          const notices: string[] = [];
          if (failed.length > 0) {
            const reason = failed[0] instanceof Error ? failed[0].message : 'That picture could not be placed.';
            notices.push(list.length === 1 ? reason : `${failed.length} of ${list.length} files couldn’t be added. ${reason}`);
          }
          const downsize = downsizingNotice(pictures);
          if (downsize) notices.push(downsize);
          if (notices.length) options.onNotice?.(notices.join(' '));
          if (pictures.length === 0) return;
          // Each picture is described once in the document, and every editor's canvas
          // loads it from there — the id and the URL travel, the bytes do not.
          open.root.transact(() => {
            for (const picture of pictures) {
              assetsMap(open.root).set(picture.mediaAssetId, {
                // A video uploaded is a clip: its asset says so, and how long it runs.
                kind: picture.duration ? 'video' : 'image',
                ...(picture.duration ? { duration: picture.duration } : {}),
                name: picture.name,
                mediaAssetId: picture.mediaAssetId,
                src: picture.url,
                mime: picture.mime,
                width: picture.width,
                height: picture.height,
              } as never);
            }
          });

          const { width: viewWidth, height: viewHeight } = viewSize();
          const centre = at ?? { x: camera.x + viewWidth / 2 / camera.zoom, y: camera.y + viewHeight / 2 / camera.zoom };
          const parent = renderer.unitsAt(centre.x, centre.y).find((id) => scene.get(id)?.type === 'artboard');
          const parentOrigin = parent ? scene.worldTransform(parent) : [1, 0, 0, 1, 0, 0];
          // Several dropped together are fanned out a little, in drop order, so they do
          // not land exactly on top of each other (files-doc-F12).
          const step = 24 / camera.zoom;
          const ops = pictures.map((picture, index) => {
            // Big pictures come in at a size that fits the view rather than filling the page.
            const fit = Math.min(1, (viewWidth * 0.6) / camera.zoom / Math.max(1, picture.width));
            const width = Math.round(picture.width * fit) || 200;
            const height = Math.round(picture.height * fit) || 200;
            const x = centre.x + index * step;
            const y = centre.y + index * step;
            return {
              op: 'create',
              ref: `picture${index}`,
              parentId: parent ?? ROOT_ID,
              node: {
                type: 'image',
                name: picture.name,
                assetId: picture.mediaAssetId,
                width,
                height,
                fit: 'fill',
                ...(picture.duration ? { clip: { duration: picture.duration } } : {}),
                transform: [1, 0, 0, 1, x - width / 2 - parentOrigin[4]!, y - height / 2 - parentOrigin[5]!],
              },
            };
          });
          // One step of its own, however long the upload took and whatever was done
          // meanwhile: every picture of one drop comes and goes together.
          undo.stopCapturing();
          const result = edit(ops);
          if (result.ok) {
            setSelection(pictures.map((_, index) => result.created[`picture${index}`]!).filter(Boolean));
            undo.stopCapturing();
          }
        },

        addShape: (shape, dropped) => {
          const at = landing(dropped);
          const side = Math.max(8, Math.round(at.side * 0.3));
          const style = options.styleRef.current;
          const line = shape.type === 'line';
          const drawn =
            shape.type === 'rect'
              ? { type: 'rect', width: side, height: side, radii: Array(4).fill(Math.round(side * (shape.round ?? 0))) }
              : shape.type === 'ellipse'
                ? { type: 'ellipse', width: side, height: side }
                : shape.type === 'polygon'
                  ? { type: 'polygon', width: side, height: side, sides: shape.sides, cornerRadius: 0 }
                  : shape.type === 'star'
                    ? { type: 'star', width: side, height: side, points: shape.points, innerRatio: shape.innerRatio, cornerRadius: 0 }
                    : { type: 'line', length: side };
          add({
            ...drawn,
            name: NAMES_OF_SHAPES[shape.type],
            transform: [1, 0, 0, 1, at.centre.x - side / 2, at.centre.y - (line ? 0 : side / 2)],
            opacity: style.opacity,
            // A line has no inside: its colour is its stroke's. Everything else is filled,
            // whatever the Fill switch says — a shape asked for by its picture arrives looking like it.
            fills: line ? [] : [{ type: 'solid', color: styleFill(style) }],
            ...(line || style.strokeWeight > 0
              ? { strokes: [{ type: 'solid', color: line ? styleFill(style) : hexColor(style.stroke) }], strokeStyle: { weight: Math.max(line ? Math.max(2, Math.round(side / 60)) : 0, style.strokeWeight) } }
              : {}),
          }, at.parent);
        },
        addText: (preset, dropped) => {
          const at = landing(dropped);
          const fontSize = Math.max(6, Math.round(at.page * preset.size));
          // Let go somewhere, it is centred there — on a guess at its width, half an em a
          // letter, since it is not measured until it is made. Pressed, it is set from the
          // page's leading edge, a third of the way down: where a heading goes.
          // A brand's face goes into the file first: a family named by a layer whose files
          // are not there yet would draw in the fallback until they arrived.
          const font = preset.font;
          if (font && !fontsMap(open.root).has(font.family)) open.root.transact(() => fontsMap(open.root).set(font.family, font), LOCAL_ORIGIN);
          const id = add({
            type: 'text',
            name: 'Text',
            sizing: 'auto-width',
            width: 0,
            height: 0,
            transform: [1, 0, 0, 1, at.centre.x - (dropped ? (preset.text.length * fontSize) / 4 : at.page * 0.38), at.centre.y - fontSize * 0.6],
            runs: [{ text: preset.text }],
            style: {
              fontFamily: font?.family ?? DEFAULT_FAMILY,
              fontSize,
              fontWeight: preset.weight,
              fills: [{ type: 'solid', color: hexColor('#000000') }],
              ...(preset.lineHeight ? { lineHeight: { unit: 'percent', value: Math.round(preset.lineHeight * 100) } } : {}),
              ...(preset.spacing ? { letterSpacing: { unit: 'percent', value: Math.round(preset.spacing * 1000) / 10 } } : {}),
              ...(preset.textCase && preset.textCase !== 'none' ? { textCase: preset.textCase } : {}),
            },
          }, at.parent);
          if (!id) return;
          refitText(id);
          // Measured again once the face has loaded: until then it was measured in the fallback.
          if (font) awaitingFonts.add(id);
          // Open with all of it chosen: the words are there to be typed over.
          editing?.start(id, 'all');
        },
        paintSelection: (given, to) => {
          // Two colours run across the box at the brand's angle, as CSS counts it: 0 is up, 90 to the right.
          const run = typeof given === 'string' ? null : { x: Math.sin((given.angle * Math.PI) / 180) / 2, y: -Math.cos((given.angle * Math.PI) / 180) / 2 };
          const paint =
            typeof given === 'string' || !run
              ? [{ type: 'solid', color: hexColor(given as string) }]
              : [{ type: 'linear', stops: [{ offset: 0, color: hexColor(given.from) }, { offset: 1, color: hexColor(given.to) }], start: { x: 0.5 - run.x, y: 0.5 - run.y }, end: { x: 0.5 + run.x, y: 0.5 + run.y } }];
          const ops: { op: 'set'; id: string; props: Record<string, unknown> }[] = [];
          const visit = (id: string) => {
            const node = scene.get(id);
            if (!node || node.locked) return;
            // A group has no colour of its own: what is in it is painted.
            if (node.type === 'group') scene.childrenOf(id).forEach(visit);
            else if (to === 'stroke') {
              if (['rect', 'ellipse', 'polygon', 'star', 'path', 'boolean', 'line'].includes(node.type)) ops.push({ op: 'set', id, props: { strokes: paint } });
            }
            // Type keeps its colour in its style — and only there. Writing `runs` back
            // with it sets the words again, and they came back at the default size.
            else if (node.type === 'text') ops.push({ op: 'set', id, props: { style: { ...node.style, fills: paint } } });
            else if (node.type === 'line') ops.push({ op: 'set', id, props: { strokes: paint } });
            else if (node.type === 'path' && node.fills.length === 0 && node.strokes.length > 0) ops.push({ op: 'set', id, props: { strokes: paint } });
            // A page chosen takes it as its background.
            else if (['rect', 'ellipse', 'polygon', 'star', 'path', 'boolean', 'artboard'].includes(node.type)) ops.push({ op: 'set', id, props: { fills: paint } });
          };
          unlocked(selection).forEach(visit);
          if (ops.length === 0) return false;
          edit(ops);
          // A text whose style changed is laid out again, as any other change to it is.
          for (const one of ops) refitText(one.id, one.props);
          seal();
          return true;
        },
        filePictures: () => {
          const out: LibraryPicture[] = [];
          // A deleted picture's asset stays (undo needs it), so list only what a layer still uses.
          const used = usedAssetIds([...scene.ids()].map((id) => scene.get(id)));
          assetsMap(open.root).forEach((asset, assetId) => {
            if (!used.has(assetId)) return;
            const one = asset as Asset & { width?: number; height?: number; name?: string; mime?: string };
            if (one.kind === 'image' && one.src) out.push({ assetId, ...(one.mediaAssetId ? { mediaAssetId: one.mediaAssetId } : {}), name: one.name ?? 'Picture', src: one.src, width: one.width ?? 0, height: one.height ?? 0, ...(one.mime ? { mime: one.mime } : {}) });
          });
          return out.reverse();
        },
        uploadPaintPicture: async (file) => {
          if (isClipFile(file)) throw new Error('Choose a still image for the fill.');
          began(1);
          try {
            const picture = await uploadPicture(open.documentId, file, { projectId: options.projectRef?.current ?? null });
            if (disposed) return null;
            const downsize = downsizingNotice([picture]);
            if (downsize) options.onNotice?.(downsize);
            open.root.transact(() => {
              assetsMap(open.root).set(picture.mediaAssetId, {
                kind: 'image', name: picture.name, mediaAssetId: picture.mediaAssetId,
                src: picture.url, mime: picture.mime, width: picture.width, height: picture.height,
              } as never);
            });
            return { assetId: picture.mediaAssetId, mediaAssetId: picture.mediaAssetId, name: picture.name,
              src: picture.url, mime: picture.mime, width: picture.width, height: picture.height };
          } finally {
            ended(1);
          }
        },
        createGenerationTarget: (size, name, avoid = []) => {
          const taken = avoid.map(id => renderer.bounds.worldGeometryBounds(id)).filter((box): box is NonNullable<typeof box> => !!box);
          const placement = generationPlacement(size, viewSize(), camera, inset(), taken);
          handBack();
          return add({ type: 'rect', name, ...placement, fills: [], strokes: [] }, null);
        },
        generationViewport: () => {
          const frame = canvas.getBoundingClientRect(), room = generationViewport(viewSize(), inset());
          return { left: frame.left + room.left, top: frame.top + room.top, right: frame.left + room.right, bottom: frame.top + room.bottom };
        },
        revealGenerationTarget: id => {
          const bounds = renderer.bounds.worldGeometryBounds(id);
          if (!bounds) return;
          const box = onScreen(bounds), frame = canvas.getBoundingClientRect(), room = generationViewport(viewSize(), inset());
          if (box.left < frame.left + room.left + 16 || box.top < frame.top + room.top + 16 || box.right > frame.left + room.right - 16 || box.bottom > frame.top + room.bottom - 16) setCamera(fitBox(bounds));
        },
        addPicture: (picture, dropped) => {
          const at = landing(dropped);
          // The picture's own address travels with the document, as an uploaded one's does.
          if (!assetsMap(open.root).has(picture.assetId)) {
            open.root.transact(() => {
              assetsMap(open.root).set(picture.assetId, {
                kind: picture.duration ? 'video' : 'image',
                name: picture.name.slice(0, 256),
                ...(picture.mediaAssetId ? { mediaAssetId: picture.mediaAssetId } : {}),
                src: picture.src,
                ...(picture.mime ? { mime: picture.mime } : {}),
                ...(picture.width > 0 && picture.height > 0 ? { width: picture.width, height: picture.height } : {}),
                ...(picture.duration ? { duration: picture.duration } : {}),
              } as never);
            });
          }
          const ratio = picture.width > 0 && picture.height > 0 ? picture.width / picture.height : 1;
          // Half the page across at most, and never taller than half of it either.
          const width = Math.round(Math.min(at.page * 0.5, at.tall * 0.5 * ratio));
          const height = Math.round(width / ratio);
          // A clip is a picture with a length: the page plays it, and lasts until it has.
          add({ type: 'image', name: picture.name, assetId: picture.assetId, width, height, fit: 'crop', ...(picture.duration ? { clip: { duration: picture.duration } } : {}), transform: [1, 0, 0, 1, at.centre.x - width / 2, at.centre.y - height / 2] }, at.parent);
        },
        placeVectorOver: (sourceId, svg, name) => {
          const source = scene.get(sourceId) as { transform: number[]; width?: number; height?: number } | undefined;
          if (!source || scene.lockedHere(sourceId)) return null;
          const parent = scene.parentOf(sourceId) ?? ROOT_ID;
          let read;
          try {
            const first = svgToArtwork(svg, { name });
            const scale = (source.width ?? first.width) / Math.max(1, first.width);
            read = svgToArtwork(svg, { name, scale, parentId: parent, origin: { x: source.transform[4]!, y: source.transform[5]! } });
          } catch (error) {
            console.warn('A traced picture could not be read', error);
            return null;
          }
          undo.stopCapturing();
          const result = edit([...read.ops, { op: 'set', id: sourceId, props: { visible: false } }]);
          if (!result.ok) { console.warn('A traced picture was refused', result.errors); return null; }
          for (const ref of read.texts) if (result.created[ref]) refitText(result.created[ref]!);
          undo.stopCapturing();
          const group = result.created[read.ref] ?? null;
          if (group) setSelection([group]);
          return group;
        },
        placeLayerDocument: (sourceId, doc, pictures, fonts, size, boardName) => {
          // On an artboard of its own at the document's size, beside the others (Tarek, 15 Oct:
          // "create an artboard for the content instead of a group"); the picture stays as it was.
          const source = scene.get(sourceId) as { name?: string } | undefined;
          if (!doc.canvas?.w || !doc.canvas.h) return null;
          open.root.transact(() => {
            for (const font of fonts) fontsMap(open.root).set(font.family, font);
            for (const picture of pictures) {
              if (!assetsMap(open.root).has(picture.mediaAssetId)) {
                assetsMap(open.root).set(picture.mediaAssetId, { kind: 'image', name: picture.name.slice(0, 256), mediaAssetId: picture.mediaAssetId, src: picture.url, mime: picture.mime,
                  ...(picture.width > 0 && picture.height > 0 ? { width: picture.width, height: picture.height } : {}) } as never);
              }
            }
          }, LOCAL_ORIGIN);
          // The board is the picture's own size; the document's units are scaled to it.
          const width = Math.round(size?.width || doc.canvas.w), height = Math.round(size?.height || doc.canvas.h);
          const built = documentOps(doc, pictures, '$layers', width / doc.canvas.w);
          const { x, y } = besideBoards(scene);
          undo.stopCapturing();
          const result = edit([
            { op: 'create', ref: 'layers', parentId: ROOT_ID, ...behindLooseArt(scene), node: { type: 'artboard', name: boardName || `${source?.name || 'Picture'} layers`,
              width, height, fills: [{ type: 'solid', color: hexColor('#ffffff') }], transform: [1, 0, 0, 1, Math.round(x), Math.round(y)] } },
            ...built.ops,
          ]);
          if (!result.ok) { console.warn('A layer document was refused', result.errors); return null; }
          const realign: unknown[] = [];
          for (const ref of built.texts) {
            const id = result.created[ref]; if (!id) continue;
            refitText(id); awaitingFonts.add(id);
            // Measured: a one-line text goes back on its box by its alignment.
            const placed = scene.get(id) as { width?: number; transform: number[] } | undefined, intended = built.boxes[ref];
            if (placed?.width && intended && intended.align !== 'start') {
              const x = intended.x + (intended.align === 'center' ? (intended.w - placed.width) / 2 : intended.w - placed.width);
              realign.push({ op: 'set', id, props: { transform: [1, 0, 0, 1, x, placed.transform[5]] } });
            }
          }
          if (realign.length) edit(realign);
          undo.stopCapturing();
          return result.created.layers ?? null;
        },
        placeDrawing: (svg, { name, share, at: dropped }) => {
          const at = landing(dropped);
          let read;
          try {
            // Read once for its size, then again at the size and place it lands at.
            const first = svgToArtwork(svg, { name });
            const scale = (at.side * share) / Math.max(first.width, first.height, 1);
            read = svgToArtwork(svg, {
              name,
              scale,
              parentId: at.parent ?? ROOT_ID,
              origin: { x: at.centre.x - (first.width * scale) / 2, y: at.centre.y - (first.height * scale) / 2 },
            });
          } catch (error) {
            console.warn('A catalogue drawing could not be read', error);
            options.onNotice?.('That could not be placed.');
            return;
          }
          handBack();
          undo.stopCapturing();
          const result = edit(read.ops);
          if (!result.ok) {
            console.warn('A catalogue drawing was refused', result.errors);
            options.onNotice?.('That could not be placed.');
            return;
          }
          for (const ref of read.texts) if (result.created[ref]) refitText(result.created[ref]!);
          const group = result.created[read.ref];
          if (group) setSelection([group]);
          undo.stopCapturing();
        },
        placeLayers: (layers, size, dropped) => {
          const clips = layers.artwork.filter((clip) => clip.node.type !== 'artboard');
          if (clips.length === 0) return;
          const at = landing(dropped);
          adoptResources(layers.resources);
          // As made, unless that is more than half the page: then it is brought down to fit.
          const scale = Math.min(1, (at.side * 0.5) / Math.max(size.width, size.height, 1));
          const corner = { x: at.centre.x - (size.width * scale) / 2, y: at.centre.y - (size.height * scale) / 2 };
          const ops: unknown[] = [];
          const refs: string[] = [];
          const refOf = new Map<string, string>();
          clips.forEach((clip, index) => {
            const ref = `placed${index}`;
            refs.push(ref);
            refOf.set(clip.id, ref);
            const w = (clip.world ?? (clip.node.transform as number[] | undefined) ?? [1, 0, 0, 1, 0, 0]) as Matrix;
            ops.push({
              op: 'create',
              ref,
              parentId: at.parent ?? ROOT_ID,
              node: { ...clip.node, transform: [w[0] * scale, w[1] * scale, w[2] * scale, w[3] * scale, w[4] * scale + corner.x, w[5] * scale + corner.y] },
            });
            const byOriginal = new Map<string, string>([[clip.id, ref]]);
            clip.children.forEach((child, childIndex) => {
              const childRef = `${ref}_${childIndex}`;
              byOriginal.set(child.id, childRef);
              refOf.set(child.id, childRef);
              ops.push({ op: 'create', ref: childRef, parentId: `$${byOriginal.get(child.parent) ?? ref}`, node: child.node });
            });
          });
          handBack();
          undo.stopCapturing();
          const result = edit(ops);
          if (!result.ok) {
            console.warn('Catalogue layers were refused', result.errors);
            options.onNotice?.('That could not be placed.');
            return;
          }
          relink(refOf, result.created);
          for (const ref of refOf.values()) if (result.created[ref]) refitText(result.created[ref]!);
          setSelection(refs.map((ref) => result.created[ref]!).filter(Boolean));
          undo.stopCapturing();
        },
        placeTemplate: (template, dropped) => {
          // Let go over a page, it is that page that is offered the template; let go on
          // the bare canvas, the new page is made where it was let go.
          const under = dropped ? artboardAt(dropped.x, dropped.y) : null;
          const board = dropped ? under : exportBoard();
          // A page nobody has put anything on is the one the template is for.
          const blank = board !== null && scene.childrenOf(board).length === 0 ? board : null;
          // A template that names no colour is on white paper, as a new page is.
          const paper = [{ type: 'solid', color: hexColor(template.background ?? '#ffffff') }];
          const ops: unknown[] = [];
          /** Refs of what is made, to find the type among it afterwards. */
          const made: string[] = [];
          const refOf = new Map<string, string>();
          const page = blank ?? '$templatePage';
          const beside = (width: number, height: number) => {
            if (dropped && !under) return [1, 0, 0, 1, Math.round(dropped.x - width / 2), Math.round(dropped.y - height / 2)];
            const { x, y } = besideBoards(scene);
            return [1, 0, 0, 1, Math.round(x), Math.round(y)];
          };

          const clip = template.layers?.artwork.find((one) => one.node.type === 'artboard');
          if (clip) {
            adoptResources(template.layers?.resources);
            // Everything the page was saved with — its size, its colour, how it plays —
            // except where it sat in the file it came from.
            const { type: _type, transform: _transform, ...asSaved } = clip.node;
            if (blank) ops.push({ op: 'set', id: blank, props: { ...asSaved, preset: null } });
            else ops.push({ op: 'create', ref: 'templatePage', parentId: ROOT_ID, ...behindLooseArt(scene), node: { ...clip.node, transform: beside(Number(clip.node.width) || 0, Number(clip.node.height) || 0) } });
            const byOriginal = new Map<string, string>();
            clip.children.forEach((child, index) => {
              const ref = `template${index}`;
              byOriginal.set(child.id, ref);
              refOf.set(child.id, ref);
              made.push(ref);
              ops.push({ op: 'create', ref, parentId: child.parent === clip.id ? page : `$${byOriginal.get(child.parent) ?? ''}`, node: child.node });
            });
          } else if (template.svg) {
            let read;
            try {
              read = svgToArtwork(template.svg, { name: template.name, parentId: page });
            } catch (error) {
              console.warn('A template could not be read', error);
              options.onNotice?.('That template could not be opened.');
              return;
            }
            const props = { name: template.name, width: read.width, height: read.height, fills: paper };
            if (blank) ops.push({ op: 'set', id: blank, props: { ...props, preset: null } });
            else ops.push({ op: 'create', ref: 'templatePage', parentId: ROOT_ID, ...behindLooseArt(scene), node: { type: 'artboard', ...props, transform: beside(read.width, read.height) } });
            // The engine hands an SVG over as one group. A template's layers belong to
            // the page itself, so the group is left out and what was in it made straight
            // on the page — when the group does nothing but hold them, which is every
            // SVG drawn at its own size from its own corner.
            const [top, ...rest] = read.ops as { op: string; ref?: string; parentId?: string; node?: { transform?: number[]; opacity?: number } }[];
            const t = top?.node?.transform;
            const holdsOnly = top?.ref === read.ref && (!t || (t[0] === 1 && t[1] === 0 && t[2] === 0 && t[3] === 1 && t[4] === 0 && t[5] === 0)) && (top.node?.opacity ?? 1) === 1;
            if (holdsOnly) ops.push(...rest.map((op) => (op.parentId === `$${read.ref}` ? { ...op, parentId: page } : op)));
            else ops.push(...read.ops);
            made.push(...read.texts);
          } else {
            return;
          }

          handBack();
          undo.stopCapturing();
          const result = edit(ops);
          if (!result.ok) {
            console.warn('A template was refused', result.errors);
            options.onNotice?.('That template could not be opened.');
            return;
          }
          relink(refOf, result.created);
          for (const ref of made) if (result.created[ref]) refitText(result.created[ref]!);
          const id = blank ?? result.created.templatePage;
          // Nothing chosen: the next press picks a line of type to change, not the page.
          setSelection([]);
          undo.stopCapturing();
          const box = id ? renderer.bounds.worldGeometryBounds(id) : null;
          if (box) setCamera(fitBox(box));
        },
        clipArtboard: (id) => {
          const board = scene.get(id);
          if (board?.type !== 'artboard') return null;
          const clip = snapshot(id);
          if (!clip) return null;
          return { name: board.name || 'Artboard', width: board.width, height: board.height, layers: { artwork: [clip], resources: resourcesOf([clip]) } };
        },
        clipSelection: () => {
          // Back to front, as they are stacked — not in the order they were picked, which
          // put whatever was picked last on top of the rest.
          const stacked = new Map(scene.paintOrder().map((id, index) => [id, index]));
          const ids = scene
            .topmost(selection)
            .filter((id) => scene.get(id)?.type !== 'artboard')
            .sort((a, b) => (stacked.get(a) ?? 0) - (stacked.get(b) ?? 0));
          const clips = ids.map(snapshot).filter((one): one is Clipping => one !== null);
          const boxes = ids.map((id) => renderer.bounds.worldGeometryBounds(id)).filter((box): box is Rect => box !== null);
          if (clips.length === 0 || boxes.length === 0) return null;
          const left = Math.min(...boxes.map((box) => box.x));
          const top = Math.min(...boxes.map((box) => box.y));
          const width = Math.max(...boxes.map((box) => box.x + box.width)) - left;
          const height = Math.max(...boxes.map((box) => box.y + box.height)) - top;
          if (!(width > 0) || !(height > 0)) return null;
          // Kept from the corner of the box they fill, wherever they sat on the page.
          const moved = clips.map((clip) => {
            const w = (clip.world ?? [1, 0, 0, 1, 0, 0]) as number[];
            return { ...clip, world: [w[0]!, w[1]!, w[2]!, w[3]!, w[4]! - left, w[5]! - top] };
          });
          const only = ids.length === 1 ? scene.get(ids[0]!)?.name : undefined;
          return { name: only ?? 'Graphic', layers: { artwork: moved, resources: resourcesOf(clips) }, width, height };
        },
        replacePicture: async (ids, file) => {
          const images = ids.filter((id) => scene.get(id)?.type === 'image');
          if (images.length === 0) return;
          began(1);
          let picture: Awaited<ReturnType<typeof uploadPicture>>;
          try {
            picture = await uploadPicture(open.documentId, file, { projectId: options.projectRef?.current ?? null });
          } catch (error) {
            if (!disposed) options.onNotice?.(error instanceof Error ? error.message : 'That picture could not be used.');
            return;
          } finally {
            ended(1);
          }
          if (disposed) return;
          const downsize = downsizingNotice([picture]);
          if (downsize) options.onNotice?.(downsize);
          open.root.transact(() => {
            assetsMap(open.root).set(picture.mediaAssetId, {
              kind: 'image',
              name: picture.name,
              mediaAssetId: picture.mediaAssetId,
              src: picture.url,
              mime: picture.mime,
              width: picture.width,
              height: picture.height,
            } as never);
          });
          const ops = images.flatMap((id) => {
            const node = scene.get(id);
            if (node?.type !== 'image') return [];
            const was = assetsMap(open.root).get(node.assetId) as Asset | undefined;
            // A layer still called after its old picture is called after the new one; a
            // name somebody gave it ("Hero photo") is theirs and stays.
            const renamed = !node.name || node.name === was?.name || node.name === 'Image';
            // Shown whole if it was being shown whole; otherwise it covers the frame. Never
            // stretched: the new picture is rarely the old one's shape.
            // A still picture put into a clip's frame makes it a picture again.
            return [{ op: 'set' as const, id, props: { assetId: picture.mediaAssetId, fit: node.fit === 'fit' ? 'fit' : 'crop', ...(renamed ? { name: picture.name } : {}), ...(node.clip ? { clip: null } : {}), ...(node.sketch ? { sketch: null } : {}), ...(node.crop ? { crop: null } : {}) } }];
          });
          if (ops.length === 0) return;
          // One step of its own, however long the upload took and whatever was done meanwhile.
          undo.stopCapturing();
          edit(ops);
          undo.stopCapturing();
        },
        fitPicture: (ids, how) => {
          const ops = ids.flatMap((id) => {
            const node = scene.get(id);
            if (!node) return [];
            // A layer shows a picture one of two ways: as an image, or as a shape filled with one.
            const raw = node as NodeRecord & { width?: number; height?: number; crop?: unknown; fills?: Paint[] };
            const at = node.type === 'image' ? -1 : (raw.fills ?? []).findLastIndex((paint) => paint.type === 'image' && paint.visible !== false);
            if (node.type !== 'image' && at < 0) return [];
            // Choosing how a picture sits starts it over: all of it again, not the crop.
            const paint = at >= 0 ? (raw.fills![at] as ImagePaint) : null;
            const fitted = (fit: 'fit' | 'crop'): Record<string, unknown> => {
              if (!paint) return { fit, ...(raw.crop ? { crop: null } : {}) };
              const { crop: _crop, transform: _transform, ...rest } = paint;
              return { fills: raw.fills!.map((fill, index) => (index === at ? { ...rest, fit } : fill)) };
            };
            if (how !== 'frame') return [{ op: 'set' as const, id, props: fitted(how === 'fit' ? 'fit' : 'crop') }];
            const assetId = paint ? paint.assetId : (node as ImageNode).assetId;
            const asset = assetsMap(open.root).get(assetId) as (Asset & { width?: number; height?: number }) | undefined;
            const frameWidth = raw.width ?? 0, frameHeight = raw.height ?? 0;
            if (!asset?.width || !asset.height || !frameWidth || !frameHeight) return [];
            // The frame takes the picture's proportions about its own middle, keeping the
            // side that was already the tighter fit — so it never grows past where it was.
            const ratio = asset.width / asset.height;
            const width = frameWidth / frameHeight > ratio ? frameHeight * ratio : frameWidth;
            const height = width / ratio;
            const [a, b, c, d, tx, ty] = node.transform;
            const dx = (frameWidth - width) / 2;
            const dy = (frameHeight - height) / 2;
            return [{ op: 'set' as const, id, props: { width, height, ...fitted('crop'), transform: [a, b, c, d, tx + a * dx + c * dy, ty + b * dx + d * dy] } as Record<string, unknown> }];
          });
          if (ops.length === 0) return;
          edit(ops);
          seal();
        },

        /** The artboard being worked on: the one holding the selection, else the first. */
        exportRaster: async (options) => {
          const ids = options.nodeIds ? [...options.nodeIds] : options.selection ? exportLayerIds() : undefined;
          const targets = options.targets.map(target => {
            const board = target.artboardId ?? exportBoard();
            // A complete artboard reference keeps its exact frame and only its own subtree.
            const wholeBoard = !!board && ids?.length === 1 && ids[0] === board && scene.get(board)?.type === 'artboard';
            const box = wholeBoard ? renderer.bounds.worldGeometryBounds(board!) : ids ? exportLayersBox(ids) : board ? renderer.bounds.worldGeometryBounds(board) : null;
            if (!box) throw new Error('There is nothing to export.');
            rasterDimensions(box, options.scale);
            return { name: target.name, box: { ...box }, ...(ids ? { ids: [...ids] } : {}) };
          });
          // Include the drawn subtrees and their sibling masks. Unrelated images and fonts
          // must not delay an export or make it fail because their files are unavailable.
          const needed = new Set<string>();
          const add = (id: string) => { for (const child of scene.subtree(id)) needed.add(child); };
          if (ids) {
            for (const id of ids) {
              add(id);
              for (const sibling of scene.childrenOf(scene.parentOf(id) ?? ROOT_ID)) if (scene.get(sibling)?.isMask) add(sibling);
            }
          } else {
            for (const id of scene.childrenOf(ROOT_ID)) {
              const box = renderer.bounds.worldVisualBounds(id);
              if (box && targets.some(target => intersects(box, target.box))) add(id);
            }
          }
          const assetIds = new Set<string>();
          const families = new Set<string>();
          const collect = (value: unknown): void => {
            if (!value || typeof value !== 'object') return;
            for (const [key, item] of Object.entries(value)) {
              if (key === 'assetId' && typeof item === 'string') assetIds.add(item);
              else if (key === 'fontFamily' && typeof item === 'string') families.add(item);
              else if (item && typeof item === 'object') collect(item);
            }
          };
          for (const id of needed) collect(scene.get(id));
          const assets: RasterSnapshot['assets'] = [...assetIds].map(id => {
            const asset = assetsMap(open.root).get(id) as Asset | undefined;
            if (!asset) throw new Error('An image is missing from the document.');
            const frame = asset.kind === 'video' ? images.get(id)?.encodeToBytes() : undefined;
            if (asset.kind === 'sketch' && !asset.src) return [id, asset];
            return [id, { ...asset, ...(asset.src ? { src: corsReadUrl(asset.src) } : {}), ...(frame ? { frame } : {}) }];
          });
          // A sketch's still is the layer's own: two copies of it can be set differently.
          const sketchFrames: [string, Uint8Array][] = [];
          for (const id of needed) {
            const node = scene.get(id);
            if (node?.type !== 'image' || !node.sketch) continue;
            images.get(node.assetId, undefined, node as ImageNode & { id: string });
            for (let waited = 0; waited < 8_000 && !images.sketches.rest(id) && !images.sketches.errorFor(id); waited += 100) {
              options.signal.throwIfAborted();
              await new Promise(resolve => setTimeout(resolve, 100));
            }
            const bytes = images.sketches.rest(id)?.encodeToBytes();
            if (bytes) sketchFrames.push([id, bytes]);
          }
          return runRasterExport({
            page: encodeStateAsUpdate(open.page), assets, sketchFrames,
            shaders: [...shadersMap(open.root).entries()] as [string, ShaderDef][],
            fonts: ([...fontsMap(open.root).values()] as DocumentFont[]).filter(font => families.has(font.family)),
            ...(proofer ? { proof: { lut: pressLut(proofer), size: PRESS_LUT_SIZE } } : {}),
            now: performance.now(), targets, format: options.format, scale: options.scale,
          }, options.signal, options.onProgress);
        },
        exportVideo: (options) => {
          const targets: RawVideoSnapshot['targets'] = [];
          for (const [index, artboardId] of options.artboardIds.entries()) {
            const node = scene.get(artboardId);
            const name = (node as { name?: string } | undefined)?.name || 'Artboard';
            const length = playableLength(scene, artboardId);
            if (length === null) return { cloud: `${name} has no animation, so there is nothing to play. Export it as a PNG instead.` };
            const box = renderer.bounds.worldGeometryBounds(artboardId);
            if (!box || box.width <= 0 || box.height <= 0) return { cloud: `${name} has no size to draw.` };
            targets.push({ name: options.names[index] ?? name, artboardId, box: { ...box }, seconds: length });
          }
          // What the drawn region holds, as a still export gathers it: every top-level layer crossing an artboard.
          const needed = new Set<string>();
          for (const id of scene.childrenOf(ROOT_ID)) {
            const box = renderer.bounds.worldVisualBounds(id);
            if (box && targets.some((target) => intersects(box, target.box))) for (const child of scene.subtree(id)) needed.add(child);
          }
          const assetIds = new Set<string>();
          const families = new Set<string>();
          const collect = (value: unknown): void => {
            if (!value || typeof value !== 'object') return;
            for (const [key, item] of Object.entries(value)) {
              if (key === 'assetId' && typeof item === 'string') assetIds.add(item);
              else if (key === 'fontFamily' && typeof item === 'string') families.add(item);
              if (item && typeof item === 'object') collect(item);
            }
          };
          for (const id of needed) collect(scene.get(id));
          // Pictures, clips (decoded in the export worker) and sketches (run by the export page).
          const assets: RawVideoSnapshot['assets'] = [];
          for (const id of assetIds) {
            const asset = assetsMap(open.root).get(id) as Asset | undefined;
            if (!asset) return { cloud: 'An image is missing from the document.' };
            if (!asset.src && asset.kind !== 'sketch') return { cloud: `${asset.name || 'An image'} has no file to export.` };
            assets.push([id, asset.src ? { ...asset, src: corsReadUrl(asset.src) } : asset]);
          }
          return {
            snapshot: {
              page: encodeStateAsUpdate(open.page),
              assets,
              shaders: [...shadersMap(open.root).entries()] as [string, ShaderDef][],
              fonts: ([...fontsMap(open.root).values()] as DocumentFont[]).filter((font) => families.has(font.family)),
              ...(proofer ? { proof: { lut: pressLut(proofer), size: PRESS_LUT_SIZE } } : {}),
              targets,
              scale: options.scale,
              fps: options.fps,
              format: options.format,
            },
          };
        },
        exportPng: (scale, artboardId, ownContent = false) => {
          const board = artboardId ?? exportBoard();
          if (!board) return null;
          const box = renderer.bounds.worldGeometryBounds(board);
          if (!box) return null;
          // On the canvas's GPU, as the canvas draws: the pictures on screen are textures, which
          // draw to no raster surface. The CPU is the fallback, where `loadOriginals` has run first.
          const gpu = gl.offscreen(Math.ceil(box.width * scale), Math.ceil(box.height * scale));
          const surface = gpu ?? ck.MakeSurface(Math.ceil(box.width * scale), Math.ceil(box.height * scale));
          if (!surface) return null;
          try {
            // Transparent behind the artboard: a PNG of a card should not carry the
            // canvas colour with it.
            surface.getCanvas().clear(ck.Color4f(0, 0, 0, 0));
            if (ownContent) renderer.drawNodes(surface.getCanvas(), [board], box, scale);
            else renderer.drawRegion(surface.getCanvas(), box, scale);
            surface.flush();
            const image = surface.makeImageSnapshot();
            const bytes = image.encodeToBytes();
            image.delete();
            return bytes ?? null;
          } finally {
            // A GPU target is deleted; a CPU one disposed, not deleted: delete frees the Skia object
            // and leaves its pixels malloc'd in CanvasKit's heap, so every PNG export leaked a picture's worth.
            if (gpu) gpu.delete(); else surface.dispose();
          }
        },
        exportSvg: (artboardId, pictureScale) => {
          const board = artboardId ?? exportBoard();
          const shared = svgShared(pictureScale);
          return board ? toSvgWithNotes(scene, { artboardId: board, ...shared }) : toSvgWithNotes(scene, shared);
        },
        /** How many slices there are, without writing a single one of them. */
        sliceCount: () => {
          let count = 0;
          const walk = (parent: string) => {
            for (const id of scene.childrenOf(parent)) {
              if (scene.get(id)?.type === 'slice') count += 1;
              else walk(id);
            }
          };
          walk(ROOT_ID);
          return count;
        },
        /**
         * Every slice on the page, as its own SVG.
         *
         * A slice is a window rather than a layer, so it cannot be written by node id: it
         * is whatever falls inside that rectangle, from wherever on the page it came. The
         * file takes the slice's own name, because naming the files is most of what a
         * person draws slices for.
         */
        exportSlices: (pictureScale) => {
          const shared = svgShared(pictureScale);
          const out: { name: string; svg: string; notes: string[] }[] = [];
          const walk = (parent: string) => {
            for (const id of scene.childrenOf(parent)) {
              const node = scene.get(id);
              if (!node) continue;
              if (node.type === 'slice') {
                const box = renderer.bounds.worldGeometryBounds(id);
                // A slice sized down to nothing exported an empty file (QA 2026-09-24, cut-slice-measure-F21).
                if (box && box.width > 0 && box.height > 0) out.push({ name: node.name || `slice-${out.length + 1}`, ...toSvgWithNotes(scene, { region: box, ...shared }) });
                continue;
              }
              walk(id);
            }
          };
          walk(ROOT_ID);
          return out;
        },
        exportLayers: () => {
          const ids = exportLayerIds();
          const size = exportLayersBox(ids);
          if (ids.length === 0 || !size) return null;
          const only = ids.length === 1 ? scene.get(ids[0]!) : null;
          return { count: ids.length, name: only ? only.name || 'Layer' : `${ids.length} layers`, size };
        },
        exportLayersPng: (scale) => {
          const ids = exportLayerIds();
          const box = exportLayersBox(ids);
          if (!box) return null;
          // On the canvas's GPU, as exportPng: the pictures on screen are textures.
          const gpu = gl.offscreen(Math.ceil(box.width * scale), Math.ceil(box.height * scale));
          const surface = gpu ?? ck.MakeSurface(Math.ceil(box.width * scale), Math.ceil(box.height * scale));
          if (!surface) return null;
          try {
            surface.getCanvas().clear(ck.Color4f(0, 0, 0, 0));
            renderer.drawNodes(surface.getCanvas(), ids, box, scale);
            surface.flush();
            const image = surface.makeImageSnapshot();
            const bytes = image.encodeToBytes();
            image.delete();
            return bytes ?? null;
          } finally {
            // A GPU target is deleted; a CPU one disposed, not deleted: see exportPng.
            if (gpu) gpu.delete(); else surface.dispose();
          }
        },
        loadOriginals: async (artboardId) => {
          const box = artboardId ? renderer.bounds.worldGeometryBounds(artboardId) : exportLayersBox(exportLayerIds());
          if (!box) return;
          const within = new Set<string>();
          const walk = (parent: string) => {
            for (const id of scene.childrenOf(parent)) {
              const node = scene.get(id) as { assetId?: string; fills?: Paint[]; strokes?: Paint[]; style?: { fills?: Paint[] } } | undefined;
              if (!node) continue;
              const ids = [node.assetId, ...[...(node.fills ?? []), ...(node.style?.fills ?? []), ...(node.strokes ?? [])].map((paint) => (paint.type === 'image' ? paint.assetId : undefined))];
              if (ids.some(Boolean)) {
                const at = renderer.bounds.worldGeometryBounds(id);
                if (at && intersects(at, box)) for (const one of ids) if (one) within.add(one);
              }
              walk(id);
            }
          };
          walk(ROOT_ID);
          await images.originals(within);
        },
        renderLayerPng: async (id, maxEdge) => {
          type Shown = { assetId?: string; fit?: string; crop?: PictureCrop; visible?: boolean };
          const node = scene.get(id) as (Shown & { type: string; width?: number; height?: number; fills?: (Shown & { type: string })[] }) | undefined;
          if (!node) return null;
          // Each picture the layer shows, with how it sits in the frame (an image layer's own, or a fill's).
          const shown: Shown[] = [
            ...(node.type === 'image' && node.assetId ? [{ assetId: node.assetId, fit: node.fit, crop: node.crop }] : []),
            ...(node.fills ?? []).filter((paint) => paint.type === 'image' && paint.assetId && paint.visible !== false),
          ];
          const assetIds = [...new Set(shown.map((one) => one.assetId!))];
          // The full originals, never the copy drawn on screen (its size is the layer's on the page).
          await images.originals(assetIds);
          for (let waited = 0; waited < 20_000 && assetIds.some((assetId) => !images.originalSize(assetId) && !images.errorFor(assetId)); waited += 200) {
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
          if (assetIds.some((assetId) => !images.originalSize(assetId))) return null;
          // The frame, not what spills past it (a shadow): the layers made from it fill that frame.
          const box = renderer.bounds.worldGeometryBounds(id);
          const frameWidth = Number(node.width), frameHeight = Number(node.height);
          if (!box || box.width <= 0 || box.height <= 0 || !(frameWidth > 0) || !(frameHeight > 0)) return null;
          // At the pictures' own resolution: as many pixels across the frame as the sharpest picture
          // has across the part of it the frame shows (its crop and fit counted), within `maxEdge`.
          let perUnit = 0;
          for (const one of shown) {
            const size = images.originalSize(one.assetId!)!;
            const how = one.fit === 'fit' ? 'contain' : one.fit === 'crop' ? 'cover' : 'stretch';
            const placed = placePicture({ width: frameWidth, height: frameHeight }, size, how, one.crop);
            perUnit = Math.max(perUnit, size.width / placed.width, size.height / placed.height);
          }
          const native = Math.max(frameWidth, frameHeight) * perUnit;
          const longEdge = Math.round(Math.min(maxEdge, Math.max(512, native || 2048)));
          const scale = longEdge / Math.max(box.width, box.height);
          const width = Math.max(1, Math.round(box.width * scale)), height = Math.max(1, Math.round(box.height * scale));
          // On the canvas's GPU, as the canvas draws: on a CPU surface a Lumen or a blur ran in
          // software and froze the editor (Tarek, 2 Nov). The CPU is the fallback only.
          const gpu = gl.offscreen(width, height);
          const surface = gpu ?? ck.MakeSurface(width, height);
          if (!surface) return null;
          try {
            surface.getCanvas().clear(ck.Color4f(0, 0, 0, 0));
            renderer.drawNodes(surface.getCanvas(), [id], box, scale);
            surface.flush();
            const image = surface.makeImageSnapshot();
            const bytes = image.encodeToBytes();
            image.delete();
            return bytes ?? null;
          } finally {
            // A GPU target is deleted; a CPU one disposed (see exportPng).
            if (gpu) gpu.delete(); else surface.dispose();
          }
        },
        renderReference: async (id, longEdge, format) => {
          const node = scene.get(id) as { assetId?: string; fills?: { type: string; assetId?: string }[] } | undefined;
          const box = node ? renderer.bounds.worldGeometryBounds(id) : null;
          if (!node || !box || box.width <= 0 || box.height <= 0) return null;
          // The copies already on screen: asking for the originals swapped the canvas itself up to
          // full-size pictures, and its Lumen then ran over every pixel of them (Tarek, 2 Nov: froze).
          const assetIds = [node.assetId, ...(node.fills ?? []).map((paint) => (paint.type === 'image' ? paint.assetId : undefined))].filter((one): one is string => !!one);
          if (assetIds.some((assetId) => !images.get(assetId))) return null;
          const scale = longEdge / Math.max(box.width, box.height);
          const width = Math.max(1, Math.round(box.width * scale)), height = Math.max(1, Math.round(box.height * scale));
          // On the GPU, as the canvas draws: on a CPU surface a Lumen ran in software and froze the page.
          const surface = gl.offscreen(width, height);
          if (!surface) return null;
          let pixels: Uint8Array | null = null;
          try {
            surface.getCanvas().clear(format === 'jpg' ? ck.WHITE : ck.TRANSPARENT);
            renderer.drawNodes(surface.getCanvas(), [id], box, scale);
            surface.flush();
            const image = surface.makeImageSnapshot();
            pixels = image.readPixels(0, 0, { width, height, colorType: ck.ColorType.RGBA_8888, alphaType: ck.AlphaType.Unpremul, colorSpace: ck.ColorSpace.SRGB }) as Uint8Array | null;
            image.delete();
          } catch (error) {
            console.warn('[references] could not draw the layer on the canvas', error);
            return null;
          } finally {
            surface.delete();
          }
          if (!pixels) return null;
          // The browser encodes: this CanvasKit build has no JPEG encoder, and its PNG one is slow.
          const out = new OffscreenCanvas(width, height), g = out.getContext('2d');
          if (!g) return null;
          g.putImageData(new ImageData(new Uint8ClampedArray(pixels.buffer as ArrayBuffer, pixels.byteOffset, width * height * 4), width, height), 0, 0);
          return out.convertToBlob(format === 'jpg' ? { type: 'image/jpeg', quality: 0.9 } : { type: 'image/png' }).catch(() => null);
        },
        previewEffects: async (id, types, longEdge, onTile, signal) => {
          const node = scene.get(id) as (NodeRecord & { assetId?: string; fills?: { type: string; assetId?: string }[]; effects?: Effect[] }) | undefined;
          const box = node ? renderer.bounds.worldGeometryBounds(id) : null;
          if (!node || !box || box.width <= 0 || box.height <= 0) return [];
          // Small copies of its pictures (the CDN's, else the full ones): a tile is 144 px, and
          // drawing a 3000 px original twelve times took a second on the main thread.
          const assetIds = [node.assetId, ...(node.fills ?? []).map((paint) => (paint.type === 'image' ? paint.assetId : undefined))].filter((one): one is string => !!one);
          const smallPictures = new Map<string, NonNullable<ReturnType<typeof images.get>>>();
          await Promise.all(assetIds.map(async (assetId) => {
            const picture = await images.small(assetId, 320);
            if (picture) smallPictures.set(assetId, picture);
          }));
          if (disposed || signal?.aborted) return [];
          const pictures: ImageSource = { get: (assetId, at, layer) => (layer?.sketch ? null : smallPictures.get(assetId)) ?? images.get(assetId, at, layer) };
          const side = Math.min(box.width, box.height);
          const picture = node.type === 'image' || (node.fills ?? []).some((paint) => paint.type === 'image');
          const had = node.effects ?? [];
          const parent = scene.parentOf(id);
          const out: EffectPreview[] = [];
          for (const type of types) {
            // One tile at a time, the page answering in between.
            await new Promise<void>((resolve) => setTimeout(resolve, 0));
            // Closed, or the layer changed: the rest are not wanted.
            if (disposed || signal?.aborted) return out;
            const look = effectLook(type, side);
            if (!look) continue;
            // The layer read through a view that wears one more effect. The painter asks a scene
            // for exactly these four things, so nothing of the page is copied and nothing written.
            const worn = { ...node, effects: [...had, look] };
            const view = {
              get: (one: string) => (one === id ? worn : scene.get(one)),
              childrenOf: (one?: string) => scene.childrenOf(one),
              parentOf: (one: string) => scene.parentOf(one),
              worldTransform: (one: string) => scene.worldTransform(one),
            } as unknown as typeof scene;
            const resources = new ResourceCache(ck, view, undefined, pictures);
            const painter = new Painter(ck, view, resources, { images: pictures, ...(text ? { text } : {}) });
            // On the canvas's GPU, as the canvas draws: on a CPU surface every blur and shader ran in software.
            const surface = gl.offscreen(longEdge, longEdge) ?? ck.MakeSurface(longEdge, longEdge);
            let png: Uint8Array | null = null;
            try {
              if (surface) {
                const canvas = surface.getCanvas();
                canvas.clear(ck.Color4f(0, 0, 0, 0));
                // A picture fills its tile, cut square from the middle. A shape or text is drawn whole with
                // room round it, where its blur and glow happen, and so is any layer's drop shadow.
                const room = type === 'drop-shadow' || !picture ? Math.max(box.width, box.height) * 1.36 : side;
                const scale = longEdge / room;
                canvas.save();
                canvas.scale(scale, scale);
                canvas.translate(-(box.x + box.width / 2 - room / 2), -(box.y + box.height / 2 - room / 2));
                if (parent !== undefined && parent !== ROOT_ID) canvas.concat(toCanvasKit(scene.worldTransform(parent)));
                painter.drawShown(canvas, id);
                canvas.restore();
                surface.flush();
                const image = surface.makeImageSnapshot();
                png = image.encodeToBytes() ?? null;
                image.delete();
              }
            } catch (error) {
              console.warn(`[effects] could not draw the ${type} tile`, error);
            } finally {
              surface?.delete();
              painter.dispose();
              resources.dispose();
            }
            const tile = { type, look, png };
            out.push(tile);
            onTile?.(tile);
          }
          return out;
        },
        exportLayersSvg: (pictureScale) => {
          const ids = exportLayerIds();
          return ids.length > 0 ? toSvgWithNotes(scene, { nodeIds: ids, ...svgShared(pictureScale) }) : null;
        },
        exportArtboard: () => exportBoard(),
        exportName: () => {
          const board = exportBoard();
          const name = board ? scene.get(board)?.name : null;
          return (name || 'artwork').replace(/[^\w\d-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'artwork';
        },
        align: (how) => {
          if (selection.length === 0) return;
          const boxes = unlocked(selection).flatMap((id) => {
            const box = renderer.bounds.worldGeometryBounds(id);
            const transform = scene.get(id)?.transform;
            return box && transform ? [{ id, box, transform }] : [];
          });
          if (!boxes.length) return;
          // A single layer aligns to its artboard; multiple layers align to each other.
          const board = selection.length === 1 ? artboardOf(selection[0]!) : null;
          const frame = board && board !== selection[0] ? renderer.bounds.worldGeometryBounds(board) : null;
          if (selection.length === 1 && (!frame || how === 'spread-x' || how === 'spread-y')) return;
          if (selection.length > 1 && boxes.length < 2) return;
          const left = frame ? frame.x : Math.min(...boxes.map((item) => item.box.x));
          const right = frame ? frame.x + frame.width : Math.max(...boxes.map((item) => item.box.x + item.box.width));
          const top = frame ? frame.y : Math.min(...boxes.map((item) => item.box.y));
          const bottom = frame ? frame.y + frame.height : Math.max(...boxes.map((item) => item.box.y + item.box.height));

          // Spreading puts equal gaps between the shapes, leaving the outermost two
          // where they are — the two the person has already placed.
          const spread = (axis: 'x' | 'y') => {
            const sorted = [...boxes].sort((a, b) => (axis === 'x' ? a.box.x - b.box.x : a.box.y - b.box.y));
            const size = (item: (typeof boxes)[number]) => (axis === 'x' ? item.box.width : item.box.height);
            const span = (axis === 'x' ? right - left : bottom - top) - sorted.reduce((sum, item) => sum + size(item), 0);
            const gap = span / (sorted.length - 1);
            let at = axis === 'x' ? left : top;
            return sorted.map((item) => {
              const delta = at - (axis === 'x' ? item.box.x : item.box.y);
              at += size(item) + gap;
              return { item, dx: axis === 'x' ? delta : 0, dy: axis === 'y' ? delta : 0 };
            });
          };

          const moves =
            how === 'spread-x' || how === 'spread-y'
              ? spread(how === 'spread-x' ? 'x' : 'y')
              : boxes.map((item) => {
                  const dx =
                    how === 'left'
                      ? left - item.box.x
                      : how === 'right'
                        ? right - (item.box.x + item.box.width)
                        : how === 'centre'
                          ? (left + right) / 2 - (item.box.x + item.box.width / 2)
                          : 0;
                  const dy =
                    how === 'top'
                      ? top - item.box.y
                      : how === 'bottom'
                        ? bottom - (item.box.y + item.box.height)
                        : how === 'middle'
                          ? (top + bottom) / 2 - (item.box.y + item.box.height / 2)
                          : 0;
                  return { item, dx, dy };
                });

          const ops = moves
            .filter(({ dx, dy }) => Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01)
            .map(({ item, dx, dy }) => {
              const t = item.transform;
              const along = deltaToParent(scene, item.id, dx, dy);
              return { op: 'set', id: item.id, props: { transform: [t[0], t[1], t[2], t[3], t[4]! + along.dx, t[5]! + along.dy] } };
            });
          if (ops.length === 0) return;
          edit(ops);
          undo.stopCapturing();
        },
        tidyUp: (how) => {
          const items = unlocked(selection).flatMap((id) => {
            const box = renderer.bounds.worldGeometryBounds(id);
            const node = scene.get(id);
            return box && node?.transform ? [{ id, box, node }] : [];
          });
          if (items.length < 2) return;
          const byId = new Map(items.map((item) => [item.id, item]));
          // Type grows wrong when scaled, and an artboard's size is not its contents': both keep theirs.
          const scalable = (node: NodeRecord) => node.type !== 'text' && node.type !== 'artboard' && node.type !== 'line';
          const boxes = items.map(({ id, box, node }) => ({ id, x: box.x, y: box.y, width: box.width, height: box.height, scalable: scalable(node) }));
          const ops = tidyUp(boxes, how).map(({ id, dx, dy, scale }) => {
            const { box, node } = byId.get(id)!;
            const t = node.transform;
            const props: Record<string, unknown> = {};
            let [a, b, c, d] = [t[0], t[1], t[2], t[3]];
            let ex = dx;
            let ey = dy;
            if (Math.abs(scale - 1) > 1e-4) {
              // Scaled about the layer's own origin (its transform's corner); that point
              // is moved on by as much as it puts the box's corner out of place.
              const origin = applyToPoint(scene.worldTransform(id), 0, 0);
              ex += (1 - scale) * (box.x - origin.x);
              ey += (1 - scale) * (box.y - origin.y);
              const own = node as { width?: unknown; height?: unknown };
              if (typeof own.width === 'number' && typeof own.height === 'number' && own.width > 0 && own.height > 0) {
                // A sized layer gets a new size, so its stroke and corners stay as they were.
                props.width = own.width * scale;
                props.height = own.height * scale;
              } else {
                [a, b, c, d] = [a * scale, b * scale, c * scale, d * scale];
              }
            }
            const along = deltaToParent(scene, id, ex, ey);
            props.transform = [a, b, c, d, t[4]! + along.dx, t[5]! + along.dy];
            return { op: 'set', id, props };
          });
          if (ops.length === 0) return;
          // One batch, closed on both sides: the whole tidy is one Cmd+Z.
          undo.stopCapturing();
          edit(ops);
          undo.stopCapturing();
        },
        boxInParent: (id) => {
          const box = renderer.bounds.worldGeometryBounds(id);
          if (!box) return null;
          const parent = scene.parentOf(id);
          const inverse = parent && parent !== ROOT_ID ? invert(scene.worldTransform(parent)) : null;
          if (!inverse) return box;
          const corners = [
            applyToPoint(inverse, box.x, box.y),
            applyToPoint(inverse, box.x + box.width, box.y),
            applyToPoint(inverse, box.x, box.y + box.height),
            applyToPoint(inverse, box.x + box.width, box.y + box.height),
          ];
          const xs = corners.map((c) => c.x);
          const ys = corners.map((c) => c.y);
          return { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
        },
        middleOf: (id) => {
          const box = renderer.bounds.worldGeometryBounds(id);
          const inverse = invert(scene.worldTransform(id));
          return box && inverse ? applyToPoint(inverse, box.x + box.width / 2, box.y + box.height / 2) : null;
        },
        update: (id, props) => {
          edit([{ op: 'set', id, props }]);
          refitText(id, props);
          // Typing in a field is a change at a time; each is its own step to take back. A
          // slider or scrub being held is one step, closed when the hand comes up
          // (QA 2026-09-24, inspector-numbers-F10).
          if (!panelHeld())undo.stopCapturing();
        },
        updateAll: (ids, props) => {
          // One batch, so one Cmd+Z takes it back from every node at once: a background
          // set across several boards used to be a step per board, and one undo left them
          // in mixed colours (QA 2026-09-24, artboards-F5).
          if (ids.length === 0) return;
          edit(ids.map((id) => ({ op: 'set', id, props })));
          for (const id of ids) refitText(id, props);
          seal();
        },
        updateEach: (writes) => {
          if (writes.length === 0) return;
          edit(writes.map(({ id, props }) => ({ op: 'set', id, props })));
          for (const { id, props } of writes) refitText(id, props);
          seal();
        },
        mask: () => {
          const plan = planSelectionMask(scene, selection);
          if (!plan) return;
          undo.stopCapturing();
          const result = edit(plan.ops);
          undo.stopCapturing();
          if (result.ok) {
            setSelection([result.created.maskGroup!]);
            // Masked, from any of its places: the hint that offers Mask instead of Intersect is done (lib/hints).
            emitHintSignal('selection.masked');
          } else options.onNotice?.('Could not create the mask. Check that the selected layers are unlocked.');
        },
        combine: (op) => {
          if (!canCombineSelection(scene, selection)) {
            options.onNotice?.('Select at least two unlocked vector shapes. Use Mask to clip pictures or text.');
            return;
          }
          const chosen = geometrySelection(scene, selection);
          // Subtract reads back to front, regardless of selection order.
          const parents = new Set(chosen.map((id) => scene.parentOf(id) ?? ROOT_ID));
          // The frontmost layer's own parent — its artboard — when they come from several.
          // They used to be lifted onto the page and off their artboards
          // (QA 2026-09-24, menus-shortcuts-quickbar-F10).
          const parent = parents.size === 1 ? [...parents][0]! : (scene.parentOf(chosen[chosen.length - 1]!) ?? ROOT_ID);
          const style = options.styleRef.current;
          undo.stopCapturing();
          const result = edit([
            {
              op: 'create',
              ref: 'shape',
              parentId: parent,
              node: {
                type: 'boolean',
                name: op.charAt(0).toUpperCase() + op.slice(1),
                booleanOp: op,
                fills: [{ type: 'solid', color: styleFill(style) }],
              },
            },
            ...chosen.map((id) => ({ op: 'move', id, parentId: '$shape', position: 'front', keepWorldTransform: true })),
          ]);
          if (!result.ok) {
            console.warn('Combining failed', result.errors);
            return;
          }
          setSelection([result.created.shape!]);
          undo.stopCapturing();
        },
        /**
         * Effects live in a list on the node, and the list IS the order they are applied
         * in — so everything here is a whole-list write rather than a patch. Writing the
         * list back entire is also what keeps two people editing one layer from ending
         * up with one person's blur at the top and the other's at the bottom.
         */
        setAdjust: (id, values) => {
          const node = scene.get(id) as (NodeRecord & { effects?: Effect[] }) | undefined;
          if (!node || !('effects' in node) || scene.lockedHere(id)) return;
          const effects = [...(node.effects ?? [])];
          const at = adjustIndex(node);
          let shaderAdded = false;
          if (isNeutral(values)) { if (at < 0) return; effects.splice(at, 1); }
          else {
            if (!shadersMap(open.root).has(ADJUST_SHADER_ID)) { open.root.transact(() => shadersMap(open.root).set(ADJUST_SHADER_ID, ADJUST_SHADER), LOCAL_ORIGIN); shaderAdded = true; }
            if (at >= 0) effects[at] = { ...(effects[at] as Effect), values: { ...values } } as Effect;
            else { if (effects.length >= 32) { options.onNotice?.('This layer already has 32 effects. Remove one to adjust it.'); return; } effects.push(adjustEffect(values) as Effect); }
          }
          edit([{ op: 'set', id, props: { effects } }]);
          // The edit redraws this layer's own tiles. Everything else on screen is redrawn only
          // when the file first gains the shader: doing it every step re-rendered every tile in
          // view on each slider move, and Adjust lagged behind the hand (Tarek, 15 Oct).
          if (shaderAdded) renderer.invalidateAll();
        },
        setBlur: (id, radius) => {
          const node = scene.get(id) as (NodeRecord & { effects?: Effect[] }) | undefined;
          if (!node || !('effects' in node) || scene.lockedHere(id)) return;
          const effects = [...(node.effects ?? [])];
          const at = effects.findIndex(effect => effect.type === 'layer-blur');
          if (radius <= 0) { if (at < 0) return; effects.splice(at, 1); }
          else if (at >= 0) effects[at] = { ...(effects[at] as Effect), radius } as Effect;
          // The picture tools' blur softens the picture and keeps its frame sharp (Focus's background, the Blur tool).
          else effects.push({ type: 'layer-blur', radius, visible: true, inside: true } as Effect);
          // The edit redraws the tiles the layer covered and covers now; nothing else changed.
          edit([{ op: 'set', id, props: { effects } }]);
        },
        addEffect: (type, look) => {
          // A tile's look when it was picked from one (shell/effectLooks), else the engine's default.
          const made = look?.type === type ? look : defaultEffect(type);
          if (!made || selection.length === 0) return [];
          // One locked layer, or one already holding the 32 effects a list may have, refused
          // the whole batch and nothing was added anywhere, without a word
          // (QA 2026-09-24, paint-F22). Those are left out, and it says so.
          const effectsHeld = (id: string) => (((scene.get(id) as { effects?: Effect[] } | undefined)?.effects ?? []) as Effect[]).length;
          const present = selection.filter((id) => scene.get(id));
          const reached = present.filter((id) => !scene.get(id)?.locked && effectsHeld(id) < MOST_EFFECTS);
          const left = present.length - reached.length;
          if (left > 0) {
            const locked = present.some((id) => scene.get(id)?.locked);
            const why = locked ? 'locked' : `already holding ${MOST_EFFECTS} effects`;
            options.onNotice?.(
              reached.length === 0
                ? present.length === 1
                  ? `No effect was added — the layer is ${why}.`
                  : `No effect was added — the layers are locked or already hold ${MOST_EFFECTS} effects.`
                : left === 1
                  ? `One layer was left out — it is locked or already holds ${MOST_EFFECTS} effects.`
                  : `${left} layers were left out — they are locked or already hold ${MOST_EFFECTS} effects.`,
            );
          }
          if (reached.length === 0) return [];
          edit(
            reached.map((id) => {
              const had = ((scene.get(id) as { effects?: Effect[] } | undefined)?.effects ?? []) as Effect[];
              return { op: 'set', id, props: { effects: [...had, made] } };
            }),
          );
          // A layer that has just grown an effect has to reach the tiles drawn without it.
          renderer.invalidateAll();
          undo.stopCapturing();
          return reached;
        },
        // One layer or several: several shapes chosen together edit the effects they share
        // in ONE batch, so one ⌘Z takes it back from all of them (QA 2026-09-24, paint-F13).
        // A locked one would refuse the whole batch, so it is left as it is.
        setEffect: (id, index, props) => {
          const ops = effectTargets(id).flatMap((one) => {
            const had = ((scene.get(one) as { effects?: Effect[] } | undefined)?.effects ?? []) as Effect[];
            if (!had[index]) return [];
            return [{ op: 'set', id: one, props: { effects: had.map((effect, at) => (at === index ? { ...effect, ...props } : effect)) } }];
          });
          if (ops.length === 0) return;
          edit(ops);
          renderer.invalidateAll();
          // Its own undo step, as adding and removing are: left open, the next unrelated
          // edit joined it and one ⌘Z took back both. A slider drag closes once, on let-go
          // (QA 2026-09-24, paint-F10).
          if (!panelHeld())undo.stopCapturing();
        },
        removeEffect: (id, index) => {
          const ops = effectTargets(id).flatMap((one) => {
            const had = ((scene.get(one) as { effects?: Effect[] } | undefined)?.effects ?? []) as Effect[];
            return had[index] ? [{ op: 'set', id: one, props: { effects: had.filter((_, at) => at !== index) } }] : [];
          });
          if (ops.length === 0) return;
          edit(ops);
          renderer.invalidateAll();
          undo.stopCapturing();
        },
        moveEffect: (id, index, to) => {
          const ops = effectTargets(id).flatMap((one) => {
            const had = ((scene.get(one) as { effects?: Effect[] } | undefined)?.effects ?? []) as Effect[];
            if (!had[index] || to < 0 || to >= had.length || to === index) return [];
            const next = [...had];
            const [moved] = next.splice(index, 1);
            next.splice(to, 0, moved!);
            return [{ op: 'set', id: one, props: { effects: next } }];
          });
          if (ops.length === 0) return;
          edit(ops);
          renderer.invalidateAll();
          undo.stopCapturing();
        },
        getShader: (id) => shaderSource(open.root).get(id),
        sketchOf: (id) => {
          const node = scene.get(id);
          const asset = node?.type === 'image' ? assetsMap(open.root).get(node.assetId) as Asset | undefined : undefined;
          if (!asset?.sketch) return null;
          // The layers a `shape` control can name: those on the sketch's own artboard.
          let board: string | undefined = id;
          while (board && scene.get(board)?.type !== 'artboard') board = scene.parentOf(board);
          const sources = [...(board ? scene.subtree(board) : [])].flatMap((other) => {
            const layer = scene.get(other);
            return other !== id && layer && layer.type !== 'artboard' && layer.type !== 'slice' && layer.type !== 'effector' ? [{ id: other, name: layer.name || layer.type }] : [];
          });
          const values = (node as { sketch?: { values?: Record<string, unknown> } }).sketch?.values ?? {};
          // What a `media` control can show (Photo Cube's faces, a numeral's mask): any picture or
          // artboard in the file, but nothing the sketch sits in, which would have it draw itself.
          const pictures: { id: string; name: string }[] = [];
          const listed = new Set<string>();
          for (const other of scene.paintOrder()) for (const one of [other, ...scene.subtree(other)]) {
            const layer = scene.get(one);
            if (!layer || one === id || scene.isAncestor(one, id) || listed.has(one)) continue;
            listed.add(one);
            if (layer.type === 'artboard') pictures.push({ id: one, name: `${layer.name || 'Artboard'} (artboard)` });
            else if (layer.type === 'image' && !(layer as { sketch?: unknown }).sketch) pictures.push({ id: one, name: layer.name || 'Picture' });
          }
          const pictureOf = (value: unknown) => {
            const named = (value as { layer?: unknown } | null)?.layer;
            return typeof named === 'string' && pictures.some((p) => p.id === named) ? named : null;
          };
          const chosen = Object.fromEntries(asset.sketch.controls.flatMap((c) => c.type === 'shape' ? [[c.id, sketchShapes.targetOf(id, values[c.id] ?? c.default)]] : c.type === 'media' ? [[c.id, pictureOf(values[c.id] ?? c.default)]] : []));
          return { name: asset.name, controls: asset.sketch.controls, error: images.sketches.errorFor(id), sources, pictures, chosen, ...(asset.sketch.presets ? { presets: asset.sketch.presets } : {}) };
        },
        setSketch: (id, props) => {
          if (scene.lockedHere(id)) return;
          edit([{ op: 'set', id, props }]);
        },
        applyShader: async (libraryId) => {
          // Capture the intended selection before the network request, then verify it
          // still exists and is editable when the preset arrives.
          const targets = [...selection];
          const item = await fetchLibraryItem(libraryId);
          // A sketch draws its own frames, so it is a layer of its own, not something on a layer.
          if (item.payload?.source?.kind === 'p5') return placeSketch(prepareLibrarySketch(item), targets);
          // Lumens GLSL runs as Lumens runs it (WebGL, its own header, passes and previous
          // frame), so whatever runs in Lumens runs here; nothing is translated.
          const shader = prepareLumensGl(item);
          const nodes = targets.map(id => scene.get(id)).filter(node => node && !scene.lockedHere(node.id));
          if (!nodes.length) throw Error('Select an unlocked layer first.');
          if (nodes.some(node => !['artboard', 'group', 'text', 'rect', 'ellipse', 'polygon', 'star', 'path', 'boolean', 'image'].includes(node!.type))) throw Error('Select an artboard, a group, text, an image or a vector shape to add a Lumens effect.');
          if (nodes.some(node => ((node as { effects?: Effect[] }).effects?.length ?? 0) >= 32)) throw Error('This layer already has 32 effects. Remove one before adding another.');
          const shaderId = newId();
          open.root.transact(() => shadersMap(open.root).set(shaderId, shader.def));
          // A moving shader plays on the page's clock, one loop at a time, and makes its page playable.
          const effect = { type: 'shader', shaderId, values: shader.values, seed: shader.seed, time: 'live', source: 'layer', ...(shader.loop ? { loop: shader.loop } : {}) };
          edit(nodes.map(node => ({ op: 'set' as const, id: node!.id, props: { effects: [...((node as { effects?: Effect[] }).effects ?? []), effect] } })));
          renderer.invalidateAll();
          undo.stopCapturing();
        },
        editText: (id) => {
          if (scene.get(id)?.type !== 'text') return;
          setSelection([id]);
          editing?.start(id, 'all');
        },
        // Repaints what is CHOSEN. The rail does not come through here — a control on
        // the rail belongs to the tool in hand and reaches no further than it. This is
        // the bar's adjust panel and the inspector, which are about the selection.
        restyle: (patch) => {
          if (selection.length === 0) return;
          const style = () => options.styleRef.current;
          const ops = unlocked(selection).flatMap((id) => {
            const node = scene.get(id);
            if (!node) return [];
            const props: Record<string, unknown> = {};

            // What each shape already wears is what fills the gaps in a patch — NOT the
            // drawing style. A patch carries the one thing that was just changed; taking
            // the rest from elsewhere repainted every other shape in the selection with
            // whatever colour that happened to hold. A shape only changes in the half
            // that was actually asked about.
            const worn = paintOf(node);

            if (patch.fill !== undefined || patch.fillOn !== undefined) {
              const filled = patch.fillOn ?? patch.fill !== undefined;
              props.fills = filled ? [{ type: 'solid', color: patch.fill !== undefined ? inkedColor(patch.fill, patch.fillInk) : hexColor(worn.fill ?? style().fill) }] : [];
            }
            if (patch.opacity !== undefined) props.opacity = patch.opacity;
            if (patch.stroke !== undefined || patch.strokeWeight !== undefined) {
              // A colour arriving at a shape with no outline gives it one. The fill has
              // always had that courtesy; the outline did not, so picking an outline
              // colour wrote it to a weight of zero and looked like nothing happened.
              const weight = patch.strokeWeight ?? (worn.weight > 0 ? worn.weight : 1);
              props.strokes = weight > 0 ? [{ type: 'solid', color: patch.stroke !== undefined ? inkedColor(patch.stroke, patch.strokeInk) : hexColor(worn.stroke ?? style().stroke) }] : [];
              props.strokeStyle = { weight: Math.max(0, weight) };
            }
            return Object.keys(props).length > 0 ? [{ op: 'set', id, props }] : [];
          });
          if (ops.length > 0) {
            edit(ops);
            // A held slider is one step, closed when the hand comes up (QA 2026-09-24, inspector-numbers-F10).
            if (!panelHeld())undo.stopCapturing();
          }
        },
      };
      options.onZoom(camera.zoom);
      // Whoever measured against the stand-in while the canvas started measures again now.
      viewChanged();
      options.onStartFailed?.(null);
    })().catch((err: unknown) => {
      if (disposed) return;
      console.error('The canvas could not start', err);
      options.onStartFailed?.(() => setAttempt((n) => n + 1));
    });

    return () => {
      disposed = true;
      for (const cleanup of cleanups.reverse()) cleanup();
    };
    // The handle's callbacks are read through refs, so this only re-runs for a new file,
    // or when a start that failed is tried again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.open, attempt]);

  // A stable object for the chrome: its methods always call the live ones above.
  const stable = useRef<CanvasHandle | null>(null);
  const delegates: CanvasHandle = {
    canvasRef,
    zoomBy: (factor) => handleRef.current.zoomBy(factor),
    fit: () => handleRef.current.fit(),
    actualSize: () => handleRef.current.actualSize(),
    panBy: (dx, dy) => handleRef.current.panBy(dx, dy),
    select: (ids) => handleRef.current.select(ids),
    deleteSelection: () => handleRef.current.deleteSelection(),
    restyle: (patch) => handleRef.current.restyle(patch),
    editText: (id) => handleRef.current.editText(id),
    applyShader: (id) => handleRef.current.applyShader(id),
    getShader: (id) => handleRef.current.getShader(id),
    sketchOf: (id) => handleRef.current.sketchOf(id),
    pickLayer: (candidates, then) => handleRef.current.pickLayer(candidates, then),
    thumbnailOf: (id) => handleRef.current.thumbnailOf(id),
    setSketch: (id, props) => handleRef.current.setSketch(id, props),
    addEffect: (type, look) => handleRef.current.addEffect(type, look),
    setAdjust: (id, values) => handleRef.current.setAdjust(id, values),
    setBlur: (id, radius) => handleRef.current.setBlur(id, radius),
    setEffect: (id, index, props) => handleRef.current.setEffect(id, index, props),
    removeEffect: (id, index) => handleRef.current.removeEffect(id, index),
    moveEffect: (id, index, to) => handleRef.current.moveEffect(id, index, to),
    combine: (op) => handleRef.current.combine(op),
    mask: () => handleRef.current.mask(),
    group: () => handleRef.current.group(),
    ungroup: () => handleRef.current.ungroup(),
    repeat: () => handleRef.current.repeat(),
    addVariation: (clonerId, recipe) => handleRef.current.addVariation(clonerId, recipe),
    removeVariation: (effectorId) => handleRef.current.removeVariation(effectorId),
    makeLayers: (clonerId) => handleRef.current.makeLayers(clonerId),
    stopRepeating: (clonerId) => handleRef.current.stopRepeating(clonerId),
    repeatSource: (clonerId) => handleRef.current.repeatSource(clonerId),
    repeatPaths: (clonerId) => handleRef.current.repeatPaths(clonerId),
    expandBlend: () => handleRef.current.expandBlend(),
    createBlend: () => handleRef.current.createBlend(),
    releaseBlend: () => handleRef.current.releaseBlend(),
    rasterize: () => handleRef.current.rasterize(),
    outlineText: () => handleRef.current.outlineText(),
    arrange: (how) => handleRef.current.arrange(how),
    flip: (axis) => handleRef.current.flip(axis),
    update: (id, props) => handleRef.current.update(id, props),
    updateAll: (ids, props) => handleRef.current.updateAll(ids, props),
    updateEach: (writes) => handleRef.current.updateEach(writes),
    middleOf: (id) => handleRef.current.middleOf(id),
    boxInParent: (id) => handleRef.current.boxInParent(id),
    align: (how) => handleRef.current.align(how),
    tidyUp: (how) => handleRef.current.tidyUp(how),
    exportPng: (scale, artboardId, ownContent) => handleRef.current.exportPng(scale, artboardId, ownContent),
    exportVideo: (options) => handleRef.current.exportVideo(options),
    exportRaster: options => handleRef.current.exportRaster(options),
    exportSvg: (artboardId, pictureScale) => handleRef.current.exportSvg(artboardId, pictureScale),
    exportBoards: (scope) => handleRef.current.exportBoards(scope),
    arrangeArtboards: (options) => handleRef.current.arrangeArtboards(options),
    editGradient: (target) => handleRef.current.editGradient(target),
    sizeArtboards: (ids, preset) => handleRef.current.sizeArtboards(ids, preset),
    exportSlices: (pictureScale) => handleRef.current.exportSlices(pictureScale),
    exportLayers: () => handleRef.current.exportLayers(),
    exportLayersPng: (scale) => handleRef.current.exportLayersPng(scale),
    loadOriginals: (artboardId) => handleRef.current.loadOriginals(artboardId),
    renderLayerPng: (id, maxEdge) => handleRef.current.renderLayerPng(id, maxEdge),
    renderReference: (id, longEdge, format) => handleRef.current.renderReference(id, longEdge, format),
    previewEffects: (id, types, longEdge, onTile, signal) => handleRef.current.previewEffects(id, types, longEdge, onTile, signal),
    exportLayersSvg: (pictureScale) => handleRef.current.exportLayersSvg(pictureScale),
    sliceCount: () => handleRef.current.sliceCount(),
    exportName: () => handleRef.current.exportName(),
    exportArtboard: () => handleRef.current.exportArtboard(),
    placePicture: (files, at) => handleRef.current.placePicture(files, at),
    addShape: (shape, at) => handleRef.current.addShape(shape, at),
    addText: (preset, at) => handleRef.current.addText(preset, at),
    paintSelection: (paint, to) => handleRef.current.paintSelection(paint, to),
    placeDrawing: (svg, options) => handleRef.current.placeDrawing(svg, options),
    placeVectorOver: (sourceId, svg, name) => handleRef.current.placeVectorOver(sourceId, svg, name),
    placeLayerDocument: (sourceId, doc, pictures, fonts, size, name) => handleRef.current.placeLayerDocument(sourceId, doc, pictures, fonts, size, name),
    placeLayers: (layers, size, at) => handleRef.current.placeLayers(layers, size, at),
    placeTemplate: (template, at) => handleRef.current.placeTemplate(template, at),
    clipArtboard: (id) => handleRef.current.clipArtboard(id),
    clipSelection: () => handleRef.current.clipSelection(),
    filePictures: () => handleRef.current.filePictures(),
    uploadPaintPicture: (file) => handleRef.current.uploadPaintPicture(file),
    addPicture: (picture, at) => handleRef.current.addPicture(picture, at),
    createGenerationTarget: (size, name, avoid) => handleRef.current.createGenerationTarget(size, name, avoid),
    generationViewport: () => handleRef.current.generationViewport(),
    revealGenerationTarget: id => handleRef.current.revealGenerationTarget(id),
    replacePicture: (ids, file) => handleRef.current.replacePicture(ids, file),
    fitPicture: (ids, how) => handleRef.current.fitPicture(ids, how),
    addArtboard: (preset, turned, custom) => handleRef.current.addArtboard(preset, turned, custom),
    artboards: () => handleRef.current.artboards(),
    focusArtboard: (id) => handleRef.current.focusArtboard(id),
    showArtboard: (id) => handleRef.current.showArtboard(id),
    viewKey: () => handleRef.current.viewKey(),
    goTo: (view) => handleRef.current.goTo(view),
    boxOf: (id) => handleRef.current.boxOf(id),
    screenAt: (at) => handleRef.current.screenAt(at),
    worldAt: (at) => handleRef.current.worldAt(at),
    worldBoxOf: (id) => handleRef.current.worldBoxOf(id),
    selectAll: () => handleRef.current.selectAll(),
    selectionPlace: () => handleRef.current.selectionPlace(),
    layersAt: (at) => handleRef.current.layersAt(at),
    lockedAt: (at) => handleRef.current.lockedAt?.(at) ?? [],
    onViewChange: (listener) => { viewWatchers.current.add(listener); return () => viewWatchers.current.delete(listener); },
    dragging: () => handleRef.current.dragging(),
    insertShape: (kind, at) => handleRef.current.insertShape(kind, at),
    duplicate: () => handleRef.current.duplicate(),
    copyBeside: (id, props) => handleRef.current.copyBeside(id, props),
    pasteText: (text: string, at?: { x: number; y: number }) => handleRef.current.pasteText(text, at),
    copySelection: () => handleRef.current.copySelection(),
    cutSelection: () => handleRef.current.cutSelection(),
    hasCopy: () => handleRef.current.hasCopy(),
    copyProperties: () => handleRef.current.copyProperties(),
    pasteProperties: () => handleRef.current.pasteProperties(),
    hasProperties: () => handleRef.current.hasProperties(),
    pickColour: (then) => handleRef.current.pickColour(then),
    fileColours: () => handleRef.current.fileColours(),
    move: (id, target) => handleRef.current.move(id, target),
    setFont: (id, family, font) => handleRef.current.setFont(id, family, font),
    addFont: (font) => handleRef.current.addFont(font),
    fontsInDocument: () => handleRef.current.fontsInDocument(),
    anchor: () => handleRef.current.anchor(),
    placeAnchor: (at) => handleRef.current.placeAnchor(at),
    rail: (tool) => handleRef.current.rail(tool),
    selectMode: (id) => handleRef.current.selectMode(id),
    guideCount: () => handleRef.current.guideCount(),
    clearGuides: () => handleRef.current.clearGuides(),
    animationTarget: () => handleRef.current.animationTarget(),
    setAnimation: (id, animation) => handleRef.current.setAnimation(id, animation),
    setLayerAnimation: (ids, animation, timing) => handleRef.current.setLayerAnimation(ids, animation, timing),
    setLayerTiming: (ids, timing) => handleRef.current.setLayerTiming(ids, timing),
    setDuration: (id, seconds) => handleRef.current.setDuration(id, seconds),
    magicAnimate: (id, styleId, settings) => handleRef.current.magicAnimate(id, styleId, settings),
    drawPath: (id, onDone) => handleRef.current.drawPath(id, onDone),
    cancelPath: () => handleRef.current.cancelPath(),
    isDrawingPath: () => handleRef.current.isDrawingPath(),
    play: (id, onStop, from) => handleRef.current.play(id, onStop, from),
    seek: (id, seconds, onStop) => handleRef.current.seek(id, seconds, onStop),
    preview: (layerId, animation, hold) => handleRef.current.preview(layerId, animation, hold),
    stop: () => handleRef.current.stop(),
    forgetPlayhead: () => handleRef.current.forgetPlayhead(),
    isPlaying: () => handleRef.current.isPlaying(),
    playhead: () => handleRef.current.playhead(),
    recipeOf: (id) => handleRef.current.recipeOf(id),
    recipeChoices: (id, recipe) => handleRef.current.recipeChoices(id, recipe),
    timelineOf: (id) => handleRef.current.timelineOf(id),
    clearAnimation: (id, layers) => handleRef.current.clearAnimation(id, layers),
    addonBridge: () => handleRef.current.addonBridge(),
    setAddonTool: (tool) => handleRef.current.setAddonTool(tool),
    cropPicture: (id) => handleRef.current.cropPicture(id),
    proofer: () => handleRef.current.proofer(),
  };
  // Refresh forwarding functions after hot updates while preserving the public handle.
  if (stable.current) Object.assign(stable.current, delegates);
  else stable.current = delegates;
  return stable.current;
}
